<template>
  <v-app class="conversational-enrollment">
    <!-- Progress Bar -->
    <v-progress-linear
      :value="progressPercentage"
      color="primary"
      height="6"
      class="progress-bar-fixed"
    ></v-progress-linear>

    <v-container fluid class="fill-height pa-0">
      <v-row class="fill-height ma-0">
        <!-- Main Content Area -->
        <v-col cols="12" class="pa-0 d-flex flex-column">
          <!-- Header -->
          <div class="app-header">
            <v-btn icon @click="goBack" v-if="currentQuestion > 0 && currentQuestion < 15" class="back-btn">
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            <div class="header-center">
              <img 
                v-if="clientConfig && clientConfig.branding && clientConfig.branding.logo" 
                :src="clientConfig.branding.logo" 
                alt="Company Logo"
                class="client-logo"
              />
              <div class="progress-text" style="color: #374151 !important;" v-if="currentQuestion > 0 && currentQuestion < 15">
                Question {{ displayQuestionNumber }} of {{ totalQuestions }}
              </div>
            </div>
            <v-btn icon @click="goToLogin" class="login-btn" v-if="currentQuestion > 0 && currentQuestion < 15">
              <v-icon>mdi-exit-to-app</v-icon>
            </v-btn>
          </div>

          <!-- Question Container -->
          <div class="question-container" ref="questionContainer">
            <transition name="slide-fade" mode="out-in">
              <div :key="currentQuestion" class="question-content">
                
                <!-- Welcome Screen -->
                <div v-if="currentQuestion === 0" class="text-center">
                  <v-icon size="80" color="primary" class="mb-4">mdi-hand-wave</v-icon>
                  <h1 class="display-2 mb-4">Welcome to Family Health Choice</h1>
                  <p class="text-h6 mb-8" style="color: #374151 !important;">
                    Let's find the perfect health plan for you.<br>
                    This will only take a few minutes.
                  </p>
                  <v-btn
                    x-large
                    color="primary"
                    @click="nextQuestion"
                    class="get-started-btn"
                  >
                    Get Started
                    <v-icon right>mdi-arrow-right</v-icon>
                  </v-btn>
                </div>

                <!-- Q1: First Name -->
                <div v-else-if="currentQuestion === 1">
                  <h2 class="question-title">What's your first name?</h2>
                  <v-text-field
                    v-model="enrollment.firstName"
                    placeholder="Enter your first name"
                    outlined
                    autofocus
                    class="large-input"
                    @keydown.enter="nextQuestion"
                    :rules="[rules.required]"
                  ></v-text-field>
                </div>

                <!-- Q2: Last Name -->
                <div v-else-if="currentQuestion === 2">
                  <h2 class="question-title">And your last name?</h2>
                  <v-text-field
                    v-model="enrollment.lastName"
                    placeholder="Enter your last name"
                    outlined
                    autofocus
                    class="large-input"
                    @keydown.enter="nextQuestion"
                    :rules="[rules.required]"
                  ></v-text-field>
                </div>

                <!-- Q3: Date of Birth -->
                <div v-else-if="currentQuestion === 3">
                  <h2 class="question-title">When's your birthday?</h2>
                  <v-date-picker
                    v-model="enrollment.dateOfBirth"
                    :max="new Date().toISOString().substr(0, 10)"
                    full-width
                    class="elevation-4 custom-date-picker"
                    color="primary"
                    :dark="false"
                    @input="onDateSelected"
                  ></v-date-picker>
                </div>

                <!-- Q4: Gender -->
                <div v-else-if="currentQuestion === 4">
                  <h2 class="question-title">What's your gender?</h2>
                  <v-radio-group v-model="enrollment.gender" row class="mt-4">
                    <v-radio
                      label="Male"
                      value="M"
                      color="primary"
                    ></v-radio>
                    <v-radio
                      label="Female"
                      value="F"
                      color="primary"
                    ></v-radio>
                  </v-radio-group>
                </div>

                <!-- Q5: Email -->
                <div v-else-if="currentQuestion === 5">
                  <h2 class="question-title">What's your email address?</h2>
                  <p class="question-subtitle">We'll send your enrollment confirmation here</p>
                  <v-text-field
                    v-model="enrollment.email"
                    placeholder="your.email@example.com"
                    outlined
                    autofocus
                    class="large-input"
                    type="email"
                    @keydown.enter="nextQuestion"
                    :rules="[rules.required, rules.email]"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon>mdi-email</v-icon>
                    </template>
                  </v-text-field>
                </div>

                <!-- Q6: Phone -->
                <div v-else-if="currentQuestion === 6">
                  <h2 class="question-title">What's your phone number?</h2>
                  <v-text-field
                    v-model="enrollment.cellPhone"
                    placeholder="(555) 123-4567"
                    outlined
                    autofocus
                    class="large-input"
                    v-mask="'(###) ###-####'"
                    @keydown.enter="nextQuestion"
                    :rules="[rules.required]"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon>mdi-phone</v-icon>
                    </template>
                  </v-text-field>
                </div>

                <!-- Q7: Address -->
                <div v-else-if="currentQuestion === 7">
                  <h2 class="question-title">Where do you live?</h2>
                  <p class="question-subtitle">Start typing your address for suggestions</p>
                  
                  <v-autocomplete
                    v-model="selectedAddress"
                    :items="addressSuggestions"
                    :loading="searchingAddress"
                    :search-input.sync="addressSearchText"
                    item-text="description"
                    return-object
                    placeholder="Start typing your address..."
                    outlined
                    class="large-input mb-3"
                    clearable
                    no-filter
                    @change="selectAddressFromAutocomplete"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon>mdi-map-marker</v-icon>
                    </template>
                    <template v-slot:no-data>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title>
                            {{ addressSearchText ? 'Type to search addresses...' : 'Start typing your address' }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>{{ item.description }}</v-list-item-title>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                  
                  <v-divider class="my-4"></v-divider>
                  <div class="text-subtitle-2 mb-2" style="color: #374151 !important;">Or enter manually:</div>
                  
                  <v-text-field
                    v-model="enrollment.streetAddress"
                    placeholder="Street Address"
                    outlined
                    class="large-input mb-3"
                    :rules="[rules.required]"
                    :readonly="addressAutoFilled"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon>mdi-home</v-icon>
                    </template>
                    <template v-slot:append v-if="addressAutoFilled">
                      <v-btn icon small @click="clearAutoFill">
                        <v-icon small>mdi-close</v-icon>
                      </v-btn>
                    </template>
                  </v-text-field>
                  
                  <v-row>
                    <v-col cols="12" sm="5">
                      <v-text-field
                        v-model="enrollment.city"
                        placeholder="City"
                        outlined
                        class="large-input"
                        :rules="[rules.required]"
                        :readonly="addressAutoFilled"
                      ></v-text-field>
                    </v-col>
                    
                    <v-col cols="6" sm="3">
                      <v-text-field
                        v-model="enrollment.state"
                        placeholder="State"
                        outlined
                        class="large-input"
                        maxlength="2"
                        :rules="[rules.required]"
                        :readonly="addressAutoFilled"
                      ></v-text-field>
                    </v-col>
                    
                    <v-col cols="6" sm="4">
                      <v-text-field
                        v-model="enrollment.zipCode"
                        placeholder="Zip Code"
                        outlined
                        class="large-input"
                        v-mask="'#####'"
                        :rules="[rules.required]"
                        :readonly="addressAutoFilled"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                  
                  <v-alert v-if="addressAutoFilled" type="success" dense text class="mt-2">
                    <v-icon left small>mdi-check-circle</v-icon>
                    Address auto-filled! Click X to edit manually.
                  </v-alert>
                </div>

                <!-- Q8: Marital Status -->
                <div v-else-if="currentQuestion === 8">
                  <h2 class="question-title">What's your marital status?</h2>
                  <div class="options-grid">
                    <v-card
                      v-for="status in maritalStatuses"
                      :key="status.value"
                      class="option-card"
                      :class="{ 'selected': enrollment.maritalStatus === status.value }"
                      @click="selectMaritalStatus(status.value)"
                      hover
                    >
                      <v-icon size="40" :color="enrollment.maritalStatus === status.value ? 'primary' : 'grey'">
                        {{ status.icon }}
                      </v-icon>
                      <div class="mt-2">{{ status.text }}</div>
                    </v-card>
                  </div>
                </div>

                <!-- Q9: Dependents -->
                <div v-else-if="currentQuestion === 9">
                  <h2 class="question-title">Do you have any dependents?</h2>
                  <p class="question-subtitle">Spouse or children you'd like to add to the plan</p>
                  <div class="options-grid-2">
                    <v-card
                      class="option-card-large"
                      :class="{ 'selected': hasDependents === true }"
                      @click="setHasDependents(true)"
                      hover
                    >
                      <v-icon size="60" :color="hasDependents === true ? 'primary' : 'grey'">
                        mdi-account-multiple
                      </v-icon>
                      <div class="text-h6 mt-3">Yes</div>
                      <div class="text-caption" style="color: #374151 !important;">I have dependents</div>
                    </v-card>
                    
                    <v-card
                      class="option-card-large"
                      :class="{ 'selected': hasDependents === false }"
                      @click="setHasDependents(false)"
                      hover
                    >
                      <v-icon size="60" :color="hasDependents === false ? 'primary' : 'grey'">
                        mdi-account
                      </v-icon>
                      <div class="text-h6 mt-3">No</div>
                      <div class="text-caption" style="color: #374151 !important;">Just me</div>
                    </v-card>
                  </div>
                </div>

                <!-- Q10: Add Dependents (if yes) -->
                <div v-else-if="currentQuestion === 10 && hasDependents">
                  <h2 class="question-title">Tell us about your dependents</h2>
                  <p class="question-subtitle">You can add up to 5 dependents</p>
                  
                  <v-alert 
                    v-if="!canProceed" 
                    type="warning" 
                    text 
                    dense 
                    class="mb-4"
                  >
                    Please fill in all required fields (First Name, Last Name, and Date of Birth) for all dependents before continuing.
                  </v-alert>
                  
                  <v-card 
                    v-for="(dep, index) in enrollment.dependents" 
                    :key="index"
                    class="mb-4 pa-4"
                    outlined
                  >
                    <div class="d-flex align-center mb-3">
                      <v-chip small :color="dep.relationship === 'Spouse' ? 'pink' : 'blue'" class="mr-2">
                        {{ dep.relationship }}
                      </v-chip>
                      <v-spacer></v-spacer>
                      <v-btn icon small @click="removeDependent(index)">
                        <v-icon small>mdi-close</v-icon>
                      </v-btn>
                    </div>
                    
                    <v-row>
                      <v-col cols="12" sm="6">
                        <v-text-field
                          v-model="dep.firstName"
                          label="First Name *"
                          outlined
                          dense
                          required
                          :error="!dep.firstName || dep.firstName.trim().length === 0"
                          :error-messages="(!dep.firstName || dep.firstName.trim().length === 0) ? 'First name is required' : ''"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-text-field
                          v-model="dep.lastName"
                          label="Last Name *"
                          outlined
                          dense
                          required
                          :error="!dep.lastName || dep.lastName.trim().length === 0"
                          :error-messages="(!dep.lastName || dep.lastName.trim().length === 0) ? 'Last name is required' : ''"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-select
                          v-model="dep.relationship"
                          label="Relationship"
                          :items="['Spouse', 'Child']"
                          outlined
                          dense
                        ></v-select>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-menu 
                          :close-on-content-click="false" 
                          max-width="290px"
                          v-model="dep.datePickerMenu"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              :value="formatDate(dep.dateOfBirth)"
                              label="Date of Birth *"
                              readonly
                              outlined
                              dense
                              required
                              :error="!dep.dateOfBirth"
                              :error-messages="!dep.dateOfBirth ? 'Date of birth is required' : ''"
                              v-bind="attrs"
                              @click="on.click"
                              @click:prepend-inner="on.click"
                              @click:append-inner="on.click"
                            ></v-text-field>
                          </template>
                          <v-date-picker
                            v-model="dep.dateOfBirth"
                            :max="new Date().toISOString().substr(0, 10)"
                            @input="onDependentDateChange"
                          ></v-date-picker>
                        </v-menu>
                      </v-col>
                      <v-col cols="12" v-if="dep.relationship === 'Child'">
                        <v-radio-group v-model="dep.gender" row dense>
                          <v-radio label="Male" value="M"></v-radio>
                          <v-radio label="Female" value="F"></v-radio>
                        </v-radio-group>
                      </v-col>
                    </v-row>
                  </v-card>
                  
                  <v-btn
                    v-if="enrollment.dependents.length < selectedPlanMaxDependents"
                    text
                    color="primary"
                    @click="addDependent"
                    class="mb-4"
                  >
                    <v-icon left>mdi-plus</v-icon>
                    Add Another Dependent
                  </v-btn>
                </div>

                <!-- Q10: Select Plan -->
                <div v-else-if="currentQuestion === 11">
                  <h2 class="question-title">Choose your health plan</h2>
                  <p class="question-subtitle">Select the plan that best fits your needs</p>
                  
                  <div class="plans-scroll">
                    <v-card
                      v-for="plan in mainPlans"
                      :key="plan._id"
                      class="plan-card-conversational mb-4"
                      :class="{ 'selected': isPlanSelected(plan) }"
                      @click="selectPlan(plan)"
                      hover
                    >
                      <v-row align="center">
                        <v-col cols="2" class="text-center">
                          <v-icon size="48" :color="isPlanSelected(plan) ? 'primary' : 'grey'">
                            {{ plan.icon }}
                          </v-icon>
                        </v-col>
                        <v-col cols="8">
                          <div class="text-h6">{{ plan.name }}</div>
                          <div class="text-caption" style="color: #374151 !important;">{{ plan.description }}</div>
                          <v-chip-group class="mt-2" @click.stop>
                            <v-chip
                              x-small
                              v-for="benefit in getPlanKeyBenefits(plan)"
                              :key="benefit"
                            >
                              {{ benefit }}
                            </v-chip>
                          </v-chip-group>
                        </v-col>
                        <v-col cols="2" class="text-right">
                          <div class="text-h6 primary--text" style="white-space: nowrap;">
                            {{ getPlanPriceForUser(plan) }}
                          </div>
                          <div class="text-caption" style="font-size: 0.75rem;">
                            <span v-if="plan.planName && plan.planName.toLowerCase().includes('phc')">starting from </span>{{ plan._id === 'benefits-only' ? 'Pay only for selected benefits' : 'per month' }}
                          </div>
                        </v-col>
                      </v-row>
                    </v-card>
                  </div>
                  
                  <!-- Disclosure - Only shown after plan selection -->
                  <div v-if="selectedPlanKey && selectedPlanDisclosure" class="mt-4">
                    <v-alert type="warning" text dense>
                    <div class="text-subtitle-2 font-weight-bold mb-2">⚠️ IMPORTANT DISCLOSURE</div>
                      <div class="text-caption" v-html="selectedPlanDisclosure"></div>
                  </v-alert>
                    
                    <div class="mt-4 text-center">
                      <v-btn
                        color="primary"
                        large
                        @click="acceptDisclosure"
                        :disabled="!selectedPlanKey"
                      >
                        I Understand and Agree - Continue
                        <v-icon right>mdi-arrow-right</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </div>

                <!-- Q11: Member Customization -->
                <div v-else-if="currentQuestion === 12">
                  <h2 class="question-title">
                    {{ isBenefitsOnlyPlan ? 'Benefits Only Selection' : 'Customize coverage for each member' }}
                  </h2>
                  <p class="question-subtitle">
                    {{ isBenefitsOnlyPlan ? 'Select stand-alone benefits for each member. Each member can choose their own benefits.' : 'Adjust optional benefits and specialty providers member by member.' }}
                  </p>
                  
                  <v-alert type="info" text dense class="mb-4">
                    Confirm each member individually. Use the chips below to switch between members; a green chip means that member is confirmed.
                  </v-alert>
                  
                  <v-card outlined class="member-customization-card">
                    <div class="px-4 py-3">
                      <div class="text-subtitle-1 font-weight-medium mb-1" style="color: #1f2937 !important;">Members to review</div>
                      <div class="text-caption grey--text mb-2">Select a member chip to review or update their choices.</div>
                      <div class="d-flex flex-wrap">
                        <v-chip
                          v-for="(member, index) in membersList"
                          :key="member.memberKey || index"
                          class="ma-1 text-capitalize"
                          :color="currentMemberSelectionIndex === index ? 'primary' : (memberSelections[index]?.confirmed ? 'success' : 'grey lighten-3')"
                          :text-color="currentMemberSelectionIndex === index || memberSelections[index]?.confirmed ? 'white' : 'primary'"
                          :outlined="currentMemberSelectionIndex !== index && !memberSelections[index]?.confirmed"
                          @click="goToMember(index)"
                        >
                          <v-icon left small>
                            {{
                              memberSelections[index]?.confirmed
                                ? 'mdi-check-circle'
                                : (currentMemberSelectionIndex === index ? 'mdi-account' : 'mdi-account-outline')
                            }}
                          </v-icon>
                          {{ formatShortMemberLabel(member, index) }}
                        </v-chip>
                      </div>
                    </div>
                    
                    <v-divider></v-divider>
                    
                    <v-card-text>
                      <v-alert
                        v-if="!isBenefitsOnlyPlan && currentMemberConfirmed"
                        type="success"
                        text
                        dense
                        class="mb-4"
                      >
                        {{ formatShortMemberLabel(currentMember, currentMemberSelectionIndex) }} confirmed. Adjust selections to make changes if needed.
                      </v-alert>
                      
                      <div class="d-flex justify-space-between align-center mb-4">
                        <div>
                          <div class="text-subtitle-1 font-weight-bold" style="color: #1f2937 !important;">
                            {{ formatMemberName(currentMember) }}
                          </div>
                          <div class="text-caption grey--text" v-if="!isBenefitsOnlyPlan">
                            Member {{ currentMemberSelectionIndex + 1 }} of {{ memberCount }}
                          </div>
                        </div>
                      </div>
                      
                      <section v-if="allowsSpecialtyProviders" class="mb-6">
                        <h3 class="text-subtitle-1 font-weight-bold mb-2" style="color: #374151 !important;">Specialty Providers</h3>
                        <p class="text-body-2 mb-4" style="color: #4b5563 !important;">
                          Choose which specialty providers {{ formatMemberName(currentMember) }} needs.
                          <span>
                            Each specialty provider you add costs $5 per month. No specialists are included in any plan.
                          </span>
                        </p>
                        
                        <v-autocomplete
                          v-model="currentMemberSpecialties"
                          :items="availableSpecialties"
                          label="Select specialty providers"
                          multiple
                          chips
                          deletable-chips
                          clearable
                          hide-selected
                          outlined
                          dense
                          small-chips
                          :menu-props="{ maxHeight: 400 }"
                          prepend-inner-icon="mdi-doctor"
                        ></v-autocomplete>
                        
                        <div class="text-caption grey--text mt-2">
                          {{ currentMemberSpecialtyCount }} selected for this member.
                          Currently {{ totalSpecialtyProvidersSelected }} specialty provider{{ totalSpecialtyProvidersSelected === 1 ? '' : 's' }} selected overall.
                          <template v-if="enrollment.planName && enrollment.planName.toLowerCase().includes('phc')">
                            <span>Current plan type: <strong>{{ enrollment.planName.split(' ').pop() }}</strong></span>
                            <span v-if="totalSpecialtyProvidersSelected === 0"> (Basic).</span>
                            <span v-else-if="totalSpecialtyProvidersSelected <= 2"> (Plus).</span>
                            <span v-else> (Premium).</span>
                          </template>
                        </div>
                        
                      </section>
                      
                      <section>
                        <h3 class="text-subtitle-1 font-weight-bold mb-3" style="color: #374151 !important;">Additional Benefits</h3>
                        <p class="text-body-2 mb-4" style="color: #4b5563 !important;">
                          {{ isBenefitsOnlyPlan ? 'Choose the stand-alone benefits you want to include for your household.' : `Choose optional benefits for ${formatMemberName(currentMember)}. Included benefits are shown for awareness and cannot be removed.` }}
                        </p>
                  
                  <div class="benefits-selection">
                    <v-row>
                      <v-col
                        cols="12"
                        v-for="benefit in getAvailableAdditionalBenefits()"
                        :key="benefit._id"
                      >
                        <v-card
                          class="plan-card pa-4 mb-4"
                          :class="{
                            'selected': !isCoreDentalSuppressed(benefit._id) && isBenefitSelectedForCurrentMember(benefit._id),
                            'included-benefit': isBenefitIncluded(benefit._id),
                            'suppressed-benefit': isCoreDentalSuppressed(benefit._id),
                            'non-clickable': !canToggleBenefit(benefit._id),
                            'clickable-card': canToggleBenefit(benefit._id)
                          }"
                          @click.stop="canToggleBenefit(benefit._id) ? toggleAdditionalBenefit(benefit._id) : null"
                          hover
                        >
                          <v-row align="center">
                            <v-col cols="8">
                              <div class="text-h6 font-weight-bold mb-2" style="color: #374151 !important;">{{ benefit.planName }}</div>
                              <div class="text-body-2 mb-2" style="color: #6b7280 !important;">{{ benefit.shortDescription }}</div>
                              <div
                                v-if="isCoreDentalSuppressed(benefit._id)"
                                class="text-body-2 suppressed-note mb-2"
                              >
                                This core plan is automatically replaced by your upgraded dental selection.
                              </div>
                              <div class="mt-2" style="flex-wrap: wrap; display: flex; gap: 4px; pointer-events: none;">
                                <v-chip 
                                  x-small 
                                  v-for="chip in benefit.benefits" 
                                  :key="chip"
                                  class="mb-1"
                                  style="flex-shrink: 0;"
                                >
                                  {{ chip }}
                                </v-chip>
                              </div>
                            </v-col>
                            <v-col cols="4" class="text-right">
                              <div
                                class="text-h6"
                                :class="isBenefitIncluded(benefit._id) ? 'success--text' : (isCoreDentalSuppressed(benefit._id) ? 'grey--text text--darken-1' : 'primary--text')"
                                style="white-space: nowrap;"
                              >
                                <template v-if="isBenefitIncluded(benefit._id)">
                                  Included
                                </template>
                                <template v-else-if="isCoreDentalSuppressed(benefit._id)">
                                  Superseded
                                </template>
                                <template v-else>
                                  +${{ getDisplayBenefitPriceById(benefit._id).toFixed(2) }}
                                </template>
                              </div>
                              <div class="text-caption" style="font-size: 0.75rem; line-height: 1;">
                                <template v-if="isBenefitIncluded(benefit._id)">
                                  with {{ selectedPlanName }}
                                </template>
                                <template v-else-if="isCoreDentalSuppressed(benefit._id)">
                                  Covered through upgraded dental plan
                                </template>
                                <template v-else>
                                  {{ isBenefitsOnlyPlan || isBundlePricingBenefit(benefit._id) ? 'per household / month' : 'per member / month' }}
                                </template>
                              </div>
                            </v-col>
                          </v-row>
                        </v-card>
                      </v-col>
                    </v-row>
                  </div>
                      </section>
                      
                      <section class="mt-6">
                        <h3 class="text-subtitle-1 font-weight-bold mb-3" style="color: #374151 !important;">Member Summary</h3>
                        <v-card outlined color="grey lighten-4">
                          <v-card-text>
                            <div class="d-flex justify-space-between align-center mb-2">
                              <span class="font-weight-medium">Benefits</span>
                              <span>${{ (currentMemberCost?.benefitTotal || 0).toFixed(2) }}</span>
                            </div>
                            <div
                              class="d-flex justify-space-between align-center mb-2"
                              v-if="allowsSpecialtyProviders"
                            >
                              <span class="font-weight-medium">Specialty Providers</span>
                              <span>
                                {{ currentMemberSpecialtyCount }} selected
                                <span v-if="currentMemberSpecialtyCount > 0">
                                  ({{ currentMemberSpecialtyCount }} specialist{{ currentMemberSpecialtyCount !== 1 ? 's' : '' }})
                                </span>
                                · ${{ currentMemberSpecialtyCostValue.toFixed(2) }}
                              </span>
                            </div>
                            <div
                              v-if="allowsSpecialtyProviders && currentMemberSpecialties.length"
                              class="mt-2"
                            >
                              <span class="font-weight-medium">Selected specialties:</span>
                              <div class="d-flex flex-wrap mt-1">
                                <v-chip
                                  v-for="spec in currentMemberSpecialties"
                                  :key="spec"
                                  small
                                  class="ma-1"
                                  color="primary"
                                  text-color="white"
                                >
                                  {{ spec }}
                                </v-chip>
                              </div>
                            </div>
                            <v-divider class="my-3"></v-divider>
                            <div class="d-flex justify-space-between align-center">
                              <span>Total for {{ formatMemberName(currentMember) }}</span>
                              <span class="primary--text">${{ (currentMemberCost?.totalExtras || 0).toFixed(2) }}</span>
                            </div>
                            <div class="text-caption grey--text mb-2">
                              Benefits extras: ${{ (currentMemberCost?.benefitTotal || 0).toFixed(2) }} · Specialty extras: ${{ currentMemberSpecialtyCostValue.toFixed(2) }}
                            </div>
                            <v-divider class="my-3"></v-divider>
                            <div class="d-flex justify-space-between align-center">
                              <span class="font-weight-medium">Household total (per month)</span>
                              <span class="text-h5 primary--text">${{ calculatedTotalPrice.toFixed(2) }}</span>
                            </div>
                            <div class="text-caption grey--text">
                              <template v-if="isBenefitsOnlyPlan">
                                Base plan: ${{ basePlanMonthlyFee.toFixed(2) }} · Specialty extras: ${{ totalSpecialtyCost.toFixed(2) }}
                              </template>
                              <template v-else>
                                Base plan: ${{ basePlanMonthlyFee.toFixed(2) }} · Benefits extras: ${{ totalBenefitsCost.toFixed(2) }} · Specialty extras: ${{ totalSpecialtyCost.toFixed(2) }}
                              </template>
                            </div>
                            <div class="text-caption grey--text mt-2" v-if="allowsSpecialtyProviders">
                              Overall specialists selected: {{ totalSpecialtyProvidersSelected }}
                              <span v-if="totalSpecialtyProvidersSelected > 0">
                                {{ totalSpecialtyProvidersSelected }} specialist{{ totalSpecialtyProvidersSelected !== 1 ? 's' : '' }} selected
                              </span>
                            </div>
                          </v-card-text>
                  </v-card>
                        <v-alert
                          v-if="exceedsPlanSpecialtyLimit"
                          type="error"
                          text
                          dense
                          class="mt-3"
                        >
                          You have selected more than the plan allows (maximum {{ memberSpecialtyMax }} specialty providers across all members). Remove selections before confirming.
                        </v-alert>
                      </section>
                    </v-card-text>
                    
                    <v-divider></v-divider>
                    
                    <v-card-actions
                      class="px-4 py-3 d-flex flex-wrap justify-space-between"
                    >
                      <v-btn text color="primary" @click="handleMemberSelectionBack">
                        <v-icon left>mdi-chevron-left</v-icon>
                        Previous Member
                      </v-btn>
                      <div class="d-flex flex-wrap align-center">
                        <v-btn
                          color="primary"
                          class="mr-2"
                          @click="confirmCurrentMember"
                          :disabled="currentMemberConfirmed || !isCurrentMemberSpecialtyValid"
                        >
                          <v-icon left>{{ currentMemberConfirmed ? 'mdi-check-circle' : 'mdi-check' }}</v-icon>
                          {{ currentMemberConfirmed ? 'Member Confirmed' : 'Confirm This Member' }}
                        </v-btn>
                        <v-btn
                          text
                          color="primary"
                          @click="handleMemberSelectionAdvance"
                          :disabled="!currentMemberConfirmed || currentMemberSelectionIndex >= memberCount - 1"
                        >
                          Next Member
                          <v-icon right>mdi-chevron-right</v-icon>
                  </v-btn>
                      </div>
                    </v-card-actions>
                  </v-card>
                  
                  <v-alert
                    v-if="allMembersConfirmed"
                    type="success"
                    text
                    dense
                    class="mt-4"
                  >
                    All members are confirmed. Use the Continue button below to proceed.
                  </v-alert>
                </div>

                <!-- Q12: Coverage Type - REMOVED (automatically calculated from dependents) -->

                <!-- Q14: Additional Information -->
                <div v-else-if="currentQuestion === 14">
                  <h2 class="question-title">Additional Information</h2>
                  <p class="question-subtitle">Help us serve you better</p>
                  
                  <v-row>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="enrollment.homePhone"
                        label="Home Phone (Optional)"
                        placeholder="(555) 123-4567"
                        outlined
                        v-mask="'(###) ###-####'"
                        prepend-inner-icon="mdi-phone-classic"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                  
                  <v-row>
                    <v-col cols="12" sm="6">
                      <v-select
                        v-model="enrollment.employmentStatus"
                        label="Employment Status"
                        :items="employmentStatusOptions"
                        outlined
                        prepend-inner-icon="mdi-briefcase"
                        :rules="[rules.required]"
                        required
                      ></v-select>
                    </v-col>
                  </v-row>
                  
                  <!-- Employment Details (conditional) -->
                  <v-row v-if="enrollment.employmentStatus === 'Employed'">
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="enrollment.employerName"
                        label="Employer Name"
                        placeholder="Company Inc."
                        outlined
                        prepend-inner-icon="mdi-domain"
                        :rules="[rules.required]"
                        required
                      ></v-text-field>
                    </v-col>
                    
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="enrollment.workPhone"
                        label="Work Phone"
                        placeholder="(555) 123-4567"
                        outlined
                        v-mask="'(###) ###-####'"
                        prepend-inner-icon="mdi-phone"
                        :rules="[rules.required]"
                        required
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <!-- Q15: Review & Sign -->
                <div v-else-if="currentQuestion === 15">
                  <h2 class="question-title">Almost done! Review and sign</h2>
                  
                  <v-card class="mb-4" outlined>
                    <v-card-title class="subtitle-1 primary white--text">
                      Your Information
                    </v-card-title>
                    <v-card-text>
                      <v-btn
                        text
                        color="primary"
                        class="mb-4"
                        @click="goToCustomization"
                      >
                        <v-icon left>mdi-pencil</v-icon>
                        Edit specialists and benefits
                      </v-btn>
                      <v-simple-table dense>
                        <tbody>
                          <tr>
                            <td class="font-weight-medium">Name</td>
                            <td>{{ enrollment.firstName }} {{ enrollment.lastName }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-medium">Email</td>
                            <td>{{ enrollment.email }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-medium">Phone</td>
                            <td>{{ enrollment.cellPhone }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-medium">Address</td>
                            <td>{{ enrollment.streetAddress }}, {{ enrollment.city }}, {{ enrollment.state }} {{ enrollment.zipCode }}</td>
                          </tr>
                          <tr v-if="enrollment.dependents.length > 0">
                            <td class="font-weight-medium">Dependents</td>
                            <td>{{ enrollment.dependents.length }} dependent(s)</td>
                          </tr>
                          <tr>
                            <td class="font-weight-medium">Plan</td>
                            <td>{{ selectedPlanName }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-medium">Coverage</td>
                            <td>{{ enrollment.familySize }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-medium">Base Plan Monthly Fee</td>
                            <td>${{ basePlanMonthlyFee.toFixed(2) }}</td>
                          </tr>
                          <tr>
                          <tr v-if="totalBenefitsCost > 0 && !isBenefitsOnlyPlan">
                            <td class="font-weight-medium">Additional Benefits</td>
                            <td>+${{ totalBenefitsCost.toFixed(2) }}/month</td>
                          </tr>
                          <tr v-if="totalSpecialtyProvidersSelected > 0">
                            <td class="font-weight-medium">Specialty Providers</td>
                            <td>
                              {{ totalSpecialtyProvidersSelected }} provider(s)
                              <span v-if="totalSpecialtyProvidersSelected > 0">
                                ({{ totalSpecialtyProvidersSelected }} specialist{{ totalSpecialtyProvidersSelected !== 1 ? 's' : '' }})
                              </span>
                              <span v-if="totalSpecialtyCost > 0">
                                (+${{ totalSpecialtyCost.toFixed(2) }}/month)
                              </span>
                            </td>
                          </tr>
                          <tr>
                            <td class="font-weight-medium">Monthly Fee</td>
                            <td>${{ (enrollment.payment.monthlyFee || 0).toFixed(2) }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-medium">Application Fee</td>
                            <td>${{ (enrollment.payment.applicationFee || 0).toFixed(2) }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-medium">Total Payment</td>
                            <td class="text-h6 success--text">${{ totalPayment.toFixed(2) }}</td>
                          </tr>
                        </tbody>
                      </v-simple-table>
                    </v-card-text>
                  </v-card>

                  <v-alert
                    v-if="selectedPlanDisclosure"
                    type="warning"
                    text
                    dense
                    class="mb-4"
                  >
                    <div class="text-subtitle-2 font-weight-bold mb-2">
                      ⚠️ Plan Disclosure
                    </div>
                    <div class="text-body-2" v-html="selectedPlanDisclosure"></div>
                  </v-alert>

                  <!-- Benefits Summary (for bundle pricing) -->
                  <v-card outlined class="mb-4" v-if="totalBenefitsCost > 0">
                    <v-card-title class="subtitle-1">
                      Additional Benefits Summary
                    </v-card-title>
                    <v-card-text>
                      <div class="text-body-1 mb-2">
                        <span class="font-weight-medium">Total Benefits Cost:</span>
                        <span class="text-h6 primary--text ml-2">${{ totalBenefitsCost.toFixed(2) }}/month</span>
                      </div>
                      <div class="text-caption grey--text">
                        Benefits with bundle pricing are calculated at the household level based on the number of members who selected them.
                      </div>
                      <v-divider class="my-3"></v-divider>
                      <div class="text-body-2">
                        <div
                          v-for="benefitId in getSelectedBundleBenefits()"
                          :key="benefitId"
                          class="mb-1"
                        >
                          <span class="font-weight-medium">{{ getBenefitNameById(benefitId) }}:</span>
                          <span class="ml-2">${{ getFamilyBenefitPriceById(benefitId).toFixed(2) }}/month</span>
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>

                  <v-card outlined class="mb-4" v-if="memberCostBreakdown.length">
                    <v-card-title class="subtitle-1">
                      Member Coverage Details
                    </v-card-title>
                    <v-card-text>
                      <div
                        v-for="(member, index) in memberCostBreakdown"
                        :key="member.memberKey || index"
                        class="mb-4"
                      >
                        <div class="font-weight-bold" style="color: #1f2937 !important;">
                          {{ formatBreakdownMemberName(member, index) }}
                        </div>
                        <div class="text-body-2 grey--text" v-if="allowsSpecialtyProviders">
                          Specialty providers: {{ member.specialtyCount }}
                          <span v-if="(member.includedSpecialtyCount || 0) + (member.extraSpecialtyCount || 0) > 0">
                            ({{ (member.includedSpecialtyCount || 0) + (member.extraSpecialtyCount || 0) }} specialist{{ ((member.includedSpecialtyCount || 0) + (member.extraSpecialtyCount || 0)) !== 1 ? 's' : '' }})
                          </span>
                          · ${{ member.specialtyCost.toFixed(2) }}
                        </div>
                        <div class="text-body-2 grey--text" v-if="allowsSpecialtyProviders">
                          <span class="font-weight-medium">Selected specialties:</span>
                          <span v-if="member.specialties && member.specialties.length">
                            {{ member.specialties.join(', ') }}
                          </span>
                          <span v-else>
                            None selected
                          </span>
                        </div>
                        <div class="text-body-2 grey--text">
                          Benefits:
                          <span v-if="member.benefits.length === 0">None</span>
                          <span v-else>
                            <span
                              v-for="(benefit, benefitIndex) in member.benefits"
                              :key="benefit.benefitId || benefitIndex"
                            >
                              {{ benefit.name }}
                              <span v-if="benefit.usesBundlePricing" class="text-caption">
                                (included in household total)
                              </span>
                              <span v-else-if="benefit.price > 0">
                                (+${{ benefit.price.toFixed(2) }})
                              </span>
                              <span v-else-if="benefit.isIncluded" class="text-caption">
                                (included)
                              </span>
                              <span v-if="benefitIndex < member.benefits.length - 1">, </span>
                            </span>
                          </span>
                        </div>
                        <div class="text-subtitle-2 font-weight-bold primary--text mt-2">
                          Total extras: ${{ member.totalExtras.toFixed(2) }}
                        </div>
                        <v-divider class="my-3" v-if="index < memberCostBreakdown.length - 1"></v-divider>
                      </div>
                    </v-card-text>
                  </v-card>
                  
                  <v-card outlined class="mb-4">
                    <v-card-title class="subtitle-1">
                      Sign Here
                    </v-card-title>
                    <v-card-text>
                      <div class="signature-pad-conversational">
                        <canvas
                          ref="signatureCanvas"
                          class="signature-canvas-conversational"
                          @mousedown="startDrawing"
                          @mousemove="draw"
                          @mouseup="stopDrawing"
                          @mouseleave="stopDrawing"
                          @touchstart="startDrawing"
                          @touchmove="draw"
                          @touchend="stopDrawing"
                        ></canvas>
                      </div>
                      <div class="d-flex justify-space-between align-center mt-2">
                        <span class="text-caption" style="color: #374151 !important;">Sign using your mouse or finger</span>
                        <v-btn text small @click="clearSignature" color="error">
                          <v-icon left small>mdi-eraser</v-icon>
                          Clear
                        </v-btn>
                      </div>
                    </v-card-text>
                  </v-card>
                  
                  <v-alert type="info" text dense>
                    <strong>Disclosure:</strong> This is NOT INSURANCE. By signing, you acknowledge you have read and agree to the terms.
                  </v-alert>
                </div>

                <!-- Q16: Payment Form (Credit/Debit Card Only) -->
                <div v-else-if="currentQuestion === 16">
                  <h2 class="question-title">Complete Your Payment</h2>
                  
                  <MidPayCollectForm
                    v-if="submittedEnrollmentId"
                    :enrollment-id="submittedEnrollmentId"
                    :amount="totalPayment"
                    :enrollment-data="{
                      primaryMember: {
                        firstName: enrollment.firstName,
                        lastName: enrollment.lastName
                      },
                      contactInfo: {
                        address: enrollment.streetAddress,
                        city: enrollment.city,
                        state: enrollment.state,
                        zip: enrollment.zipCode,
                        phone: enrollment.cellPhone
                      }
                    }"
                    @payment-success="handlePaymentSuccess"
                    @payment-error="handlePaymentError"
                    @cancel="handlePaymentCancel"
                  />
                  
                  <div v-else class="text-center pa-8">
                    <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
                    <p class="mt-4">Preparing payment form...</p>
                  </div>
                </div>

                <!-- Success Screen -->
                <div v-else-if="currentQuestion === 17" class="text-center">
                  <v-icon size="100" color="success" class="mb-4">mdi-check-circle</v-icon>
                  <h1 class="display-2 mb-4">You're all set!</h1>
                  <p class="text-h6 mb-4" style="color: #374151 !important;">
                    Your enrollment has been submitted successfully.
                  </p>
                  <v-card class="mb-4 mx-auto" max-width="500" outlined>
                    <v-card-text>
                      <div class="text-caption" style="color: #374151 !important;">Application Code</div>
                      <div class="text-h4 primary--text font-weight-bold mb-2">{{ submittedApplicationCode || 'Generating...' }}</div>
                      <div class="text-caption grey--text mb-3">Please save this code for your records</div>
                      <v-divider class="my-3"></v-divider>
                      <div class="text-body-2">
                        We've sent a confirmation email to <strong>{{ enrollment.email }}</strong>
                      </div>
                    </v-card-text>
                  </v-card>
                  
                  <v-btn 
                    large 
                    color="primary" 
                    @click="downloadMemberCard" 
                    :disabled="!submittedEnrollmentId || downloadingCard"
                    :loading="downloadingCard"
                    class="mr-2 mb-2"
                  >
                    <v-icon left>mdi-card-account-details</v-icon>
                    Download Member Card{{ enrollment.dependents && enrollment.dependents.length > 0 ? 's' : '' }}
                  </v-btn>
                  
                  <br>
                  
                  <v-btn large color="primary" @click="startNewEnrollment" class="mr-2">
                    <v-icon left>mdi-plus</v-icon>
                    New Enrollment
                  </v-btn>
                  <v-btn large text @click="goToLogin">
                    Back
                  </v-btn>
                </div>

              </div>
            </transition>
          </div>

          <!-- Navigation Buttons -->
          <div class="nav-footer" v-if="currentQuestion > 0 && currentQuestion < 17">
            <!-- Hide default Continue button on Q10 when disclosure is shown (we have a custom button) -->
            <!-- Hide button on Q16 (payment page) as it's not needed -->
            <v-btn
              v-if="!(currentQuestion === 11 && selectedPlanKey && selectedPlanDisclosure) && currentQuestion !== 16"
              x-large
              color="primary"
              @click="handleNext"
              :disabled="!canProceed"
              :loading="submitting"
              block
              elevation="2"
            >
              Continue
              <v-icon right>mdi-arrow-right</v-icon>
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </v-container>
    
    <!-- Modal de confirmação para cancelar enrollment -->
    <v-dialog v-model="showCancelDialog" max-width="500">
      <v-card>
        <v-card-title class="headline">
          <v-icon left color="warning">mdi-alert-circle</v-icon>
          Cancel Enrollment?
        </v-card-title>
        
        <v-card-text>
          Are you sure you want to cancel your enrollment? All your progress will be lost and you'll need to start over.
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn 
            text 
            @click="showCancelDialog = false"
            color="grey"
          >
            Keep Going
          </v-btn>
          <v-btn 
            color="error" 
            @click="confirmCancel"
            depressed
          >
            Cancel Enrollment
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import axios from 'axios';
import MidPayCollectForm from '@/components/MidPayCollectForm.vue';

export default {
  name: 'FHCEnrollmentConversational',
  
  components: {
    MidPayCollectForm
  },
  
  data() {
    return {
      currentQuestion: 0,
      submitting: false,
      showCancelDialog: false,
      submittedEnrollmentId: null,
      submittedApplicationCode: null,
      hasDependents: null,
      downloadingCard: false,
      isHandlingPopState: false, // Flag to prevent history update loops
      
      // Address autocomplete
      addressSearchText: '',
      selectedAddress: null,
      addressSuggestions: [],
      searchingAddress: false,
      addressAutoFilled: false,
      addressSearchTimeout: null,
      googleMapsLoaded: false,
      
      // Drawing
      isDrawing: false,
      signatureContext: null,
      
      enrollment: {
        firstName: '',
        lastName: '',
        dateOfBirth: null,
        gender: '',
        email: '',
        cellPhone: '',
        streetAddress: '',
        city: '',
        state: 'FL',
        zipCode: '',
        maritalStatus: '',
        dependents: [],
        planId: null,
        planType: '',
        planName: '',
        selectedSpecialtyProviders: 0, // Number of specialty providers selected
        familySize: 'Single',
        ageGroup: '',
        homePhone: '',
        employmentStatus: '',
        employerName: '',
        workPhone: '',
        payment: {
          method: '',
          monthsPaid: 1,
          monthlyFee: 0,
          applicationFee: 0,
          processingFee: 0,
          totalPayment: 0
        },
        signature: '',
        signatureDate: new Date().toISOString().substr(0, 10),
        status: 'Draft'
      },
      
      availablePlans: [],
      mainPlans: [], // Only main plans (PHC, DPO)
      additionalBenefits: [], // Only additional benefits
      selectedAdditionalBenefits: [], // Aggregated unique benefit IDs selected across members
      memberSelections: [],
      currentMemberSelectionIndex: 0,
      calculatedTotalPrice: 0, // Total price with bundle discounts
      selectedPlanDisclosure: null, // Disclosure text for selected plan
      disclosureAccepted: false, // Whether disclosure has been accepted
      selectedPlanKey: null,
      isBenefitsOnlyPlan: false,
      benefitsOnlyPlanOption: null,
      standaloneCostAllocation: null,
      
      // Client branding/theme
      clientConfig: null,
      clientCode: 'FHC', // Default to Family Health Choice

      baselineIncludedBenefitCodes: ['DENTAL-CORE', 'HMA-001'], // RX-001 and TM-001 are optional, not included by default
      includedBenefitCodesByPlan: {},
      includedBenefitIds: [],
      coreDentalBenefitId: null,
      essentialDentalBenefitId: null,
      eliteDentalBenefitId: null,
      basePlanMonthlyFee: 0,
      basePlanProcessingFee: 0,
      totalBenefitsCost: 0,
      totalSpecialtyCost: 0,
      memberCostBreakdown: [],
      isCalculatingPrice: false,
      pendingPriceRecalc: false,
      priceRecalcTimeoutId: null,
      signatureInitAttempts: 0,
      isInitializingSignature: false,
      q15RecalcDone: false,
      
      benefitOptions: [
        { value: 'medical', text: 'Medical Savings', icon: 'mdi-medical-bag', description: 'Doctor visits & treatments' },
        { value: 'dental', text: 'Dental Care', icon: 'mdi-tooth', description: 'Preventive & restorative' },
        { value: 'vision', text: 'Vision Care', icon: 'mdi-eye', description: 'Eye exams & glasses' },
        { value: 'pharmacy', text: 'Pharmacy Discounts', icon: 'mdi-pill', description: 'Prescription savings' },
        { value: 'telemedicine', text: 'TeleMedicine', icon: 'mdi-video', description: '24/7 virtual doctor' },
        { value: 'hospital', text: 'Hospital Advocacy', icon: 'mdi-hospital-building', description: 'Bill negotiation' },
        { value: 'lab', text: 'Laboratory', icon: 'mdi-test-tube', description: 'Lab work discounts' },
        { value: 'specialty', text: 'Specialty Care', icon: 'mdi-doctor', description: 'Specialist visits' },
        { value: 'preventive', text: 'Preventive Care', icon: 'mdi-shield-check', description: 'Wellness checkups' }
      ],
      
      availableSpecialties: [
        'Allergy/Immunology',
        'Cardiac Electrophysiology',
        'Cardiology',
        'Chiropractor',
        'Dermatology',
        'Endocrinology',
        'Gastroenterology',
        'General Surgery',
        'Hand Surgery',
        'Hematology (Blood Disease)',
        'Infectious Disease',
        'Interventional Cardiology',
        'Interventional Radiology',
        'Mental Health Counselor',
        'Nephrology',
        'Neurology',
        'Neuropsychology',
        'Nurse Practitioner',
        'Nurse Practitioner Cardiology',
        'Nurse Practitioner Family',
        'Nurse Practitioner Mental Health',
        'Nurse Practitioner Orthopaedic',
        'Obstetrics & Gynecology',
        'Occupational Therapy',
        'Ophthalmology',
        'Optometry',
        'Orthopaedic',
        'Orthopaedic Surgeon',
        'Orthopaedic Surgery',
        'Otolaryngology (ENT)',
        'Pain Management',
        'Physical Medicine and Rehabilitation',
        'Physical Therapy',
        'Plastic Surgery',
        'Podiatry',
        'Psychiatry',
        'Psychiatry & Neurology',
        'Psychology',
        'Pulmonology',
        'Rheumatology',
        'Sleep Medicine',
        'Sports Medicine',
        'Urgent Care',
        'Urology'
      ],
      
      standaloneBenefitPricing: {
        'hma-001': { single: 9.99, couple: 14.99, family: 19.99 },
        'tm-001': { single: 9.99, couple: 14.99, family: 19.99 },
        'dental-core': { single: 6.99, couple: 13.99, family: 19.99 },
        'dental-essential': { single: 9.99, couple: 19.99, family: 28.99 },
        'dental-elite': { single: 13.99, couple: 28.99, family: 43.99 },
        'rx-001': { single: 11.99, couple: 15.99, family: 19.99 }
      },
      
      maritalStatuses: [
        { value: 'Single', text: 'Single', icon: 'mdi-account' },
        { value: 'Married', text: 'Married', icon: 'mdi-account-heart' },
        { value: 'Divorced', text: 'Divorced', icon: 'mdi-account-remove' },
        { value: 'Widowed', text: 'Widowed', icon: 'mdi-account-cancel' }
      ],
      
      // coverageOptions removed - familySize is now auto-calculated from dependents
      
      employmentStatusOptions: [
        { text: 'Employed', value: 'Employed' },
        { text: 'Self-Employed', value: 'Self-Employed' },
        { text: 'Retired', value: 'Retired' }
      ],
      
      rules: {
        required: value => !!value || 'This field is required',
        email: value => {
          const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          return pattern.test(value) || 'Please enter a valid email';
        },
        phone: value => {
          const pattern = /^\(\d{3}\) \d{3}-\d{4}$/;
          return !value || pattern.test(value) || 'Please enter a valid phone';
        }
      }
    };
  },
  
  computed: {
    totalQuestions() {
      // Q1-Q15 are actual questions (Q0 is welcome, Q16 is payment, Q17 is success)
      // Q11 (Specialty Providers) may be skipped if plan doesn't allow it
      // Q13 is skipped (auto-calculated from dependents)
      // Q16 (payment method) was removed - payment is always by card
      // Base: Q1-Q10, Q12, Q14-Q15 = 13 questions
      let total = 13;
      if (!this.hasDependents) total--; // Subtract Q9 (dependent entry) if no dependents
      return total;
    },
    
    displayQuestionNumber() {
      // Map internal question number to display question number
      let displayNum = this.currentQuestion;
      
      // Subtract 1 for skipped Q13 if we're past it
      if (this.currentQuestion > 13) {
        displayNum--;
      }
      
      // Subtract 1 for skipped Q11 if plan doesn't allow specialty providers and we're past it
      // Subtract 1 for skipped Q9 if no dependents and we're past it
      if (!this.hasDependents && this.currentQuestion > 9) {
        displayNum--;
      }
      
      return displayNum;
    },
    
    progressPercentage() {
      // Don't show progress on welcome (0) or success (17) screens
      if (this.currentQuestion === 0) return 0;
      if (this.currentQuestion >= 17) return 100;
      
      // Calculate progress based on display question number
      return ((this.displayQuestionNumber - 1) / (this.totalQuestions - 1)) * 100;
    },
    
    filteredPlans() {
      // Keep for backward compatibility, but now just returns main plans
      return this.mainPlans;
    },
    
    selectedPlanName() {
      if (this.isBenefitsOnlyPlan) {
        return this.benefitsOnlyPlanOption ? this.benefitsOnlyPlanOption.name : 'Benefits Only';
      }
      // For PHC plans, use the dynamically updated planName (Basic/Plus/Premium)
      if (this.enrollment.planName && this.enrollment.planName.toLowerCase().includes('phc')) {
        return this.enrollment.planName;
      }
      // For other plans, use the plan name from availablePlans
      const plan = this.availablePlans.find(p => p._id === this.enrollment.planId);
      return plan ? (plan.displayName || plan.name || plan.planName) : (this.enrollment.planName || '');
    },
    
    selectedPlanMaxDependents() {
      // All plans have a fixed limit: 1 subscriber + 5 dependents
      return 5;
    },

    membersList() {
      // For all plans (including Benefits Only), return individual members
      // Each member can select their own benefits independently
      const members = [];
      members.push({
        memberKey: 'primary',
        memberIndex: 0,
        isPrimary: true,
        firstName: this.enrollment.firstName,
        lastName: this.enrollment.lastName,
        relationship: 'Primary'
      });
      if (Array.isArray(this.enrollment.dependents)) {
        this.enrollment.dependents.forEach((dep, index) => {
          members.push({
            memberKey: `dependent-${index}`,
            memberIndex: index + 1,
            isPrimary: false,
            firstName: dep.firstName,
            lastName: dep.lastName,
            relationship: dep.relationship || 'Dependent'
          });
        });
      }
      return members;
    },

    currentMember() {
      return this.membersList[this.currentMemberSelectionIndex] || null;
    },

    currentMemberSelection() {
      return this.memberSelections[this.currentMemberSelectionIndex] || null;
    },

    currentMemberSpecialties: {
      get() {
        const selection = this.currentMemberSelection;
        return selection && Array.isArray(selection.selectedSpecialties)
          ? selection.selectedSpecialties
          : [];
      },
      set(newValues) {
        this.updateCurrentMemberSpecialties(newValues || []);
      }
    },

    currentSpecialtyBreakdown() {
      if (!this.allowsSpecialtyProviders) return null;
      const breakdown = this.generateSpecialtyBreakdown();
      return breakdown[this.currentMemberSelectionIndex] || null;
    },

    currentMemberSpecialtyCount() {
      const breakdown = this.currentSpecialtyBreakdown;
      if (!breakdown) {
        return this.currentMemberSpecialties.length;
      }
      return breakdown.count;
    },

    currentMemberIncludedSpecialties() {
      const breakdown = this.currentSpecialtyBreakdown;
      return breakdown ? breakdown.included : 0;
    },

    currentMemberExtraSpecialties() {
      const breakdown = this.currentSpecialtyBreakdown;
      return breakdown ? breakdown.extra : 0;
    },

    currentMemberSpecialtyCostValue() {
      const breakdown = this.currentSpecialtyBreakdown;
      return breakdown ? breakdown.cost : 0;
    },

    isCurrentMemberSpecialtyValid() {
      if (!this.allowsSpecialtyProviders) return true;
      const max = this.memberSpecialtyMax;
      if (max && max > 0 && this.totalSpecialtyProvidersSelected > max) {
        return false;
      }
      return true;
    },

    exceedsPlanSpecialtyLimit() {
      if (!this.allowsSpecialtyProviders) return false;
      const max = this.memberSpecialtyMax;
      return !!(max && max > 0 && this.totalSpecialtyProvidersSelected > max);
    },

    householdMemberCount() {
      const dependents = Array.isArray(this.enrollment.dependents) ? this.enrollment.dependents.length : 0;
      return 1 + dependents;
    },

    currentMemberConfirmed() {
      const selection = this.currentMemberSelection;
      // For all plans (including Benefits Only), check if current member is confirmed
      if (!selection) return false;
      // For plans with specialty providers, also check if specialty selection is valid
      if (this.allowsSpecialtyProviders && !this.isCurrentMemberSpecialtyValid) return false;
      return !!selection.confirmed;
    },

    allMembersConfirmed() {
      // For all plans, check if all members are confirmed
      return this.memberSelections.length > 0 &&
        this.memberSelections.every(selection => selection && selection.confirmed);
    },

    memberCount() {
      return this.membersList.length;
    },

    currentMemberCost() {
      return this.memberCostBreakdown[this.currentMemberSelectionIndex] || null;
    },

    memberSpecialtyMin() {
      return 0;
    },

    memberSpecialtyMax() {
      if (!this.allowsSpecialtyProviders) return 0;
      return this.maxSpecialtyProviders;
    },

    totalSpecialtyProvidersSelected() {
      return this.memberSelections.reduce((sum, selection) => {
        const specialties = selection?.selectedSpecialties;
        const count = Array.isArray(specialties) ? specialties.length : 0;
        return sum + count;
      }, 0);
    },

    totalExtraSpecialtyProviders() {
      // REMOVED: No specialists are included - all are extra
      return this.totalSpecialtyProvidersSelected; // All specialists are "extra" (charged)
    },

    totalIncludedSpecialtyProviders() {
      // REMOVED: No specialists are included
      return 0; // No specialists are included in any plan
    },

    effectiveIncludedBenefitIds() {
      // Benefits Only plan has NO included benefits
      if (this.isBenefitsOnlyPlan || 
          this.enrollment.planType === 'BenefitsOnly' ||
          (this.enrollment.planId && this.enrollment.planId.toString() === '000000000000000000000001')) {
        return [];
      }
      if (!Array.isArray(this.includedBenefitIds)) return [];
      return [...this.includedBenefitIds];
    },

    allowsSpecialtyProviders() {
      if (this.isBenefitsOnlyPlan) {
        return false;
      }
      if (!this.enrollment.planName && !this.enrollment.planId) {
        return false;
      }
      
      // Check by planId first (more reliable)
      if (this.enrollment.planId) {
        const plan = this.availablePlans.find(p => p._id === this.enrollment.planId);
        if (plan) {
          const planName = (plan.planName || '').toLowerCase();
          // Discount Plan - cannot select specialists
          if (planName.includes('discount')) {
            return false;
          }
          // PHC Plan - can select specialists (type will be determined by count)
          if (planName.includes('phc') || plan.planType === 'PHC') {
            return true;
          }
        }
      }
      
      // Fallback to planName check
      if (this.enrollment.planName) {
        const planName = this.enrollment.planName.toLowerCase();
        
        // Discount Plan - cannot select specialists or providers
        if (planName.includes('discount')) {
          return false;
        }
        
        // PHC Plan - can select specialists (type determined by count)
        // Even if it's currently "Basic", user can select specialists which will change it to Plus/Premium
        if (planName.includes('phc')) {
          return true;
        }
      }
      
      return false;
    },
    
    maxSpecialtyProviders() {
      if (!this.enrollment.planName) return 0;
      const planName = this.enrollment.planName.toLowerCase();
      
      // Discount Plan - no specialists allowed
      if (planName.includes('discount')) return 0;
      
      // PHC plans - no max limit (user can select as many as needed)
      // Type is determined by count: 0=Basic, 1-2=Plus, 3+=Premium
      if (planName.includes('phc')) return 999; // Very high limit, effectively unlimited
      
      return 0;
    },
    
    // Check if plan has specialty providers included by default
    // REMOVED: No specialists are included in any plan - all are charged extra
    hasIncludedSpecialtyProviders() {
      return false; // No specialists are included in any plan
    },
    
    // Get the number of included specialty providers
    // REMOVED: No specialists are included - all are charged extra
    includedSpecialtyProviders() {
      return 0; // No specialists are included in any plan
    },
    
    // Get the number of extra specialty providers
    // All specialists are charged extra (no included ones)
    extraSpecialtyProviders() {
      return this.enrollment.selectedSpecialtyProviders || 0; // All specialists are "extra"
    },
    
    benefitKeywords() {
      return {
        medical: ['medical', 'physician', 'doctor', 'primary care'],
        dental: ['dental', 'tooth', 'dentist'],
        vision: ['vision', 'eye', 'glasses'],
        pharmacy: ['pharmacy', 'prescription', 'drug', 'medication'],
        telemedicine: ['telemedicine', 'telemed', 'virtual', 'online'],
        hospital: ['hospital', 'advocacy', 'bill negotiation'],
        lab: ['laboratory', 'lab', 'test'],
        specialty: ['specialty', 'specialist'],
        preventive: ['preventive', 'wellness', 'checkup']
      };
    },
    
    canProceed() {
      switch(this.currentQuestion) {
        case 1: return !!this.enrollment.firstName;
        case 2: return !!this.enrollment.lastName;
        case 3: return !!this.enrollment.dateOfBirth;
        case 4: return !!this.enrollment.gender;
        case 5: return !!this.enrollment.email && this.rules.email(this.enrollment.email) === true;
        case 6: return !!this.enrollment.cellPhone && this.enrollment.cellPhone.length >= 10;
        case 7: return !!this.enrollment.streetAddress && !!this.enrollment.city && !!this.enrollment.state && !!this.enrollment.zipCode;
        case 8: return !!this.enrollment.maritalStatus;
        case 9: return this.hasDependents !== null;
        case 10: {
          // All dependents must have required fields: firstName, lastName, and dateOfBirth
          if (!this.enrollment.dependents || this.enrollment.dependents.length === 0) {
            return false;
          }
          
          // Check that every dependent has all required fields
          return this.enrollment.dependents.every(dep => 
            dep.firstName && 
            dep.firstName.trim().length > 0 && 
            dep.lastName && 
            dep.lastName.trim().length > 0 && 
            dep.dateOfBirth
          );
        }
        case 11: return this.selectedPlanKey && this.disclosureAccepted; // Plan selection with disclosure acceptance
        case 12: return this.isBenefitsOnlyPlan ? true : (this.memberSelections.length > 0 && this.allMembersConfirmed);
        case 13: return true;
        case 14: {
          // Employment Status is required
          const hasEmploymentStatus = !!this.enrollment.employmentStatus;
          
          // If employed, employer name and work phone are required
          if (this.enrollment.employmentStatus === 'Employed') {
            return hasEmploymentStatus && 
                   !!this.enrollment.employerName && 
                   !!this.enrollment.workPhone && 
                   this.enrollment.workPhone.length >= 10;
          }
          
          return hasEmploymentStatus;
        }
        case 15: return !!this.enrollment.signature;
        default: return true;
      }
    },
    
    totalPayment() {
      const monthlyFee = parseFloat(this.enrollment.payment.monthlyFee || 0);
      const monthsPaid = parseInt(this.enrollment.payment.monthsPaid || 1, 10);
      const applicationFee = parseFloat(this.enrollment.payment.applicationFee || 0);
      const total = (monthlyFee * monthsPaid) + applicationFee;
      return parseFloat(total.toFixed(2));
    }
  },
  
  watch: {
    // Watch for changes in enrollment date of birth to recalculate price
    'enrollment.dateOfBirth': {
      handler(newDate) {
        if (newDate && (this.enrollment.planId || this.isBenefitsOnlyPlan)) {
          // Recalculate price when primary member's DOB changes and plan is selected
          this.$nextTick(() => {
            this.scheduleBundleRecalculation();
          });
        }
      },
      deep: false
    },
    
    // Watch for changes in dependents array (addition/removal)
    'enrollment.dependents': {
      handler(newDependents) {
        console.log('👀 Dependents watcher triggered');
        console.log('   New dependents:', JSON.parse(JSON.stringify(newDependents)));
        console.log('   Plan selected:', this.enrollment.planId || (this.isBenefitsOnlyPlan ? 'benefits-only' : null));
        this.syncMemberSelections();
        if (this.enrollment.planId || this.isBenefitsOnlyPlan) {
          this.$nextTick(() => {
            this.scheduleBundleRecalculation();
          });
        }
      },
      deep: true
    },
    
    // Watch for address search text changes
    addressSearchText(newValue) {
      if (!newValue || newValue.length < 3) {
        this.addressSuggestions = [];
        return;
      }
      
      // Debounce the search
      clearTimeout(this.addressSearchTimeout);
      this.addressSearchTimeout = setTimeout(() => {
        this.searchAddresses(newValue);
      }, 300);
    },
    
    // Recalculate price when reaching review screen (Q15)
    currentQuestion(newQuestion, oldQuestion) {
      if (newQuestion === 15 && oldQuestion !== 15 && (this.enrollment.planId || this.isBenefitsOnlyPlan)) {
        if (!this.q15RecalcDone) {
          console.log('📋 Reached Q15 (Review) - Recalculating bundle price to ensure accuracy...');
          this.q15RecalcDone = true;
          this.$nextTick(() => {
            this.scheduleBundleRecalculation();
          });
        }
      } else if (newQuestion !== 15) {
        // Reset flag when leaving Q15
        this.q15RecalcDone = false;
      }
    },
    
    membersList: {
      handler() {
        this.syncMemberSelections();
      },
      deep: true
    },
    
    memberSelections: {
      handler() {
        this.updateAggregatedBenefitSelections();
        if (this.isBenefitsOnlyPlan) {
          return;
        }
        if (this.enrollment.planId) {
          this.$nextTick(() => {
            this.scheduleBundleRecalculation();
          });
        }
      },
      deep: true
    }
  },
  
  async mounted() {
    await this.loadClientConfig();
    await this.loadPlans();
    this.applyTheme();
    this.hideChatWidgets();
    
    // Initialize browser history management
    this.initBrowserHistory();
    
    // Force calendar colors if starting on date picker question
    if (this.currentQuestion === 3) {
      setTimeout(() => {
        this.forceCalendarColors();
      }, 500);
    }
  },
  
  beforeDestroy() {
    this.showChatWidgets();
    // Remove popstate listener
    window.removeEventListener('popstate', this.handlePopState);
  },
  
  methods: {
    async loadClientConfig() {
      try {
        const response = await axios.get(`/api/enrollment-config/client/${this.clientCode}`);
        this.clientConfig = response.data;
        console.log('🎨 Client config loaded:', this.clientConfig.clientName);
      } catch (error) {
        console.warn('⚠️ Client config not found, using default theme');
        this.clientConfig = null;
      }
    },
    
    applyTheme() {
      if (!this.clientConfig || !this.clientConfig.branding) {
        console.log('Using default theme');
        return;
      }
      
      const branding = this.clientConfig.branding;
      
      // Apply CSS variables for theming
      const root = document.documentElement;
      if (branding.primaryColor) {
        root.style.setProperty('--theme-primary', branding.primaryColor);
      }
      if (branding.secondaryColor) {
        root.style.setProperty('--theme-secondary', branding.secondaryColor);
      }
      if (branding.accentColor) {
        root.style.setProperty('--theme-accent', branding.accentColor);
      }
      if (branding.lightColor) {
        root.style.setProperty('--theme-light', branding.lightColor);
      }
      if (branding.lighterColor) {
        root.style.setProperty('--theme-lighter', branding.lighterColor);
      }
      
      console.log('✅ Theme applied:', branding);
    },
    
    async loadPlans() {
      try {
        const response = await axios.get('/api/enrollment-config/plans');
        
        // Map all plans
        this.availablePlans = response.data.map(plan => {
          // Calculate startingPrice
          let startingPrice = 0;
          
          if (plan.flatPrice) {
            startingPrice = plan.flatPrice;
          } else if (plan.incrementalMemberPricing && plan.incrementalMemberPricing.enabled && plan.incrementalMemberPricing.pricing) {
            // For incremental pricing, use price for 1st member in age group 19-34 (most common)
            const pricing = plan.incrementalMemberPricing.pricing;
            if (pricing["19 - 34 Yrs"] && pricing["19 - 34 Yrs"]["1st"]) {
              startingPrice = pricing["19 - 34 Yrs"]["1st"];
            } else if (pricing["Under 18 Yrs"] && pricing["Under 18 Yrs"]["1st"]) {
              startingPrice = pricing["Under 18 Yrs"]["1st"];
            } else {
              // Fallback: use first available age group
              const firstAgeGroup = Object.keys(pricing)[0];
              if (firstAgeGroup && pricing[firstAgeGroup]["1st"]) {
                startingPrice = pricing[firstAgeGroup]["1st"];
              }
            }
          } else if (plan.pricingTiers && plan.pricingTiers.length > 0) {
            startingPrice = plan.pricingTiers[0]?.monthlyFee || 0;
          }
          
          return {
          _id: plan._id,
          name: plan.displayName || plan.planName,
          description: plan.shortDescription,
          planName: plan.planName,
          planCode: plan.planCode,
          planType: plan.planType,
          icon: plan.icon,
          color: plan.color,
          benefits: plan.benefits,
          pricingModel: plan.pricingModel,
          pricingTiers: plan.pricingTiers,
          bundlePricing: plan.bundlePricing,
          dpoBundlePricing: plan.dpoBundlePricing,
            incrementalMemberPricing: plan.incrementalMemberPricing,
          applicationFee: plan.applicationFee,
          processingFee: plan.processingFee || 0,
            startingPrice: startingPrice,
            flatPrice: plan.flatPrice,
          customFields: plan.customFields,
          allowDependents: plan.allowDependents,
          maxDependents: plan.maxDependents,
            legalNotice: plan.legalNotice,
            disclosures: plan.disclosures || [], // Include disclosures array
            disclosure: plan.disclosure || null // Include single disclosure field if exists
          };
        });
        
        // Filter plans: Only show PHC Plan (one generic), Discount Plan, and Benefits Only
        // First, get all PHC plans (excluding Dual)
        const phcPlans = this.availablePlans.filter(plan => {
          const planType = plan.planType || '';
          const planName = (plan.planName || '').toLowerCase();
          const displayName = (plan.displayName || plan.name || '').toLowerCase();
          
          // Exclude Dual plans
          if (planName.includes('dual') || displayName.includes('dual')) {
            return false;
          }
          
          // Only include PHC plans
          if (planType === 'PHC' && (planName.includes('phc') || displayName.includes('phc'))) {
            return true;
          }
          
          return false;
        });
        
        // Get Discount plans
        const discountPlans = this.availablePlans.filter(plan => {
          const planName = (plan.planName || '').toLowerCase();
          const displayName = (plan.displayName || plan.name || '').toLowerCase();
          return planName.includes('discount') || displayName.includes('discount');
        });
        
        // Build mainPlans: Only ONE PHC plan (use Basic as default), Discount Plan, and Benefits Only
        this.mainPlans = [];
        
        // Add ONE PHC plan (prefer Basic, or first available)
        if (phcPlans.length > 0) {
          // Try to find Basic first
          let phcPlan = phcPlans.find(p => {
            const name = (p.planName || '').toLowerCase();
            return name.includes('basic') && !name.includes('dual');
          });
          
          // If no Basic, use the first PHC plan
          if (!phcPlan) {
            phcPlan = phcPlans[0];
          }
          
          // Use the Basic plan's actual name if available, otherwise default to "PHC Basic Plan"
          let displayName = 'PHC Basic Plan';
          if (phcPlan.planName) {
            const planNameLower = phcPlan.planName.toLowerCase();
            if (planNameLower.includes('basic')) {
              // Use the actual plan name if it contains "basic"
              displayName = phcPlan.planName;
            } else {
              // If it's a PHC plan but not Basic, still show as "PHC Basic Plan" (lowest tier)
              displayName = 'PHC Basic Plan';
            }
          } else if (phcPlan.displayName || phcPlan.name) {
            // Fallback to displayName or name
            const fallbackName = (phcPlan.displayName || phcPlan.name || '').toLowerCase();
            if (fallbackName.includes('basic')) {
              displayName = phcPlan.displayName || phcPlan.name;
            } else {
              displayName = 'PHC Basic Plan';
            }
          }
          
          this.mainPlans.push({
            ...phcPlan,
            name: displayName,
            planName: displayName,
            displayName: displayName,
            description: phcPlan.description || 'Primary Health Care Plan - Type determined by specialty selections'
          });
        }
        
        // Add Discount Plan
        if (discountPlans.length > 0) {
          this.mainPlans.push(...discountPlans);
        }
        
        // Sort by price (highest first)
        this.mainPlans.sort((a, b) => {
          const priceA = parseFloat(a.flatPrice || a.startingPrice || 0);
          const priceB = parseFloat(b.flatPrice || b.startingPrice || 0);
          return priceB - priceA;
        });
        
        console.log('📋 Filtered main plans:', this.mainPlans.map(p => ({
          name: p.name,
          planName: p.planName,
          planType: p.planType
        })));
        
        // Check if "Benefits Only" plan exists in the database
        let benefitsOnlyPlan = this.availablePlans.find(plan => 
          plan.planType === 'BenefitsOnly' || 
          (plan.planName && plan.planName.toLowerCase() === 'benefits only')
        );
        
        // If not found, create a virtual plan with a valid ObjectId
        // Using a special ObjectId that represents "Benefits Only" (all zeros except last digit = 1)
        // This is a valid MongoDB ObjectId format that can be stored
        if (!benefitsOnlyPlan) {
          // Use a valid 24-character hex ObjectId for "Benefits Only"
          // Format: 000000000000000000000001 (valid ObjectId, easy to recognize)
          const benefitsOnlyId = '000000000000000000000001';
          benefitsOnlyPlan = {
            _id: benefitsOnlyId,
            name: 'Benefits Only',
            planName: 'Benefits Only',
            planType: 'BenefitsOnly',
            description: 'Skip the base plan and choose only the benefits you need.',
            icon: 'mdi-playlist-plus',
            startingPrice: 0,
            applicationFee: 25,
            benefits: ['Stand-alone benefits'],
            disclosure: '<strong>PLEASE READ:</strong> This selection provides access only to stand-alone benefit plans. You are responsible for reviewing each benefit\'s terms and any associated limitations. No medical or specialty provider network access is included beyond the benefits you select.',
            pricingModel: 'benefits-only'
          };
          console.log('📋 Created virtual "Benefits Only" plan with ID:', benefitsOnlyId);
        } else {
          console.log('📋 Found "Benefits Only" plan in database with ID:', benefitsOnlyPlan._id);
        }
        
        this.benefitsOnlyPlanOption = benefitsOnlyPlan;
        this.mainPlans.push(this.benefitsOnlyPlanOption);
        
        this.additionalBenefits = this.availablePlans.filter(plan => 
          plan.planType === 'Additional Benefit' || 
          ['Dental', 'Vision', 'Pharmacy', 'TeleMed', 'Hospital Advocacy'].includes(plan.planType)
        );
        
        console.log(`📊 Loaded ${this.mainPlans.length} main plans and ${this.additionalBenefits.length} additional benefits`);
        this.updateDentalBenefitIds();
        this.updateIncludedBenefitsForPlan();
        this.syncMemberSelections();
        
        if (this.enrollment.planType === 'BenefitsOnly') {
          this.isBenefitsOnlyPlan = true;
          this.selectedPlanKey = this.benefitsOnlyPlanOption?._id || 'benefits-only';
          // Use the Benefits Only plan's _id as planId (it's a valid plan, just with $0 cost)
          this.enrollment.planId = this.benefitsOnlyPlanOption?._id || null;
          this.enrollment.planName = this.benefitsOnlyPlanOption ? this.benefitsOnlyPlanOption.name : 'Benefits Only';
          this.enrollment.payment.applicationFee = 25;
          // Clear included benefits - Benefits Only has NO included benefits
          this.includedBenefitIds = [];
          this.selectedPlanDisclosure = this.benefitsOnlyPlanOption?.disclosure || this.selectedPlanDisclosure;
          this.$nextTick(() => this.scheduleBundleRecalculation(true));
        } else if (this.enrollment.planId) {
          this.selectedPlanKey = this.enrollment.planId;
        }
        
      } catch (error) {
        console.error('Error loading plans:', error);
      }
    },
    
    getIncludedBenefitCodesForPlan(plan) {
      if (!plan) return [];
      // Benefits Only plan does NOT have any included benefits
      if (this.isBenefitsOnlyPlan || plan.planType === 'BenefitsOnly' || 
          (plan.planName && plan.planName.toLowerCase() === 'benefits only')) {
        return [];
      }
      const codes = [...(this.baselineIncludedBenefitCodes || [])];
      const candidates = [
        (plan.planCode || '').toLowerCase(),
        (plan.planName || '').toLowerCase(),
        (plan.displayName || plan.name || '').toLowerCase()
      ];
      for (const key of candidates) {
        if (!key) continue;
        if (this.includedBenefitCodesByPlan[key]) {
          codes.push(...this.includedBenefitCodesByPlan[key]);
        }
      }
      return codes;
    },

    getIncludedBenefitIdsForPlan(plan) {
      // Benefits Only plan does NOT have any included benefits
      if (this.isBenefitsOnlyPlan || plan?.planType === 'BenefitsOnly' || 
          (plan?.planName && plan.planName.toLowerCase() === 'benefits only')) {
        return [];
      }
      const codes = this.getIncludedBenefitCodesForPlan(plan);
      if (!codes.length || !Array.isArray(this.additionalBenefits)) return [];
      const ids = [];
      codes.forEach(code => {
        const benefit = this.additionalBenefits.find(b => (b.planCode || '').toLowerCase() === code.toLowerCase());
        if (benefit) {
          ids.push(benefit._id);
        } else {
          console.warn(`⚠️ Included benefit with planCode ${code} not found among additional benefits`);
        }
      });
      return ids;
    },

    updateDentalBenefitIds() {
      if (!Array.isArray(this.additionalBenefits)) return;
      const findByCode = code => {
        const benefit = this.additionalBenefits.find(b => (b.planCode || '').toLowerCase() === code.toLowerCase());
        return benefit ? benefit._id : null;
      };
      this.coreDentalBenefitId = findByCode('DENTAL-CORE');
      this.essentialDentalBenefitId = findByCode('DENTAL-ESSENTIAL');
      this.eliteDentalBenefitId = findByCode('DENTAL-ELITE');
      this.syncMemberSelections();
    },

    syncMemberSelections(reset = false) {
      // For Benefits Only plan, also create selections per member (not just household)
      // Each member can select their own benefits
      const members = this.membersList;
      const availableBenefitIds = new Set(this.additionalBenefits.map(b => b._id));
      const includedIds = new Set(this.effectiveIncludedBenefitIds);
      const existingMap = new Map();

      if (!reset) {
        this.memberSelections.forEach(selection => {
          existingMap.set(selection.memberKey, selection);
        });
      }
      
      if (this.isBenefitsOnlyPlan) {
        // Create a selection for each member, allowing individual benefit selection
        const newSelections = members.map((member, index) => {
          const existing = existingMap.get(member.memberKey);
          let selectedBenefits = [];
          let confirmed = false; // Default to false

          if (existing) {
            selectedBenefits = (existing.selectedBenefits || []).filter(id => 
              availableBenefitIds.has(id) && !includedIds.has(id)
            );
            // Only preserve confirmed status if it's explicitly true
            // If undefined, null, or false, set to false
            confirmed = existing.confirmed === true && typeof existing.confirmed === 'boolean';
          }

          selectedBenefits = this.normalizeMemberBenefits(selectedBenefits);

          if (existing) {
            const originalBenefits = Array.isArray(existing.selectedBenefits) ? existing.selectedBenefits : [];
            const benefitsChanged = originalBenefits.length !== selectedBenefits.length ||
              originalBenefits.some(id => !selectedBenefits.includes(id));
            if (benefitsChanged) {
              confirmed = false; // Reset to false if benefits changed
            }
          }

          return {
            memberKey: member.memberKey,
            memberIndex: index,
            isPrimary: member.isPrimary,
            firstName: member.firstName,
            lastName: member.lastName,
            relationship: member.relationship,
            specialtyCount: 0,
            selectedSpecialties: [],
            selectedBenefits,
            confirmed: reset ? false : confirmed
          };
        });

        this.memberSelections = newSelections;

        if (reset) {
          this.currentMemberSelectionIndex = 0;
        } else if (this.currentMemberSelectionIndex >= newSelections.length) {
          this.currentMemberSelectionIndex = Math.max(newSelections.length - 1, 0);
        }
        return;
      }
      
      // For non-Benefits Only plans, continue with existing logic
      // (members, availableBenefitIds, includedIds, existingMap already declared above)

      const minRequirement = this.hasIncludedSpecialtyProviders ? this.includedSpecialtyProviders : 0;
      const maxSpecialtyAllowed = this.memberSpecialtyMax || null;
      const newSelections = members.map((member, index) => {
        const existing = existingMap.get(member.memberKey);
        let selectedSpecialties = [];
        let selectedBenefits = [];
        let confirmed = false;

        if (existing) {
          if (Array.isArray(existing.selectedSpecialties)) {
            selectedSpecialties = this.normalizeSelectedSpecialties(existing.selectedSpecialties);
          } else if (existing.specialtyCount && existing.specialtyCount > 0) {
            selectedSpecialties = [];
          }
          selectedBenefits = (existing.selectedBenefits || []).filter(id => 
            availableBenefitIds.has(id) && !includedIds.has(id)
          );
          confirmed = !!existing.confirmed;
        }

        if (!this.allowsSpecialtyProviders) {
          selectedSpecialties = [];
        } else if (!selectedSpecialties.length && this.hasIncludedSpecialtyProviders && !reset) {
          // keep as empty until user selects
          selectedSpecialties = [];
        }

        selectedBenefits = this.normalizeMemberBenefits(selectedBenefits);
        selectedSpecialties = this.normalizeSelectedSpecialties(selectedSpecialties);
        if (this.allowsSpecialtyProviders && maxSpecialtyAllowed && maxSpecialtyAllowed > 0 && selectedSpecialties.length > maxSpecialtyAllowed) {
          selectedSpecialties = selectedSpecialties.slice(0, maxSpecialtyAllowed);
        }

        if (existing) {
          const originalBenefits = Array.isArray(existing.selectedBenefits) ? existing.selectedBenefits : [];
          const benefitsChanged = originalBenefits.length !== selectedBenefits.length ||
            originalBenefits.some(id => !selectedBenefits.includes(id));
          const originalSpecialties = Array.isArray(existing.selectedSpecialties) ? existing.selectedSpecialties : [];
          const specialtiesChanged = originalSpecialties.length !== selectedSpecialties.length ||
            originalSpecialties.some(spec => !selectedSpecialties.includes(spec));
          if (benefitsChanged || specialtiesChanged) {
            confirmed = false;
          }
        }

        const specialtyCount = this.allowsSpecialtyProviders ? selectedSpecialties.length : 0;
        if (confirmed) {
          const meetsMin = specialtyCount >= minRequirement;
          const meetsMax = !maxSpecialtyAllowed || specialtyCount <= maxSpecialtyAllowed;
          if (!meetsMin || !meetsMax) {
            confirmed = false;
          }
        }

        return {
          memberKey: member.memberKey,
          memberIndex: index,
          isPrimary: member.isPrimary,
          firstName: member.firstName,
          lastName: member.lastName,
          relationship: member.relationship,
          specialtyCount,
          selectedSpecialties,
          selectedBenefits,
          confirmed: reset ? false : confirmed
        };
      });

      this.memberSelections = newSelections;

      if (reset) {
        this.currentMemberSelectionIndex = 0;
      } else if (this.currentMemberSelectionIndex >= newSelections.length) {
        this.currentMemberSelectionIndex = Math.max(newSelections.length - 1, 0);
      }
    },

    updateAggregatedBenefitSelections() {
      if (this.isBenefitsOnlyPlan) {
        this.includedBenefitIds = [];
        // Aggregate benefits from all member selections
        const benefitSet = new Set();
        this.memberSelections.forEach(selection => {
          (selection?.selectedBenefits || []).forEach(id => benefitSet.add(id));
        });
        this.selectedAdditionalBenefits = Array.from(benefitSet);
        return;
      }
      const benefitSet = new Set();
      let totalSpecialties = 0;
      this.memberSelections.forEach(selection => {
        const specialtyCount = Array.isArray(selection?.selectedSpecialties)
          ? selection.selectedSpecialties.length
          : (selection?.specialtyCount || 0);
        totalSpecialties += specialtyCount;
        (selection?.selectedBenefits || []).forEach(id => benefitSet.add(id));
      });
      this.selectedAdditionalBenefits = Array.from(benefitSet);
      this.enrollment.selectedSpecialtyProviders = totalSpecialties;
      
      // Update PHC plan type based on total specialists
      this.updatePHCPlanTypeBasedOnSpecialists();
    },

    updateIncludedBenefitsForPlan(planOverride = null) {
      if (this.isBenefitsOnlyPlan) {
        this.includedBenefitIds = [];
        return;
      }
      const plan = planOverride || this.availablePlans.find(p => p._id === this.enrollment.planId);
      if (!plan) {
        this.includedBenefitIds = [];
        return;
      }
      const includedIds = this.getIncludedBenefitIdsForPlan(plan);
      this.includedBenefitIds = includedIds;
      // Remove included benefits from optional selection to avoid duplicates
      this.selectedAdditionalBenefits = this.selectedAdditionalBenefits.filter(id => !includedIds.includes(id));
      if (Array.isArray(this.memberSelections) && this.memberSelections.length > 0) {
        const updatedSelections = this.memberSelections.map(selection => {
          const filtered = (selection.selectedBenefits || []).filter(id => !includedIds.includes(id));
          const normalized = this.normalizeMemberBenefits(filtered);
          const original = Array.isArray(selection.selectedBenefits) ? selection.selectedBenefits : [];
          const benefitsChanged = original.length !== normalized.length ||
            original.some(id => !normalized.includes(id));
          return {
            ...selection,
            selectedBenefits: normalized,
            specialtyCount: Array.isArray(selection.selectedSpecialties) ? selection.selectedSpecialties.length : (selection.specialtyCount || 0),
            confirmed: benefitsChanged ? false : selection.confirmed
          };
        });
        this.memberSelections = updatedSelections;
      }
    },

    generateSpecialtyBreakdown() {
      if (this.isBenefitsOnlyPlan) {
        return this.memberSelections.map(() => ({
          count: 0,
          included: 0,
          extra: 0,
          cost: 0,
          specialties: []
        }));
      }
      
      // Count total specialists across all members (summed within the plan)
      const totalSpecialists = this.memberSelections.reduce((total, selection) => {
        const specialties = Array.isArray(selection?.selectedSpecialties)
          ? this.normalizeSelectedSpecialties(selection.selectedSpecialties)
          : [];
        return total + specialties.length;
      }, 0);
      
      // Determine how many are included based on plan type
      // REMOVED: No specialists are included - all are charged extra
      // All specialists cost $5 each (no included ones)
      const totalExtraCost = totalSpecialists * 5; // $5 per specialist (total for the family)
      
      console.log('🔍 generateSpecialtyBreakdown:', {
        totalSpecialists,
        totalExtraCost: `$${totalExtraCost.toFixed(2)}`,
        planName: this.enrollment.planName
      });
      
      // Create breakdown for each member (for display purposes)
      // The cost is calculated at the family level, not per member
      // REMOVED: No specialists are included - all are charged extra
      const breakdown = [];
      
      this.memberSelections.forEach((selection, index) => {
        const specialties = Array.isArray(selection?.selectedSpecialties)
          ? this.normalizeSelectedSpecialties(selection.selectedSpecialties)
          : [];
        const count = specialties.length;
        
        // REMOVED: No specialists are included - all are charged extra
        const included = 0; // No specialists are included
        const extra = count; // All specialists are extra (charged)
        
        // Cost per specialist is $5
        // Each member pays $5 per specialist they select (for display purposes)
        const cost = count * 5; // $5 per specialist for this member
        
        console.log(`   Member ${index + 1}: count=${count}, included=${included}, extra=${extra}, cost=$${cost.toFixed(2)}`);
        
        breakdown.push({
          count,
          included,
          extra,
          cost, // $5 per specialist for this member
          specialties
        });
      });
      
      console.log('🔍 Breakdown summary:', {
        totalIncluded: 0, // No specialists are included
        totalExtra: breakdown.reduce((sum, b) => sum + b.extra, 0),
        familyExtraCost: `$${totalExtraCost.toFixed(2)}`
      });
      
      return breakdown;
    },

    getCanonicalSpecialty(value) {
      if (!value && value !== 0) return null;
      const normalize = str => String(str)
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .replace(/[^\w&()/\-\s]/g, '');
      const target = normalize(value);
      return this.availableSpecialties.find(item => normalize(item) === target) || null;
    },

    normalizeSelectedSpecialties(specialties = []) {
      if (!Array.isArray(specialties)) return [];
      const unique = [];
      specialties.forEach(rawValue => {
        const canonical = this.getCanonicalSpecialty(rawValue);
        if (canonical && !unique.includes(canonical)) {
          unique.push(canonical);
        }
      });
      return unique;
    },

    normalizeMemberBenefits(benefitIds = []) {
      const uniqueIds = Array.from(new Set(benefitIds));
      let normalized;

      if (this.isBenefitsOnlyPlan) {
        normalized = uniqueIds.filter(id => !!id);
      } else {
        normalized = uniqueIds.filter(id => id && id !== this.coreDentalBenefitId);
      }

      if (this.eliteDentalBenefitId && normalized.includes(this.eliteDentalBenefitId)) {
        // Elite replaces all other dental options
        normalized = normalized.filter(id => id === this.eliteDentalBenefitId);
        return normalized;
      }

      if (this.essentialDentalBenefitId && normalized.includes(this.essentialDentalBenefitId)) {
        // Essential removes Elite if present
        normalized = normalized.filter(id => id !== this.eliteDentalBenefitId);
      }

      return normalized;
    },

    isBenefitIncluded(benefitId) {
      return this.effectiveIncludedBenefitIds.includes(benefitId);
    },

    isBenefitSelected(benefitId, selection = this.currentMemberSelection) {
      if (!selection) return false;
      if (this.isBenefitIncluded(benefitId)) {
        if (this.isCoreDentalBenefit(benefitId) && this.isCoreDentalSuppressedForSelection(selection)) {
          return false;
        }
        return true;
      }
      return (selection.selectedBenefits || []).includes(benefitId);
    },

    isBenefitSelectedForCurrentMember(benefitId) {
      // For all plans (including Benefits Only), check the current member's selection
      // This allows each member to have their own benefit selections
      return this.isBenefitSelected(benefitId, this.currentMemberSelection);
    },

    isCoreDentalBenefit(benefitId) {
      return !!this.coreDentalBenefitId && benefitId === this.coreDentalBenefitId;
    },

    isCoreDentalSuppressed(benefitId, selection = this.currentMemberSelection) {
      if (!this.isCoreDentalBenefit(benefitId)) return false;
      return this.isCoreDentalSuppressedForSelection(selection);
    },

    isCoreDentalSuppressedForSelection(selection) {
      if (!selection) return false;
      const benefits = Array.isArray(selection.selectedBenefits) ? selection.selectedBenefits : [];
      return (this.essentialDentalBenefitId && benefits.includes(this.essentialDentalBenefitId)) ||
        (this.eliteDentalBenefitId && benefits.includes(this.eliteDentalBenefitId));
    },

    goToMember(index) {
      if (index < 0 || index >= this.membersList.length) return;
      this.currentMemberSelectionIndex = index;
    },

    updateCurrentMemberSpecialties(values) {
      if (!this.allowsSpecialtyProviders) return;
      const selection = this.currentMemberSelection;
      if (!selection) return;
      const normalized = this.normalizeSelectedSpecialties(values);
      const max = this.memberSpecialtyMax || null;
      const trimmed = max && max > 0 ? normalized.slice(0, max) : normalized;
      
      // Check if the values actually changed to avoid unnecessary updates
      const currentSpecialties = Array.isArray(selection.selectedSpecialties) ? selection.selectedSpecialties : [];
      const specialtiesChanged = currentSpecialties.length !== trimmed.length ||
        currentSpecialties.some(spec => !trimmed.includes(spec)) ||
        trimmed.some(spec => !currentSpecialties.includes(spec));
      
      if (!specialtiesChanged) {
        // No change, skip update to avoid loops
        return;
      }
      
      const updated = {
        ...selection,
        selectedSpecialties: trimmed,
        specialtyCount: trimmed.length,
        confirmed: false
      };
      this.$set(this.memberSelections, this.currentMemberSelectionIndex, updated);
      
      // Update PHC plan type based on total specialists selected
      // Use a small delay to avoid immediate recalculation loops
      this.$nextTick(() => {
        setTimeout(() => {
          if (!this.isCalculatingPrice) {
            this.updatePHCPlanTypeBasedOnSpecialists();
          }
        }, 50);
      });
    },
    
    // Determine PHC plan type based on number of specialists selected
    // 0 specialists = Basic, 1-2 = Plus, 3+ = Premium
    updatePHCPlanTypeBasedOnSpecialists() {
      // Only apply to PHC plans
      if (!this.enrollment.planId || this.isBenefitsOnlyPlan) {
        console.log('⚠️ updatePHCPlanTypeBasedOnSpecialists: Skipping - no planId or benefits only');
        return;
      }
      if (!this.enrollment.planName || !this.enrollment.planName.toLowerCase().includes('phc')) {
        console.log('⚠️ updatePHCPlanTypeBasedOnSpecialists: Skipping - not a PHC plan', this.enrollment.planName);
        return;
      }
      
      // Count total specialists across all members
      const totalSpecialists = this.memberSelections.reduce((total, selection) => {
        const specialties = Array.isArray(selection?.selectedSpecialties) 
          ? selection.selectedSpecialties 
          : [];
        return total + specialties.length;
      }, 0);
      
      console.log(`🔍 updatePHCPlanTypeBasedOnSpecialists: Total specialists = ${totalSpecialists}`);
      
      // Determine plan type
      let planType = 'Basic';
      if (totalSpecialists >= 3) {
        planType = 'Premium';
      } else if (totalSpecialists >= 1) {
        planType = 'Plus';
      }
      
      // Update planName to "PHC Plan [Type]" - always update, even if calculating price
      const newPlanName = `PHC Plan ${planType}`;
      const planNameChanged = this.enrollment.planName !== newPlanName;
      
      console.log(`📋 updatePHCPlanTypeBasedOnSpecialists: Current planName = "${this.enrollment.planName}", New planName = "${newPlanName}", Changed = ${planNameChanged}`);
      
      if (planNameChanged) {
        console.log(`📋 Updating PHC plan type: ${totalSpecialists} specialists → ${planType}`);
        this.enrollment.planName = newPlanName;
        console.log(`✅ Updated enrollment.planName to: "${this.enrollment.planName}"`);
      }
      
      // Prevent infinite loops - don't update price if we're currently calculating price
      // The price will be calculated in calculateBundlePrice based on the updated planName
      if (this.isCalculatingPrice) {
        return;
      }
      
      // REMOVED: Plus and Premium cost the same as Basic
      // No need to update basePlanMonthlyFee - all PHC plans (Basic/Plus/Premium) have the same base price
      // Only specialists are charged extra ($5 each)
      // The plan type (Basic/Plus/Premium) is only for categorization, not pricing
      
      // Only schedule recalculation if plan type changed (for display purposes)
      // The base price doesn't change, only the specialty cost changes
      if (planNameChanged) {
        // Schedule recalculation to update specialty costs
        // Use a small delay to avoid immediate recalculation loops
        this.$nextTick(() => {
          setTimeout(() => {
            if (!this.isCalculatingPrice) {
              this.scheduleBundleRecalculation(true);
            }
          }, 100);
        });
      }
    },

    handleMemberSelectionBack() {
      if (this.currentQuestion !== 12) return;
      if (this.currentMemberSelectionIndex > 0) {
        this.currentMemberSelectionIndex -= 1;
        return;
      }
      this.currentQuestion = 11;
    },

    handleMemberSelectionAdvance() {
      if (this.currentQuestion !== 12) return;
      if (!this.currentMemberConfirmed) return;
      if (this.currentMemberSelectionIndex < this.memberCount - 1) {
        this.currentMemberSelectionIndex += 1;
      }
    },

    confirmCurrentMember() {
      if (this.currentQuestion !== 12) return;
      // Allow confirmation for all plans, including Benefits Only
      const selection = this.currentMemberSelection;
      if (!selection) return;
      // Only check specialty validity if the plan allows specialty providers
      if (this.allowsSpecialtyProviders && !this.isCurrentMemberSpecialtyValid) return;
      if (!selection.confirmed) {
        const updated = {
          ...selection,
          confirmed: true
        };
        this.$set(this.memberSelections, this.currentMemberSelectionIndex, updated);
        console.log(`✅ Member ${this.currentMemberSelectionIndex} confirmed:`, {
          memberIndex: this.currentMemberSelectionIndex,
          memberName: `${selection.firstName || ''} ${selection.lastName || ''}`.trim(),
          selectedBenefits: selection.selectedBenefits,
          confirmed: true
        });
        
        // Recalculate price when member confirms (benefits may have changed)
        this.$nextTick(() => {
          this.scheduleBundleRecalculation(true);
        });
      }
      requestAnimationFrame(() => {
        let targetIndex = null;
        const nextUnconfirmed = this.memberSelections.findIndex((sel, idx) => idx > this.currentMemberSelectionIndex && !sel.confirmed);
        if (nextUnconfirmed !== -1) {
          targetIndex = nextUnconfirmed;
        } else {
          const firstUnconfirmed = this.memberSelections.findIndex(sel => !sel.confirmed);
          if (firstUnconfirmed !== -1) {
            targetIndex = firstUnconfirmed;
          }
        }
        // Auto-advance to next unconfirmed member for all plans
        if (targetIndex !== null) {
          this.currentMemberSelectionIndex = targetIndex;
        }
        const allConfirmedAfter = this.memberSelections.every(sel => sel && sel.confirmed);
        requestAnimationFrame(() => {
          if (!allConfirmedAfter) {
            this.scrollToTop();
          } else {
            this.scrollToBottom();
          }
        });
      });
    },

    getMemberDisplayLabel(member, index) {
      if (!member) return `Member ${index + 1}`;
      if (member.isPrimary) return 'Primary Member';
      const relation = member.relationship || 'Dependent';
      return `${relation} ${index}`;
    },

    formatMemberName(member) {
      if (!member) return '';
      if (this.isBenefitsOnlyPlan && member.relationship === 'Household') {
        return 'Household';
      }
      const first = member.firstName || '';
      const last = member.lastName || '';
      const name = `${first} ${last}`.trim();
      return name || this.getMemberDisplayLabel(member, member.memberIndex);
    },

    formatShortMemberLabel(member, index) {
      if (!member) return `Member ${index + 1}`;
      // For all plans, show individual member labels
      if (member.isPrimary) return 'Primary';
      const relation = member.relationship || '';
      if (relation && relation !== 'Household') return relation;
      return `Member ${index + 1}`;
    },

    formatBreakdownMemberName(member, index) {
      if (!member) return `Member ${index + 1}`;
      const name = `${member.firstName || ''} ${member.lastName || ''}`.trim();
      if (name) return name;
      if (member.isPrimary) return 'Primary Member';
      if (member.relationship) return `${member.relationship}`;
      return `Member ${index + 1}`;
    },

    getMemberExtraSpecialtyProviders(selection) {
      if (!selection || !this.allowsSpecialtyProviders) return 0;
      const index = this.memberSelections.indexOf(selection);
      if (index === -1) return 0;
      const breakdown = this.generateSpecialtyBreakdown();
      const entry = breakdown[index];
      return entry ? entry.extra : 0;
    },

    getBenefitById(benefitId) {
      return this.additionalBenefits.find(b => b._id === benefitId);
    },

    getBenefitNameById(benefitId) {
      const benefit = this.getBenefitById(benefitId);
      return benefit ? benefit.planName || benefit.displayName || benefit.name : 'Benefit';
    },

    getStandaloneBenefitCost(benefitId, familyCategory = 'single') {
      const benefit = this.getBenefitById(benefitId);
      if (!benefit) return 0;
      const code = (benefit.planCode || benefit.planName || '').toLowerCase();
      const pricing = this.standaloneBenefitPricing[code];
      if (pricing) {
        const key = familyCategory.toLowerCase();
        if (pricing[key] !== undefined) {
          return parseFloat(pricing[key]) || 0;
        }
      }
      const tiers = benefit.pricingTiers || [];
      const label = familyCategory === 'single' ? 'Individual' : familyCategory === 'couple' ? 'Couple' : 'Family';
      const tier = tiers.find(t => (t.label || '').toLowerCase().includes(label.toLowerCase()));
      if (tier && tier.monthlyFee !== undefined) {
        return parseFloat(tier.monthlyFee) || 0;
      }
      if (benefit.startingPrice !== undefined) {
        return parseFloat(benefit.startingPrice) || 0;
      }
      return 0;
    },

    // Get benefit price for display purposes
    // For bundle pricing: shows price based on how many members BEFORE current member have selected this benefit
    //   - 0 previous = Individual price
    //   - 1 previous = Couple price
    //   - 2+ previous = Family price
    // For regular pricing: shows per member price
    getDisplayBenefitPriceById(benefitId, memberIndex = null) {
      const benefit = this.getBenefitById(benefitId);
      if (!benefit) return 0;
      if (this.isBenefitIncluded(benefitId) || this.isCoreDentalSuppressed(benefitId)) return 0;
      
      if (this.isBenefitsOnlyPlan) {
        // For standalone plan, also use incremental pricing based on how many members have selected
        // In standalone, benefits are selected at household level, but we still need to show
        // the correct price based on previous selections
        const currentIndex = memberIndex !== null ? memberIndex : this.currentMemberSelectionIndex;
        
        // Count how many members before current have this benefit selected
        // IMPORTANT: Only count CONFIRMED selections for standalone plan too
        let previousSelectionsCount = 0;
        if (Array.isArray(this.memberSelections)) {
          for (let i = 0; i < currentIndex && i < this.memberSelections.length; i++) {
            const memberSelection = this.memberSelections[i];
            // Only count if the member has confirmed their selection
            if (memberSelection && memberSelection.confirmed && Array.isArray(memberSelection.selectedBenefits)) {
              if (memberSelection.selectedBenefits.includes(benefitId)) {
                previousSelectionsCount++;
              }
            }
          }
        }
        
        // Determine pricing tier: 0 = Individual, 1 = Couple, 2+ = Family
        let familyCategory = 'single';
        if (previousSelectionsCount === 1) {
          familyCategory = 'couple';
        } else if (previousSelectionsCount >= 2) {
          familyCategory = 'family';
        }
        
        return this.getStandaloneBenefitCost(benefitId, familyCategory);
      }

      // Use current member index if not provided
      const currentIndex = memberIndex !== null ? memberIndex : this.currentMemberSelectionIndex;

      // Check if this is PHC or DPO plan and if benefit has bundle pricing
      const selectedPlan = this.availablePlans.find(p => p._id === this.enrollment.planId);
      if (selectedPlan) {
        const isPHCPlan = (selectedPlan.planName || '').toLowerCase().includes('phc') || selectedPlan.planType === 'PHC';
        const isDPOPlan = (selectedPlan.planName || '').toLowerCase().includes('dpo') || selectedPlan.planType === 'DPO';
        
        // For bundle pricing, count how many members BEFORE current member have selected this benefit
        if ((isDPOPlan && benefit.dpoBundlePricing && benefit.dpoBundlePricing.enabled) ||
            (isPHCPlan && benefit.bundlePricing && benefit.bundlePricing.enabled)) {
          
          // Count members before current index who have CONFIRMED selecting THIS SPECIFIC benefit
          // IMPORTANT: 
          // 1. Only count CONFIRMED selections (not just selected)
          // 2. Only count members BEFORE current index (i < currentIndex, not <=)
          // 3. Count is based on how many times THIS SPECIFIC benefit was confirmed, not family size
          // 4. STRICT CHECK: confirmed must be explicitly true (not truthy, not undefined)
          let previousSelectionsCount = 0;
          if (Array.isArray(this.memberSelections)) {
            for (let i = 0; i < currentIndex && i < this.memberSelections.length; i++) {
              const memberSelection = this.memberSelections[i];
              
              // Check if this member has this SPECIFIC benefit selected
              const hasThisBenefit = memberSelection && 
                                     Array.isArray(memberSelection.selectedBenefits) &&
                                     memberSelection.selectedBenefits.includes(benefitId);
              
              // Check if this member has CONFIRMED their selection
              // IMPORTANT: We need to check if the member confirmed, but the confirmation
              // should reset when benefits change. So we check confirmed === true explicitly.
              const isConfirmed = memberSelection && 
                                  memberSelection.confirmed === true && 
                                  typeof memberSelection.confirmed === 'boolean';
              
              // Only count if member has this benefit AND has confirmed
              if (hasThisBenefit && isConfirmed) {
                previousSelectionsCount++;
              }
            }
          }
          
          // Determine pricing tier based on previous CONFIRMED selections
          // The price shown should reflect how many members BEFORE current have confirmed
          // This is the price that will be charged if current member selects the benefit
          // 0 previous = Individual, 1 previous = Couple, 2+ previous = Family
          let familySizeCategory = 'Individual';
          if (previousSelectionsCount === 1) {
            familySizeCategory = 'Couple';
          } else if (previousSelectionsCount >= 2) {
            familySizeCategory = 'Family';
          }
          
          
          // Get the appropriate bundle pricing tier
          let bundleTier = null;
          
          // Check if this is a Dental plan with tier-based pricing
          const isDentalPlan = (benefit.planCode && benefit.planCode.startsWith('DENTAL-')) || benefit.planType === 'Dental';
          
          // Try to get dentalTier from enrollment, or extract from planCode/name
          let dentalTier = null;
          if (isDentalPlan) {
            dentalTier = this.enrollment.dentalTier || this.enrollment.customFields?.dentalTier;
            
            // If not found, try to extract from planCode first (most reliable)
            if (!dentalTier && benefit.planCode) {
              const planCode = benefit.planCode.toUpperCase();
              if (planCode.includes('ELITE')) {
                dentalTier = 'Elite Smile';
              } else if (planCode.includes('ESSENTIAL')) {
                dentalTier = 'Essential Smile';
              } else if (planCode.includes('CORE')) {
                dentalTier = 'Core Care';
              }
            }
            
            // If still not found, try to extract from benefit name
            if (!dentalTier && benefit.planName) {
              const planName = benefit.planName.toLowerCase();
              if (planName.includes('elite smile')) {
                dentalTier = 'Elite Smile';
              } else if (planName.includes('essential smile')) {
                dentalTier = 'Essential Smile';
              } else if (planName.includes('core care')) {
                dentalTier = 'Core Care';
              }
            }
            
            // Also check displayName
            if (!dentalTier && benefit.displayName) {
              const displayName = benefit.displayName.toLowerCase();
              if (displayName.includes('elite smile')) {
                dentalTier = 'Elite Smile';
              } else if (displayName.includes('essential smile')) {
                dentalTier = 'Essential Smile';
              } else if (displayName.includes('core care')) {
                dentalTier = 'Core Care';
              }
            }
            
          }
          
          if (isDPOPlan && benefit.dpoBundlePricing && benefit.dpoBundlePricing.enabled) {
            // For Dental plans, use tier-based pricing structure
            if (isDentalPlan && dentalTier && benefit.dpoBundlePricing.tiers && benefit.dpoBundlePricing.tiers[dentalTier]) {
              const tierPricing = benefit.dpoBundlePricing.tiers[dentalTier];
              bundleTier = tierPricing.pricingTiers && tierPricing.pricingTiers.find(
                t => t.label === familySizeCategory
              );
            } else if (benefit.dpoBundlePricing.pricingTiers) {
              // For non-Dental plans or if tiers structure doesn't exist, use regular pricingTiers
              bundleTier = benefit.dpoBundlePricing.pricingTiers.find(
                t => t.label === familySizeCategory
              );
            }
          } else if (isPHCPlan && benefit.bundlePricing && benefit.bundlePricing.enabled) {
            // For Dental plans, use tier-based pricing structure
            if (isDentalPlan && dentalTier && benefit.bundlePricing.tiers && benefit.bundlePricing.tiers[dentalTier]) {
              const tierPricing = benefit.bundlePricing.tiers[dentalTier];
              bundleTier = tierPricing.pricingTiers && tierPricing.pricingTiers.find(
                t => t.label === familySizeCategory
              );
            } else if (benefit.bundlePricing.pricingTiers) {
              // For non-Dental plans or if tiers structure doesn't exist, use regular pricingTiers
              bundleTier = benefit.bundlePricing.pricingTiers.find(
                t => t.label === familySizeCategory
              );
            }
          }
          
          if (bundleTier && bundleTier.monthlyFee !== undefined) {
            // For display, show the family price (not divided by members)
            // DPO: Individual $6.00, Couple $9.00, Family $13.00
            // PHC: Individual $4.20, Couple $8.40, Family $12.60
            return parseFloat(bundleTier.monthlyFee);
          }
        }
      }

      // Fallback to regular pricing (per member)
      const tiers = benefit.pricingTiers || [];
      const singleTier = tiers.find(t => {
        const label = (t.label || '').toLowerCase();
        return label === 'single' || label === 'individual' || label === 'member' || label === 'solo';
      });
      if (singleTier && singleTier.monthlyFee !== undefined) {
        return parseFloat(singleTier.monthlyFee) || 0;
      }
      if (benefit.startingPrice !== undefined) {
        return parseFloat(benefit.startingPrice) || 0;
      }
      return 0;
    },
    
    // Check if benefit uses bundle pricing (for display text)
    isBundlePricingBenefit(benefitId) {
      const benefit = this.getBenefitById(benefitId);
      if (!benefit) return false;
      if (this.isBenefitsOnlyPlan) return false;
      
      const selectedPlan = this.availablePlans.find(p => p._id === this.enrollment.planId);
      if (!selectedPlan) return false;
      
      const isPHCPlan = (selectedPlan.planName || '').toLowerCase().includes('phc') || selectedPlan.planType === 'PHC';
      const isDPOPlan = (selectedPlan.planName || '').toLowerCase().includes('dpo') || selectedPlan.planType === 'DPO';
      
      return (isDPOPlan && benefit.dpoBundlePricing && benefit.dpoBundlePricing.enabled) ||
             (isPHCPlan && benefit.bundlePricing && benefit.bundlePricing.enabled);
    },
    
    getPerMemberBenefitPriceById(benefitId) {
      const benefit = this.getBenefitById(benefitId);
      if (!benefit) return 0;
      if (this.isBenefitIncluded(benefitId) || this.isCoreDentalSuppressed(benefitId)) return 0;
      if (this.isBenefitsOnlyPlan) {
        const numberOfMembers = this.householdMemberCount;
        const familyCategory = numberOfMembers === 1 ? 'single' : numberOfMembers === 2 ? 'couple' : 'family';
        return this.getStandaloneBenefitCost(benefitId, familyCategory);
      }

      // Check if this is PHC or DPO plan and if benefit has bundle pricing
      const selectedPlan = this.availablePlans.find(p => p._id === this.enrollment.planId);
      if (selectedPlan) {
        const isPHCPlan = (selectedPlan.planName || '').toLowerCase().includes('phc') || selectedPlan.planType === 'PHC';
        const isDPOPlan = (selectedPlan.planName || '').toLowerCase().includes('dpo') || selectedPlan.planType === 'DPO';
        
        // For bundle pricing, return 0 here - it will be calculated at family level in calculateBundlePrice
        if (isDPOPlan && benefit.dpoBundlePricing && benefit.dpoBundlePricing.enabled) {
          return 0; // Bundle pricing is calculated at family level
        }
        if (isPHCPlan && benefit.bundlePricing && benefit.bundlePricing.enabled) {
          return 0; // Bundle pricing is calculated at family level
        }
      }

      // Fallback to regular pricing (per member)
      const tiers = benefit.pricingTiers || [];
      const singleTier = tiers.find(t => {
        const label = (t.label || '').toLowerCase();
        return label === 'single' || label === 'individual' || label === 'member' || label === 'solo';
      });
      if (singleTier && singleTier.monthlyFee !== undefined) {
        return parseFloat(singleTier.monthlyFee) || 0;
      }
      if (benefit.startingPrice !== undefined) {
        return parseFloat(benefit.startingPrice) || 0;
      }
      return 0;
    },
    
    // Get benefit price at family level (for bundle pricing)
    getSelectedBundleBenefits() {
      // Get all unique benefit IDs that use bundle pricing and have been selected
      const selectedBundleBenefits = new Set();
      
      if (!Array.isArray(this.memberSelections)) {
        return [];
      }
      
      const selectedPlan = this.availablePlans.find(p => p._id === this.enrollment.planId);
      if (!selectedPlan) {
        return [];
      }
      
      const isPHCPlan = (selectedPlan.planName || '').toLowerCase().includes('phc') || selectedPlan.planType === 'PHC';
      const isDPOPlan = (selectedPlan.planName || '').toLowerCase().includes('dpo') || selectedPlan.planType === 'DPO';
      
      // Collect all selected benefit IDs
      this.memberSelections.forEach(memberSelection => {
        if (memberSelection && Array.isArray(memberSelection.selectedBenefits)) {
          memberSelection.selectedBenefits.forEach(benefitId => {
            if (benefitId) {
              const benefit = this.getBenefitById(benefitId);
              if (benefit) {
                // Check if this benefit uses bundle pricing
                const usesBundlePricing = (isDPOPlan && benefit.dpoBundlePricing && benefit.dpoBundlePricing.enabled) ||
                                         (isPHCPlan && benefit.bundlePricing && benefit.bundlePricing.enabled);
                
                if (usesBundlePricing) {
                  selectedBundleBenefits.add(benefitId);
                }
              }
            }
          });
        }
      });
      
      return Array.from(selectedBundleBenefits);
    },

    getFamilyBenefitPriceById(benefitId) {
      const benefit = this.getBenefitById(benefitId);
      if (!benefit) return 0;
      if (this.isBenefitIncluded(benefitId) || this.isCoreDentalSuppressed(benefitId)) return 0;
      
      const selectedPlan = this.availablePlans.find(p => p._id === this.enrollment.planId);
      if (!selectedPlan) return 0;
      
      const isPHCPlan = (selectedPlan.planName || '').toLowerCase().includes('phc') || selectedPlan.planType === 'PHC';
      const isDPOPlan = (selectedPlan.planName || '').toLowerCase().includes('dpo') || selectedPlan.planType === 'DPO';
      
      // For bundle pricing, calculate based on how many members have SELECTED this benefit
      // IMPORTANT: Count members who have SELECTED the benefit (not just confirmed)
      // This is used for the total calculation, so it should reflect selected benefits immediately
      if ((isDPOPlan && benefit.dpoBundlePricing && benefit.dpoBundlePricing.enabled) ||
          (isPHCPlan && benefit.bundlePricing && benefit.bundlePricing.enabled)) {
        
        // Count how many members have SELECTED this benefit (not just confirmed)
        // This allows the price to update immediately when a benefit is selected
        let selectedCount = 0;
        if (Array.isArray(this.memberSelections)) {
          this.memberSelections.forEach(memberSelection => {
            if (memberSelection && 
                Array.isArray(memberSelection.selectedBenefits) &&
                memberSelection.selectedBenefits.includes(benefitId)) {
              selectedCount++;
            }
          });
        }
        
        // Determine pricing tier based on selected benefits
        // IMPORTANT: For bundle pricing, the total price is based on how many members have selected
        // The pricing logic is:
        // - 0 selected = $0 (no cost yet)
        // - 1 selected = Individual price ($4.20 for PHC, $6.00 for DPO) - first member selected
        // - 2 selected = Couple price ($8.40 for PHC, $9.00 for DPO) - second member selected, upgrades to couple
        // - 3+ selected = Family price ($12.60 for PHC, $13.00 for DPO) - third+ member selected, upgrades to family
        if (selectedCount === 0) {
          return 0; // No selections, so no cost yet
        }
        
        let familySizeCategory = 'Individual';
        if (selectedCount === 1) {
          familySizeCategory = 'Individual'; // First member selected, pays individual price
        } else if (selectedCount === 2) {
          familySizeCategory = 'Couple'; // Second member selected, upgrades to couple price
        } else if (selectedCount >= 3) {
          familySizeCategory = 'Family'; // Third+ member selected, upgrades to family price
        }
        
        // Check if this is a Dental plan with tier-based pricing
        const isDentalPlan = (benefit.planCode && benefit.planCode.startsWith('DENTAL-')) || benefit.planType === 'Dental';
        
        // Try to get dentalTier from enrollment, or extract from planCode/name
        let dentalTier = null;
        if (isDentalPlan) {
          dentalTier = this.enrollment.dentalTier || this.enrollment.customFields?.dentalTier;
          
          // If not found, try to extract from planCode first (most reliable)
          if (!dentalTier && benefit.planCode) {
            const planCode = benefit.planCode.toUpperCase();
            if (planCode.includes('ELITE')) {
              dentalTier = 'Elite Smile';
            } else if (planCode.includes('ESSENTIAL')) {
              dentalTier = 'Essential Smile';
            } else if (planCode.includes('CORE')) {
              dentalTier = 'Core Care';
            }
          }
          
          // If still not found, try to extract from benefit name
          if (!dentalTier && benefit.planName) {
            const planName = benefit.planName.toLowerCase();
            if (planName.includes('elite smile')) {
              dentalTier = 'Elite Smile';
            } else if (planName.includes('essential smile')) {
              dentalTier = 'Essential Smile';
            } else if (planName.includes('core care')) {
              dentalTier = 'Core Care';
            }
          }
          
          // Also check displayName
          if (!dentalTier && benefit.displayName) {
            const displayName = benefit.displayName.toLowerCase();
            if (displayName.includes('elite smile')) {
              dentalTier = 'Elite Smile';
            } else if (displayName.includes('essential smile')) {
              dentalTier = 'Essential Smile';
            } else if (displayName.includes('core care')) {
              dentalTier = 'Core Care';
            }
          }
        }
        
        // Check for DPO bundle pricing
        if (isDPOPlan && benefit.dpoBundlePricing && benefit.dpoBundlePricing.enabled) {
          let bundleTier = null;
          
          // For Dental plans, use tier-based pricing structure
          if (isDentalPlan && dentalTier && benefit.dpoBundlePricing.tiers && benefit.dpoBundlePricing.tiers[dentalTier]) {
            const tierPricing = benefit.dpoBundlePricing.tiers[dentalTier];
            bundleTier = tierPricing.pricingTiers && tierPricing.pricingTiers.find(
              t => t.label === familySizeCategory
            );
          } else if (benefit.dpoBundlePricing.pricingTiers) {
            // For non-Dental plans, use regular pricingTiers
            bundleTier = benefit.dpoBundlePricing.pricingTiers.find(
              t => t.label === familySizeCategory
            );
          }
          
          if (bundleTier && bundleTier.monthlyFee !== undefined) {
            return parseFloat(bundleTier.monthlyFee) || 0;
          } else {
            console.warn(`⚠️ getFamilyBenefitPriceById (DPO): Tier not found for category="${familySizeCategory}", dentalTier="${dentalTier}", available tiers:`, benefit.dpoBundlePricing.pricingTiers || benefit.dpoBundlePricing.tiers);
          }
        }
        
        // Check for PHC bundle pricing
        if (isPHCPlan && benefit.bundlePricing && benefit.bundlePricing.enabled) {
          let bundleTier = null;
          
          // For Dental plans, use tier-based pricing structure
          if (isDentalPlan && dentalTier && benefit.bundlePricing.tiers && benefit.bundlePricing.tiers[dentalTier]) {
            const tierPricing = benefit.bundlePricing.tiers[dentalTier];
            bundleTier = tierPricing.pricingTiers && tierPricing.pricingTiers.find(
              t => t.label === familySizeCategory
            );
          } else if (benefit.bundlePricing.pricingTiers) {
            // For non-Dental plans, use regular pricingTiers
            bundleTier = benefit.bundlePricing.pricingTiers.find(
              t => t.label === familySizeCategory
            );
          }
          
          if (bundleTier && bundleTier.monthlyFee !== undefined) {
            return parseFloat(bundleTier.monthlyFee) || 0;
          } else {
            console.warn(`⚠️ getFamilyBenefitPriceById (PHC): Tier not found for category="${familySizeCategory}", dentalTier="${dentalTier}", isDentalPlan=${isDentalPlan}, available tiers:`, benefit.bundlePricing.pricingTiers || benefit.bundlePricing.tiers);
          }
        }
        
        console.warn(`⚠️ getFamilyBenefitPriceById: No bundle pricing tier found for benefit ${benefitId}, selectedCount=${selectedCount}, category=${familySizeCategory}`);
      }
      
      return 0;
    },

    getMemberBenefitCost(selection) {
      if (!selection) return 0;
      if (this.isBenefitsOnlyPlan && this.standaloneCostAllocation) {
        const index = this.memberSelections.indexOf(selection);
        if (index > -1) {
          return this.standaloneCostAllocation.perMemberTotals[index] || 0;
        }
        return 0;
      }
      return (selection.selectedBenefits || []).reduce((sum, id) => sum + this.getPerMemberBenefitPriceById(id), 0);
    },

    canToggleBenefit(benefitId) {
      if (this.isBenefitsOnlyPlan) return true;
      if (this.isBenefitIncluded(benefitId)) return false;
      if (this.isCoreDentalSuppressed(benefitId)) return false;
      if (this.isCoreDentalBenefit(benefitId)) return false;
      return true;
    },

    getMemberSpecialtyCost(selection) {
      if (!selection || !this.allowsSpecialtyProviders) return 0;
      const index = this.memberSelections.indexOf(selection);
      if (index === -1) return 0;
      const breakdown = this.generateSpecialtyBreakdown();
      const entry = breakdown[index];
      return entry ? entry.cost : 0;
    },

    getMemberTotalExtras(selection) {
      return this.getMemberBenefitCost(selection) + this.getMemberSpecialtyCost(selection);
    },

    goToCustomization() {
      if (!this.enrollment.planId && !this.isBenefitsOnlyPlan) return;
      
      // Clear any pending price recalculations to avoid loops when returning to screen 12
      if (this.priceRecalcTimeoutId) {
        clearTimeout(this.priceRecalcTimeoutId);
        this.priceRecalcTimeoutId = null;
      }
      this.pendingPriceRecalc = false;
      
      if (this.allowsSpecialtyProviders) {
        this.currentQuestion = 12;
        if (this.memberSelections && this.memberSelections.length > 0) {
          const nextUnconfirmed = this.memberSelections.findIndex(sel => !sel.confirmed);
          this.currentMemberSelectionIndex = nextUnconfirmed !== -1 ? nextUnconfirmed : 0;
        } else {
          this.currentMemberSelectionIndex = 0;
        }
      } else {
        this.currentQuestion = 12;
      }
      this.scrollToTop();
    },

    scrollToTop() {
      if (typeof window === 'undefined') return;
      requestAnimationFrame(() => {
        if (window.scrollTo) {
          try {
            window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
          } catch (err) {
            window.scrollTo(0, 0);
          }
        }
        const container = this.$refs.questionContainer;
        if (container) {
          if (container.scrollTo) {
            try {
              container.scrollTo({ top: 0, behavior: 'smooth' });
            } catch (err) {
              container.scrollTop = 0;
            }
          } else {
            container.scrollTop = 0;
          }
        }
      });
    },

    scrollToBottom() {
      if (typeof window === 'undefined') return;
      requestAnimationFrame(() => {
        const target = document.body.scrollHeight || 0;
        if (window.scrollTo) {
          try {
            window.scrollTo({ top: target, left: 0, behavior: 'smooth' });
          } catch (err) {
            window.scrollTo(0, target);
          }
        }
        const container = this.$refs.questionContainer;
        if (container) {
          if (container.scrollTo) {
            try {
              container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
            } catch (err) {
              container.scrollTop = container.scrollHeight;
            }
          } else {
            container.scrollTop = container.scrollHeight;
          }
        }
      });
    },
    
    isPlanSelected(plan) {
      if (!plan) return false;
      return this.selectedPlanKey === plan._id;
    },
    
    async selectAddressFromAutocomplete(selectedItem) {
      if (!selectedItem) return;
      
      const placeId = selectedItem.place_id;
      const featureData = selectedItem.feature_data;
      
      try {
        this.searchingAddress = true;
        
        // Use Mapbox API to get address details
        const response = await axios.get('/api/geocoding/place-details', {
          params: {
            placeId: placeId,
            featureData: featureData
          }
        });
        
        console.log('🔍 Raw response from Mapbox:', response.data);
        
        const addressComponents = response.data.address_components;
        
        console.log('📦 Address components:', addressComponents);
        
        // Parse address components
        let street = '';
        let city = '';
        let state = '';
        let zipCode = '';
        
        addressComponents.forEach(component => {
          const types = component.types;
          
          console.log('🏷️ Component:', component.long_name, 'Types:', types);
          
          if (types.includes('street_number')) {
            street = component.long_name;
          }
          if (types.includes('route')) {
            street += (street ? ' ' : '') + component.long_name;
          }
          if (types.includes('locality')) {
            city = component.long_name;
          }
          if (types.includes('administrative_area_level_1')) {
            state = component.short_name;
          }
          if (types.includes('postal_code')) {
            zipCode = component.long_name;
          }
        });
        
        console.log('🧩 Parsed values:', { street, city, state, zipCode });
        
        // Fill the form
        this.enrollment.streetAddress = street.trim();
        this.enrollment.city = city;
        this.enrollment.state = state;
        this.enrollment.zipCode = zipCode;
        this.addressAutoFilled = true;
        
        // Debug log
        console.log('📍 Address filled:', {
          street: this.enrollment.streetAddress,
          city: this.enrollment.city,
          state: this.enrollment.state,
          zip: this.enrollment.zipCode,
          canProceed: this.canProceed
        });
        
        // Force Vue to re-evaluate computed properties
        this.$nextTick(() => {
          this.$forceUpdate();
        });
        
      } catch (error) {
        console.error('❌ Error getting place details:', error);
        console.error('Error response:', error.response?.data);
        this.$toast?.error('Failed to load address details') || console.error('Failed to load address details');
      } finally {
        this.searchingAddress = false;
      }
    },
    
    clearAutoFill() {
      this.addressAutoFilled = false;
      this.selectedAddress = null;
      this.addressSearchText = '';
    },
    
    async searchAddresses(query) {
      if (!query || query.length < 3) return;
      
      try {
        this.searchingAddress = true;
        
        // Call Google Places Autocomplete API through our backend
        const response = await axios.get('/api/geocoding/autocomplete', {
          params: {
            input: query,
            components: 'country:us' // Restrict to US addresses
          }
        });
        
        this.addressSuggestions = response.data.predictions || [];
        
      } catch (error) {
        console.error('Error searching addresses:', error);
        this.addressSuggestions = [];
      } finally {
        this.searchingAddress = false;
      }
    },
    
    nextQuestion() {
      if (!this.canProceed) {
        console.warn('⚠️ Cannot proceed - validation failed');
        console.log('   Current question:', this.currentQuestion);
        console.log('   canProceed:', this.canProceed);
        return;
      }
      
      const previousQuestion = this.currentQuestion;
      
        if (this.currentQuestion === 9 && this.hasDependents === false) {
        // Skip dependent details if user indicated no dependents
          this.currentQuestion += 2;
      } else if (this.currentQuestion === 11) {
        // Move into member customization
        this.currentMemberSelectionIndex = 0;
        this.currentQuestion = 12;
      } else if (this.currentQuestion === 12) {
        this.autoSetFamilySize();
        this.currentMemberSelectionIndex = 0;
        this.currentQuestion = 14;
          } else {
        this.currentQuestion++;
        // Skip legacy question 13 (family size) if encountered
        if (this.currentQuestion === 13) {
          this.currentQuestion++;
        }
          }
          
      // Update browser history when moving forward
      this.updateBrowserHistory(previousQuestion, this.currentQuestion);
          
      this.$nextTick(() => {
          if (this.currentQuestion === 15) {
            // Update PHC plan type before showing review screen
            if (this.enrollment.planName && this.enrollment.planName.toLowerCase().includes('phc')) {
              this.updatePHCPlanTypeBasedOnSpecialists();
            }
            setTimeout(() => {
              this.initSignatureCanvas();
            }, 100);
          }
          if (this.currentQuestion === 3) {
            setTimeout(() => {
              this.forceCalendarColors();
            }, 100);
          }
        });
    },
    
    goBack() {
      if (this.currentQuestion > 0) {
        const previousQuestion = this.currentQuestion;
        
        // Skip dependent entry if going back and no dependents
        if (this.currentQuestion === 11 && this.hasDependents === false) {
          this.currentQuestion -= 2;
        } 
        else if (this.currentQuestion === 12) {
          if (this.currentMemberSelectionIndex > 0) {
            this.currentMemberSelectionIndex -= 1;
            return;
          }
          this.currentQuestion = 11;
        }
        else if (this.currentQuestion === 15) {
          this.currentQuestion = 14;
        }
        else if (this.currentQuestion === 14) {
          this.currentQuestion = 12;
          this.currentMemberSelectionIndex = Math.max(this.memberCount - 1, 0);
        }
        else {
          this.currentQuestion--;
          if (this.currentQuestion === 13) {
          this.currentQuestion--;
          }
        }
        
        // Update browser history when going back
        this.updateBrowserHistory(previousQuestion, this.currentQuestion);
        
        // If going back to Q11 and a plan is already selected, reload disclosure
        if (this.currentQuestion === 11 && (this.enrollment.planId || this.isBenefitsOnlyPlan)) {
          const selectedPlan = this.isBenefitsOnlyPlan
            ? this.benefitsOnlyPlanOption
            : this.mainPlans.find(p => p._id === this.enrollment.planId);
          if (selectedPlan) {
            // Reload disclosure for the selected plan
            if (selectedPlan.disclosures && Array.isArray(selectedPlan.disclosures) && selectedPlan.disclosures.length > 0) {
              this.selectedPlanDisclosure = selectedPlan.disclosures[0];
            } else if (selectedPlan.disclosure) {
              this.selectedPlanDisclosure = selectedPlan.disclosure;
            } else {
              // Default disclosure
              this.selectedPlanDisclosure = '<strong>THIS IS NOT INSURANCE.</strong> This is a discount medical plan organization. This plan does NOT meet the minimum creditable coverage requirements under the Affordable Care Act (ACA). The plan provides discounts at certain healthcare providers for medical services. The plan does not make payments directly to the providers of medical services. The plan member is obligated to pay for all healthcare services but will receive a discount from those healthcare providers who have contracted with the discount plan organization.';
            }
            this.disclosureAccepted = false; // Reset acceptance when going back
          }
        }
      }
    },
    
    initBrowserHistory() {
      // Add initial state to history
      const state = { question: this.currentQuestion };
      const url = `${window.location.pathname}?step=${this.currentQuestion}`;
      window.history.replaceState(state, '', url);
      
      // Listen for browser back/forward buttons
      window.addEventListener('popstate', this.handlePopState);
    },
    
    handlePopState(event) {
      // When user clicks browser back button, navigate within the enrollment
      if (event.state && typeof event.state.question === 'number') {
        const targetQuestion = event.state.question;
        const previousQuestion = this.currentQuestion;
        
        // If user is at question 0 and trying to go back, allow navigation away
        if (previousQuestion === 0 && targetQuestion < 0) {
          return; // Allow browser to navigate away
        }
        
        // Only update if we're still within enrollment bounds
        if (targetQuestion >= 0 && targetQuestion <= 15) {
          // Temporarily disable history updates to avoid loops
          this.isHandlingPopState = true;
          
          // Update current question
          this.currentQuestion = targetQuestion;
          
          // Handle special cases for going back
          if (targetQuestion < previousQuestion) {
            // Handle special navigation cases
            if (this.currentQuestion === 11 && this.hasDependents === false) {
              this.currentQuestion = 9;
            } else if (this.currentQuestion === 13) {
              this.currentQuestion = 12;
            }
            
            // Reload disclosure if going back to Q11
            if (this.currentQuestion === 11 && (this.enrollment.planId || this.isBenefitsOnlyPlan)) {
              const selectedPlan = this.isBenefitsOnlyPlan
                ? this.benefitsOnlyPlanOption
                : this.mainPlans.find(p => p._id === this.enrollment.planId);
              if (selectedPlan) {
                if (selectedPlan.disclosures && Array.isArray(selectedPlan.disclosures) && selectedPlan.disclosures.length > 0) {
                  this.selectedPlanDisclosure = selectedPlan.disclosures[0];
                } else if (selectedPlan.disclosure) {
                  this.selectedPlanDisclosure = selectedPlan.disclosure;
                }
                this.disclosureAccepted = false;
              }
            }
          }
          
          this.isHandlingPopState = false;
        }
      } else {
        // No state - might be navigating away from enrollment
        // If we're at question 0, allow navigation away
        if (this.currentQuestion === 0) {
          return; // Allow browser to navigate away
        }
        
        // If we're past question 0, keep user in enrollment
        if (this.currentQuestion > 0) {
          // Restore enrollment state
          const state = { question: this.currentQuestion };
          const url = `${window.location.pathname}?step=${this.currentQuestion}`;
          window.history.pushState(state, '', url);
        }
      }
    },
    
    updateBrowserHistory(fromQuestion, toQuestion) {
      // Skip history update if we're handling a popstate event (to avoid loops)
      if (this.isHandlingPopState) {
        return;
      }
      
      // Update URL and history state without triggering navigation
      const state = { question: toQuestion };
      const url = `${window.location.pathname}?step=${toQuestion}`;
      window.history.pushState(state, '', url);
    },
    
    async handleNext() {
      console.log('🔵 handleNext called at question:', this.currentQuestion);
      
      if (this.currentQuestion === 15) {
        // Q15 is Review & Sign - submit enrollment and go to payment
        console.log('📝 Q15: Calling submitEnrollment...');
        await this.submitEnrollment();
      } else {
        // All other questions - just go to next
        this.nextQuestion();
      }
    },
    
    
    selectMaritalStatus(status) {
      this.enrollment.maritalStatus = status;
      setTimeout(() => this.nextQuestion(), 300);
    },
    
    setHasDependents(value) {
      this.hasDependents = value;
      if (value) {
        this.enrollment.dependents = [];
        this.addDependent();
      }
      setTimeout(() => this.nextQuestion(), 300);
    },

    autoSetFamilySize() {
      const numDependents = Array.isArray(this.enrollment.dependents) ? this.enrollment.dependents.length : 0;
      if (numDependents === 0) {
        this.enrollment.familySize = 'Single';
      } else if (numDependents === 1) {
        this.enrollment.familySize = 'Couple';
      } else {
        this.enrollment.familySize = 'Family';
      }
    },
    
    addDependent() {
      if (this.enrollment.dependents.length < this.selectedPlanMaxDependents) {
        this.enrollment.dependents.push({
          firstName: '',
          lastName: '',
          dateOfBirth: null,
          relationship: 'Child',
          gender: 'M',
          datePickerMenu: false
        });
        
        // Recalculate price if plan is already selected
        if (this.enrollment.planId || this.isBenefitsOnlyPlan) {
          this.$nextTick(() => {
            this.scheduleBundleRecalculation();
          });
        }
      }
    },
    
    removeDependent(index) {
      this.enrollment.dependents.splice(index, 1);
      
      // Recalculate price if plan is already selected
      if (this.enrollment.planId || this.isBenefitsOnlyPlan) {
        this.$nextTick(() => {
          this.scheduleBundleRecalculation();
        });
      }
    },
    
    onDependentDateChange(date) {
      console.log('📅 Dependent date of birth changed:', date);
      
      // Close the date picker menu for the dependent that was changed
      this.$nextTick(() => {
        const dependent = this.enrollment.dependents.find(dep => dep.dateOfBirth === date);
        if (dependent) {
          dependent.datePickerMenu = false;
        }
      });
      
      // Recalculate price if plan is already selected
      if (this.enrollment.planId || this.isBenefitsOnlyPlan) {
        console.log('💰 Recalculating price after dependent DOB change...');
        // Use setTimeout to ensure Vue has updated the model
        setTimeout(() => {
          this.scheduleBundleRecalculation();
        }, 100);
      }
    },
    
    selectPlan(plan) {
      console.log('📋 Selecting plan:', {
        _id: plan._id,
        planName: plan.planName,
        displayName: plan.displayName || plan.name,
        planType: plan.planType
      });
      this.selectedPlanKey = plan._id;
      this.standaloneCostAllocation = null;
      
      if (plan._id === 'benefits-only' || plan.planType === 'BenefitsOnly' || 
          (plan.planName && plan.planName.toLowerCase() === 'benefits only')) {
        this.isBenefitsOnlyPlan = true;
        // Benefits Only is a valid plan, just with $0 cost - use its _id
        this.enrollment.planId = plan._id;
        this.enrollment.planType = 'BenefitsOnly';
        this.enrollment.planName = plan.planName || plan.name;
        this.enrollment.maxDependents = 5;
        this.enrollment.payment.monthlyFee = 0;
        this.enrollment.payment.applicationFee = 25;
        this.enrollment.payment.processingFee = 0;
        this.enrollment.selectedSpecialtyProviders = 0;
        this.selectedAdditionalBenefits = [];
        // Clear included benefits - Benefits Only has NO included benefits
        this.includedBenefitIds = [];
        this.syncMemberSelections(true);
        this.updateIncludedBenefitsForPlan(null);
        this.disclosureAccepted = false;
        this.basePlanMonthlyFee = 0;
        this.basePlanProcessingFee = 0;
        
        console.log('✅ Benefits Only plan selected - no included benefits will be added');
        
        if (plan.disclosure) {
          this.selectedPlanDisclosure = plan.disclosure;
        } else {
          this.selectedPlanDisclosure = '<strong>Important:</strong> By selecting the Benefits Only option, you understand that no medical plan is being purchased. Only the stand-alone benefit plans you select will be included. Please review each benefit\'s terms carefully.';
        }
        
        this.$nextTick(() => {
          this.scheduleBundleRecalculation(true);
        });
        return;
      }
      
      this.isBenefitsOnlyPlan = false;
      this.enrollment.planId = plan._id;
      this.enrollment.planType = plan.planType;
      this.enrollment.maxDependents = 5;
      
      // For PHC plans, start with "PHC Plan Basic" (will be updated based on specialists)
      // For Discount plans, keep the original name
      const planNameLower = (plan.planName || '').toLowerCase();
      if (planNameLower.includes('phc')) {
        try {
          // Always start with "PHC Plan Basic" - type will be updated when specialists are selected
          this.enrollment.planName = 'PHC Plan Basic';
          
          // Find PHC Basic plan - we'll use it for pricing calculation
          const basicPlan = this.availablePlans.find(p => {
            if (!p) return false;
            const pName = (p.planName || '').toLowerCase();
            const pDisplay = (p.displayName || p.name || '').toLowerCase();
            const isPHC = pName.includes('phc') || pDisplay.includes('phc') || p.planType === 'PHC';
            const isBasic = pName.includes('basic') || pDisplay.includes('basic');
            const isNotDual = !pName.includes('dual') && !pDisplay.includes('dual');
            return isPHC && isBasic && isNotDual;
          });
          
          // Set initial price - will be recalculated based on age in calculateBundlePrice
          if (basicPlan) {
            // Use starting price as initial value, will be recalculated based on age
            this.basePlanMonthlyFee = parseFloat(basicPlan.startingPrice || 0) || 0;
            this.enrollment.payment.monthlyFee = basicPlan.startingPrice || 0;
          } else {
            // Fallback to plan's starting price
            console.warn('⚠️ PHC Basic plan not found, using selected plan price');
            this.basePlanMonthlyFee = parseFloat(plan.startingPrice || 0) || 0;
            this.enrollment.payment.monthlyFee = plan.startingPrice || 0;
          }
        } catch (error) {
          console.error('❌ Error setting up PHC plan:', error);
          // Fallback to plan's starting price
          this.basePlanMonthlyFee = parseFloat(plan.startingPrice || 0) || 0;
          this.enrollment.payment.monthlyFee = plan.startingPrice || 0;
        }
      } else {
        this.enrollment.planName = plan.planName;
        this.enrollment.payment.monthlyFee = plan.startingPrice;
        this.basePlanMonthlyFee = parseFloat(plan.startingPrice || 0) || 0;
      }
      
      this.enrollment.payment.applicationFee = plan.applicationFee;
      this.enrollment.payment.processingFee = 0;
      this.basePlanProcessingFee = 0;
      console.log('✅ Enrollment updated:', {
        planId: this.enrollment.planId,
        planName: this.enrollment.planName,
        allowsSpecialtyProviders: this.allowsSpecialtyProviders
      });
      
      // Reset specialists when selecting a new plan
      // Discount Plan doesn't allow specialists, PHC starts with 0 (Basic)
      this.enrollment.selectedSpecialtyProviders = 0;
      this.selectedAdditionalBenefits = [];
      this.syncMemberSelections(true);
      this.updateIncludedBenefitsForPlan(plan);
      this.disclosureAccepted = false;
      
      // For PHC plans, don't call updatePHCPlanTypeBasedOnSpecialists here
      // It will be called automatically when specialists are selected
      // This prevents infinite loops during plan selection
      
      console.log('🔍 Getting disclosure for plan:', plan.planName || plan.name);
      if (plan.disclosures && Array.isArray(plan.disclosures) && plan.disclosures.length > 0) {
        this.selectedPlanDisclosure = plan.disclosures[0];
      } else if (plan.disclosure) {
        this.selectedPlanDisclosure = plan.disclosure;
      } else {
        this.selectedPlanDisclosure = '<strong>THIS IS NOT INSURANCE.</strong> This is a discount medical plan organization. This plan does NOT meet the minimum creditable coverage requirements under the Affordable Care Act (ACA). The plan provides discounts at certain healthcare providers for medical services. The plan does not make payments directly to the providers of medical services. The plan member is obligated to pay for all healthcare services but will receive a discount from those healthcare providers who have contracted with the discount plan organization.';
      }
      
      // Schedule price calculation after plan selection (with delay to avoid loops)
      this.$nextTick(() => {
        setTimeout(() => {
          if (!this.isCalculatingPrice) {
            this.scheduleBundleRecalculation(true);
          }
        }, 200);
      });
    },
    
    acceptDisclosure() {
      if (!this.selectedPlanKey) {
        console.warn('⚠️ Cannot accept disclosure - no plan selected');
        return;
      }
      
      this.disclosureAccepted = true;
      console.log('✅ Disclosure accepted, advancing to next question');
      console.log('   Current enrollment.planName:', this.enrollment.planName);
      console.log('   allowsSpecialtyProviders:', this.allowsSpecialtyProviders);
      console.log('   canProceed:', this.canProceed);
      
      // Advance to next question after accepting disclosure
      // The nextQuestion() method will handle skipping Q11 if needed
      setTimeout(() => {
        this.nextQuestion();
      }, 300);
    },
    
    toggleAdditionalBenefit(benefitId) {
      console.log('🔍 Toggle benefit clicked:', benefitId);
      const benefit = this.additionalBenefits.find(b => b._id === benefitId);
      if (!benefit) {
        console.warn('⚠️ Benefit not found for ID:', benefitId);
        return;
      }

      const isDentalBenefit = (benefit.planType || '').toLowerCase() === 'dental';
      console.log('🔍 Benefit type:', benefit.planType, 'isDental:', isDentalBenefit);

      if (this.isBenefitIncluded(benefitId)) {
        console.log('ℹ️ Benefit is included with the selected plan and cannot be removed.');
        return;
      }

      if (!this.isBenefitsOnlyPlan && isDentalBenefit && this.coreDentalBenefitId && benefitId === this.coreDentalBenefitId) {
        console.log('ℹ️ Core dental plan is automatically managed and cannot be toggled manually.');
        return;
      }

      // For Benefits Only plan, use the same per-member selection logic as other plans
      // Each member can select their own benefits independently

      const currentSelection = this.currentMemberSelection;
      if (!currentSelection) return;

      let updatedSelection = [...(currentSelection.selectedBenefits || [])];
      const index = updatedSelection.indexOf(benefitId);
      
      if (index > -1) {
        console.log('🔍 Removing benefit from selection');
        updatedSelection.splice(index, 1);
      } else {
        if (isDentalBenefit) {
          if (this.eliteDentalBenefitId && benefitId === this.eliteDentalBenefitId) {
            updatedSelection = [];
          } else if (this.essentialDentalBenefitId && benefitId === this.essentialDentalBenefitId) {
            updatedSelection = updatedSelection.filter(id => id !== this.eliteDentalBenefitId);
          } else {
            updatedSelection = updatedSelection.filter(id => id !== this.eliteDentalBenefitId && id !== this.essentialDentalBenefitId);
          }
        }
        console.log('🔍 Adding benefit to selection');
        updatedSelection.push(benefitId);
      }

      const deduped = Array.from(new Set(updatedSelection));
      const wasConfirmed = currentSelection.confirmed === true;
      const updated = {
        ...currentSelection,
        selectedBenefits: deduped,
        confirmed: false // Always reset to false when benefits change
      };
      this.$set(this.memberSelections, this.currentMemberSelectionIndex, updated);
      console.log('🔍 Final selected benefits for member:', deduped);
      console.log('🔍 Member confirmation reset:', {
        memberIndex: this.currentMemberSelectionIndex,
        wasConfirmed: wasConfirmed,
        nowConfirmed: updated.confirmed,
        reason: 'Benefit selection changed - must confirm again'
      });
      
      // Update aggregated benefits and recalculate price
      this.updateAggregatedBenefitSelections();
      this.scheduleBundleRecalculation(true);
      
      // Remove focus after click
      this.$nextTick(() => {
        document.activeElement.blur();
      });
    },
    
    scheduleBundleRecalculation(immediate = false) {
      if (immediate) {
        if (this.priceRecalcTimeoutId) {
          clearTimeout(this.priceRecalcTimeoutId);
          this.priceRecalcTimeoutId = null;
        }
        this.pendingPriceRecalc = false;
        this.calculateBundlePrice();
        return;
      }
      this.pendingPriceRecalc = true;
      if (this.priceRecalcTimeoutId) {
        return;
      }
      this.priceRecalcTimeoutId = setTimeout(() => {
        this.priceRecalcTimeoutId = null;
        this.calculateBundlePrice();
      }, 120);
    },

    async calculateBundlePrice() {
      if (this.isCalculatingPrice) {
        this.pendingPriceRecalc = true;
        return;
      }
      this.isCalculatingPrice = true;
      try {
        if (this.isBenefitsOnlyPlan) {
          this.pendingPriceRecalc = false;
          this.autoSetFamilySize();
          
          // For standalone plan, use bundle pricing like PHC/DPO
          // Calculate total price based on how many members have SELECTED each benefit
          // The price is based on the total count, not incremental per member
          let totalBenefits = 0;
          const perMemberTotals = Array(this.householdMemberCount).fill(0);
          const perBenefitDetails = {};
          
          // Get all unique benefit IDs selected by any member
          const allSelectedBenefitIds = new Set();
          if (Array.isArray(this.memberSelections)) {
            this.memberSelections.forEach(memberSelection => {
              if (memberSelection && Array.isArray(memberSelection.selectedBenefits)) {
                memberSelection.selectedBenefits.forEach(benefitId => {
                  allSelectedBenefitIds.add(benefitId);
                });
              }
            });
          }
          
          // Calculate bundle pricing for each benefit based on total selections
          allSelectedBenefitIds.forEach(benefitId => {
            // Count how many members have SELECTED this benefit
            let selectedCount = 0;
            const membersWithBenefit = [];
            if (Array.isArray(this.memberSelections)) {
              this.memberSelections.forEach((memberSelection, memberIndex) => {
                if (memberSelection && 
                    Array.isArray(memberSelection.selectedBenefits) &&
                    memberSelection.selectedBenefits.includes(benefitId)) {
                  selectedCount++;
                  membersWithBenefit.push(memberIndex);
                }
              });
            }
            
            // Determine pricing tier based on total selections (like PHC/DPO bundle pricing)
            // 0 = $0, 1 = Individual, 2 = Couple, 3+ = Family
            if (selectedCount === 0) {
              return; // Skip if no one selected
            }
            
            let familyCategory = 'single';
            if (selectedCount === 1) {
              familyCategory = 'single'; // Individual price
            } else if (selectedCount === 2) {
              familyCategory = 'couple'; // Couple price (replaces individual)
            } else if (selectedCount >= 3) {
              familyCategory = 'family'; // Family price (replaces couple)
            }
            
            const benefitCost = this.getStandaloneBenefitCost(benefitId, familyCategory);
            if (benefitCost > 0) {
              // Add the bundle price once (not per member)
              totalBenefits += benefitCost;
              
              // Distribute cost equally among members who selected it (for display purposes)
              const costPerMember = benefitCost / selectedCount;
              membersWithBenefit.forEach(memberIndex => {
                perMemberTotals[memberIndex] = (perMemberTotals[memberIndex] || 0) + costPerMember;
              });
              
              perBenefitDetails[benefitId] = {
                cost: benefitCost,
                members: membersWithBenefit,
                selectedCount: selectedCount,
                familyCategory: familyCategory
              };
            }
          });
          
          this.standaloneCostAllocation = {
            perMemberTotals,
            perBenefitDetails,
            familyCategory: 'bundle' // Bundle pricing like PHC/DPO
          };
          
          // For Benefits Only plan, benefits ARE the base plan
          // So basePlanMonthlyFee = total benefits cost, and totalBenefitsCost = 0
          this.basePlanMonthlyFee = parseFloat(totalBenefits.toFixed(2));
          this.basePlanProcessingFee = 0;
          this.totalBenefitsCost = 0; // No "additional benefits" - benefits are the plan itself
          this.totalSpecialtyCost = 0;
          this.enrollment.selectedSpecialtyProviders = 0;
          this.enrollment.payment.applicationFee = 25;
          this.enrollment.payment.processingFee = 0;
          this.calculatedTotalPrice = parseFloat(this.basePlanMonthlyFee.toFixed(2));
          this.enrollment.payment.monthlyFee = this.calculatedTotalPrice;
          this.enrollment.payment.totalPayment = this.totalPayment;
          this.memberCostBreakdown = this.buildMemberCostBreakdown();
          this.isCalculatingPrice = false;
          return;
        }
        
        const selectedPlan = this.availablePlans.find(p => p._id === this.enrollment.planId);
        if (!selectedPlan) {
          this.basePlanMonthlyFee = 0;
          this.basePlanProcessingFee = 0;
          this.totalBenefitsCost = 0;
          this.totalSpecialtyCost = 0;
          this.calculatedTotalPrice = 0;
          this.enrollment.payment.processingFee = 0;
          return;
        }
        
        // Ensure family size stays in sync with dependents
        this.autoSetFamilySize();

        // Build per-member breakdown
        this.standaloneCostAllocation = null;
        const memberBreakdown = this.buildMemberCostBreakdown();
        this.memberCostBreakdown = memberBreakdown;

        // Calculate total benefits cost
        // For bundle pricing benefits, calculate at family level
        // For regular benefits, sum per member
        let totalBenefits = 0;
        // Reuse selectedPlan from above (already declared at line 3539)
        const isPHCPlan = selectedPlan && ((selectedPlan.planName || '').toLowerCase().includes('phc') || selectedPlan.planType === 'PHC');
        const isDPOPlan = selectedPlan && ((selectedPlan.planName || '').toLowerCase().includes('dpo') || selectedPlan.planType === 'DPO');
        
        // Get all unique benefit IDs selected by any member
        const allSelectedBenefitIds = new Set();
        memberBreakdown.forEach(member => {
          if (Array.isArray(member.benefits)) {
            member.benefits.forEach(b => {
              if (!b.isIncluded) {
                allSelectedBenefitIds.add(b.benefitId);
              }
            });
          }
        });
        
        // Calculate bundle pricing benefits at family level
        const bundlePricingBenefits = new Set();
        allSelectedBenefitIds.forEach(benefitId => {
          const benefit = this.getBenefitById(benefitId);
          if (benefit) {
            if ((isDPOPlan && benefit.dpoBundlePricing && benefit.dpoBundlePricing.enabled) ||
                (isPHCPlan && benefit.bundlePricing && benefit.bundlePricing.enabled)) {
              bundlePricingBenefits.add(benefitId);
              totalBenefits += this.getFamilyBenefitPriceById(benefitId);
            }
          }
        });
        
        // Calculate regular pricing benefits (per member)
        memberBreakdown.forEach(member => {
          if (Array.isArray(member.benefits)) {
            member.benefits.forEach(b => {
              if (!b.isIncluded && !bundlePricingBenefits.has(b.benefitId)) {
                totalBenefits += b.price || 0;
              }
            });
          }
        });
        
        const totalSpecialtyCount = memberBreakdown.reduce((sum, member) => sum + (member.specialtyCount || 0), 0);
        
        // Calculate specialty cost at family level (not per member)
        // Specialists are summed within the plan, extras are charged at family level
        // REMOVED: No specialists are included - all are charged extra
        const totalSpecialty = totalSpecialtyCount * 5; // $5 per specialist (family total)

        this.totalBenefitsCost = parseFloat(totalBenefits.toFixed(2));
        this.totalSpecialtyCost = parseFloat(totalSpecialty.toFixed(2));
        this.enrollment.selectedSpecialtyProviders = totalSpecialtyCount;
        
        // Don't call updatePHCPlanTypeBasedOnSpecialists here - it's already called when specialists change
        // and calling it here creates an infinite loop (it calls scheduleBundleRecalculation which calls this function)
        // The plan type should already be updated before calculateBundlePrice is called
        
        const numberOfMembers = Math.max(this.memberCount, 1);
        const ageGroup = this.calculateAgeGroup(this.enrollment.dateOfBirth);
        
        // For PHC plans, use the price already updated by updatePHCPlanTypeBasedOnSpecialists
        // For other plans, calculate from selectedPlan
        let baseMonthlyFee = this.basePlanMonthlyFee || parseFloat(selectedPlan.startingPrice || 0);
        let applicationFee = parseFloat(selectedPlan.applicationFee || 0);
        
        // Reuse isPHCPlan from above (already declared at line 3563)
        
        // For PHC plans, we need to find the correct plan (Basic/Plus/Premium) based on current planName
        // and calculate price based on age. For other plans, use selectedPlan directly.
        let planToUseForPricing = selectedPlan;
        
        if (isPHCPlan && this.enrollment.planName) {
          // REMOVED: Plus and Premium cost the same as Basic
          // All PHC plans (Basic/Plus/Premium) have the same base price
          // Use Basic plan for pricing (or first available PHC plan)
          const basicPlan = this.availablePlans.find(p => {
            if (!p) return false;
            const pName = (p.planName || '').toLowerCase();
            const pDisplay = (p.displayName || p.name || '').toLowerCase();
            const isPHC = pName.includes('phc') || pDisplay.includes('phc') || p.planType === 'PHC';
            const isBasic = pName.includes('basic') || pDisplay.includes('basic');
            const isNotDual = !pName.includes('dual') && !pDisplay.includes('dual');
            return isPHC && isBasic && isNotDual;
          });
          
          if (basicPlan) {
            planToUseForPricing = basicPlan;
            console.log(`💰 Using Basic plan for pricing (all PHC plans have same base price)`);
          } else {
            // Fallback: use first available PHC plan
            const firstPHCPlan = this.availablePlans.find(p => {
              if (!p) return false;
              const pName = (p.planName || '').toLowerCase();
              const pDisplay = (p.displayName || p.name || '').toLowerCase();
              const isPHC = pName.includes('phc') || pDisplay.includes('phc') || p.planType === 'PHC';
              const isNotDual = !pName.includes('dual') && !pDisplay.includes('dual');
              return isPHC && isNotDual;
            });
            if (firstPHCPlan) {
              planToUseForPricing = firstPHCPlan;
              console.log(`💰 Fallback: Using first available PHC plan for pricing`);
            }
          }
        }
        
        // Calculate price based on age and plan type
        baseMonthlyFee = parseFloat(planToUseForPricing.startingPrice || 0);

        if (planToUseForPricing.incrementalMemberPricing && planToUseForPricing.incrementalMemberPricing.enabled) {
          const primaryDOB = this.enrollment.dateOfBirth;
          if (!primaryDOB) {
            console.warn('⚠️ Cannot calculate incremental pricing: primary member DOB is missing.');
          } else {
            // For incremental pricing:
            // - 1st position is always the subscriber (primary), price based on subscriber's age
            // - 2nd position (first dependent): fixed price per position, uses subscriber's age group (independent of dependent's age)
            // - 3rd, 4th, 5th, etc. (subsequent dependents): price based on each dependent's own age group
            // DO NOT sort by age - the order is: subscriber first, then dependents in the order they were added
            const orderedMemberAges = [primaryDOB];
            if (Array.isArray(this.enrollment.dependents)) {
              this.enrollment.dependents.forEach(dep => {
                if (dep.dateOfBirth) {
                  orderedMemberAges.push(dep.dateOfBirth);
                }
              });
            }
            
            // Log detailed information for debugging
            console.log('💰 Frontend: Calculating incremental pricing');
            console.log('   Subscriber DOB:', this.enrollment.dateOfBirth);
            console.log('   Dependents:', this.enrollment.dependents?.map(d => ({ name: `${d.firstName} ${d.lastName}`, dob: d.dateOfBirth })) || []);
            console.log('   Ordered member ages (subscriber first, then dependents in order):', orderedMemberAges);
            console.log('   Number of members:', orderedMemberAges.length);
            console.log('   Note: 1st = subscriber (price based on subscriber age), 2nd = first dependent (fixed price using subscriber age group), 3rd+ = subsequent dependents (price based on own age)');
            
            // Calculate and log ages for each member
            const subscriberAgeGroup = this.calculateAgeGroup(primaryDOB);
            orderedMemberAges.forEach((dob, idx) => {
              const birthDate = new Date(dob);
              const today = new Date();
              let age = today.getFullYear() - birthDate.getFullYear();
              const monthDiff = today.getMonth() - birthDate.getMonth();
              if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                age--;
              }
              const position = idx === 0 ? "1st" : idx === 1 ? "2nd" : idx === 2 ? "3rd" : idx === 3 ? "4th" : idx === 4 ? "5th" : "6th";
              const ageGroup = this.calculateAgeGroup(dob);
              if (idx === 0) {
                console.log(`   Member ${idx + 1} (${position}): DOB ${dob}, Age ${age}, Group ${ageGroup} - Subscriber (price based on this age)`);
              } else if (idx === 1) {
                console.log(`   Member ${idx + 1} (${position}): DOB ${dob}, Age ${age}, Group ${ageGroup} - First Dependent (fixed price using subscriber's age group ${subscriberAgeGroup})`);
              } else {
                console.log(`   Member ${idx + 1} (${position}): DOB ${dob}, Age ${age}, Group ${ageGroup} - Subsequent Dependent (price based on own age)`);
              }
            });

            try {
              const response = await axios.post('/api/enrollment-config/calculate-bundle', {
                mainPlanId: planToUseForPricing._id,
                benefitIds: [],
                ageGroup,
                numberOfMembers: orderedMemberAges.length,
                memberAges: orderedMemberAges
              });

              const calculatedPrice = parseFloat(response.data.totalMonthlyFee) || baseMonthlyFee;
              if (calculatedPrice !== baseMonthlyFee) {
                baseMonthlyFee = calculatedPrice;
                console.log(`💰 Calculated price based on age: $${baseMonthlyFee.toFixed(2)} (was $${parseFloat(planToUseForPricing.startingPrice || 0).toFixed(2)})`);
              }
              applicationFee = parseFloat(response.data.applicationFee) || applicationFee;
            } catch (error) {
              console.error('❌ Error calculating bundle price:', error);
            }
          }
        } else if (planToUseForPricing.pricingModel === 'tiered-both' && planToUseForPricing.pricingTiers) {
          const familySizeCategory = numberOfMembers === 1 ? 'Individual' : 
                                     numberOfMembers === 2 ? 'Two Persons' : 
                                     numberOfMembers === 3 ? 'Three Persons' :
                                     numberOfMembers === 4 ? 'Four Persons' :
                                     numberOfMembers === 5 ? 'Five Persons' : 'Six Persons';
          
          const matchingTier = planToUseForPricing.pricingTiers.find(tier => 
            tier.label.includes(familySizeCategory) && tier.label.includes(ageGroup)
          );
          
          if (matchingTier) {
            baseMonthlyFee = parseFloat(matchingTier.monthlyFee || baseMonthlyFee);
          }
        } else if (planToUseForPricing.pricingModel === 'flat' && planToUseForPricing.flatPrice) {
          baseMonthlyFee = parseFloat(planToUseForPricing.flatPrice);
        }
        
        // Always update the price for PHC plans (it should be calculated based on age)
        // The price from updatePHCPlanTypeBasedOnSpecialists is just a placeholder
        this.basePlanMonthlyFee = parseFloat((baseMonthlyFee || 0).toFixed(2));
        this.basePlanProcessingFee = 0;
        this.enrollment.payment.applicationFee = parseFloat((applicationFee || 0).toFixed(2));
        this.enrollment.payment.processingFee = 0;

        const totalMonthly = this.basePlanMonthlyFee + this.totalBenefitsCost + this.totalSpecialtyCost;
        this.calculatedTotalPrice = parseFloat(totalMonthly.toFixed(2));
        this.enrollment.payment.monthlyFee = this.calculatedTotalPrice;
        this.enrollment.payment.totalPayment = this.totalPayment;
        
        console.log('💰 Pricing summary:', {
          basePlanMonthlyFee: this.basePlanMonthlyFee,
          totalBenefitsCost: this.totalBenefitsCost,
          totalSpecialtyCost: this.totalSpecialtyCost,
          totalSpecialtyCount,
          calculatedTotalPrice: this.calculatedTotalPrice,
          applicationFee: this.enrollment.payment.applicationFee
        });
      } catch (error) {
        console.error('Error calculating bundle price:', error);
        this.calculatedTotalPrice = this.enrollment.payment.monthlyFee || 0;
      } finally {
        this.isCalculatingPrice = false;
        // Only schedule recalculation if we're not already calculating and it's not Benefits Only
        // This prevents infinite loops
        if (this.pendingPriceRecalc && !this.isBenefitsOnlyPlan && !this.isCalculatingPrice) {
          const wasPending = this.pendingPriceRecalc;
          this.pendingPriceRecalc = false;
          // Use a small delay to prevent immediate re-entry
          setTimeout(() => {
            if (wasPending && !this.isCalculatingPrice) {
              this.scheduleBundleRecalculation(true);
            }
          }, 50);
        } else {
          this.pendingPriceRecalc = false;
        }
      }
    },

    buildMemberCostBreakdown() {
      const breakdown = this.generateSpecialtyBreakdown();
      const allocation = this.isBenefitsOnlyPlan ? (this.standaloneCostAllocation || { perMemberTotals: [], perBenefitDetails: {} }) : null;
      const perMemberTotals = allocation ? allocation.perMemberTotals || [] : [];
      const perBenefitDetails = allocation ? allocation.perBenefitDetails || {} : {};
      return this.memberSelections.map((selection, index) => {
        const member = this.membersList[index] || {};
        const sanitizedSelection = {
          ...selection,
          selectedBenefits: Array.isArray(selection.selectedBenefits) ? selection.selectedBenefits : [],
          selectedSpecialties: Array.isArray(selection.selectedSpecialties) ? this.normalizeSelectedSpecialties(selection.selectedSpecialties) : []
        };

        // Combine included benefits (default) with selected benefits (manual)
        // Included benefits are applied to all members, selected benefits are per member
        // Benefits Only plan does NOT have any included benefits - only manually selected ones
        const allBenefitIds = new Set();
        
        // Add included benefits (default - applied to all members)
        // BUT NOT for Benefits Only plan - it has no included benefits
        if (!this.isBenefitsOnlyPlan && Array.isArray(this.includedBenefitIds)) {
          this.includedBenefitIds.forEach(id => allBenefitIds.add(id));
        }
        
        // Add selected benefits (manual - per member)
        if (Array.isArray(sanitizedSelection.selectedBenefits)) {
          sanitizedSelection.selectedBenefits.forEach(id => allBenefitIds.add(id));
        }
        
        // Check if this plan uses bundle pricing
        const selectedPlan = this.availablePlans.find(p => p._id === this.enrollment.planId);
        const isPHCPlan = selectedPlan && ((selectedPlan.planName || '').toLowerCase().includes('phc') || selectedPlan.planType === 'PHC');
        const isDPOPlan = selectedPlan && ((selectedPlan.planName || '').toLowerCase().includes('dpo') || selectedPlan.planType === 'DPO');
        
        const benefits = Array.from(allBenefitIds).map(benefitId => {
          const name = this.getBenefitNameById(benefitId);
          const benefit = this.getBenefitById(benefitId);
          // Included benefits have price 0 (they're included in the plan)
          // Only selected benefits have a price
          // Benefits Only plan has NO included benefits - all benefits are paid
          const isIncluded = !this.isBenefitsOnlyPlan && 
            Array.isArray(this.includedBenefitIds) && 
            this.includedBenefitIds.includes(benefitId);
          
          let price = 0;
          if (!isIncluded) {
            // Check if this benefit uses bundle pricing
            const usesBundlePricing = benefit && (
              (isDPOPlan && benefit.dpoBundlePricing && benefit.dpoBundlePricing.enabled) ||
              (isPHCPlan && benefit.bundlePricing && benefit.bundlePricing.enabled)
            );
            
            if (usesBundlePricing) {
              // For bundle pricing, show 0 per member (calculated at family level)
              price = 0;
            } else {
              // Regular pricing (per member)
              price = this.getPerMemberBenefitPriceById(benefitId);
            }
          }
          
          if (allocation) {
            const detail = perBenefitDetails[benefitId];
            if (detail && Array.isArray(detail.members) && detail.members.includes(index)) {
              price = detail.cost / detail.members.length;
            } else {
              price = 0;
            }
          }
          
          return {
            benefitId,
            name,
            price: parseFloat((price || 0).toFixed(2)),
            isIncluded: isIncluded, // Flag to identify included vs selected benefits
            usesBundlePricing: benefit && (
              (isDPOPlan && benefit.dpoBundlePricing && benefit.dpoBundlePricing.enabled) ||
              (isPHCPlan && benefit.bundlePricing && benefit.bundlePricing.enabled)
            )
          };
        });

        // For bundle pricing benefits, don't sum per member (calculated at family level)
        const benefitTotal = allocation
          ? (perMemberTotals[index] || 0)
          : benefits.reduce((sum, benefit) => {
              // Don't add bundle pricing benefits here - they're calculated at family level
              if (benefit.usesBundlePricing) {
                return sum;
              }
              return sum + (benefit.price || 0);
            }, 0);

        const specialtyInfo = breakdown[index] || { count: 0, extra: 0, included: 0, cost: 0, specialties: [] };

        return {
          memberKey: sanitizedSelection.memberKey,
          isPrimary: member.isPrimary || index === 0,
          firstName: member.firstName || '',
          lastName: member.lastName || '',
          relationship: member.relationship || (member.isPrimary ? 'Primary' : 'Member'),
          specialtyCount: specialtyInfo.count,
          includedSpecialtyCount: specialtyInfo.included,
          extraSpecialtyCount: specialtyInfo.extra,
          specialties: specialtyInfo.specialties,
          specialtyCost: parseFloat((specialtyInfo.cost || 0).toFixed(2)),
          benefits,
          benefitTotal: parseFloat((benefitTotal || 0).toFixed(2)),
          totalExtras: parseFloat(((benefitTotal || 0) + (specialtyInfo.cost || 0)).toFixed(2))
        };
      });
    },
    
    getPlanPriceForUser(plan) {
      if (plan._id === 'benefits-only') {
        return '$0.00';
      }
      
      // For DPO or flat pricing, return flat price (no processing fee)
      if (plan.pricingModel === 'flat') {
        const value = plan.flatPrice ? parseFloat(plan.flatPrice) : parseFloat(plan.startingPrice);
        return `$${(value || 0).toFixed(2)}`;
      }
      
      // For plans with incremental member pricing
      if (plan.incrementalMemberPricing && plan.incrementalMemberPricing.enabled && plan.incrementalMemberPricing.pricing) {
        const pricing = plan.incrementalMemberPricing.pricing;
        
        // For incremental pricing:
        // - 1st position is always the subscriber (primary), price based on subscriber's age
        // - 2nd, 3rd, 4th, etc. are dependents in order, with fixed prices per position (use subscriber's age group)
        // DO NOT sort by age - the order is: subscriber first, then dependents in the order they were added
        const primaryDOB = this.enrollment.dateOfBirth;
        if (!primaryDOB) {
          // Fallback if no primary DOB
          const fallback = plan.startingPrice ? parseFloat(plan.startingPrice) : 0;
          return `$${fallback.toFixed(2)}`;
        }
        
        const orderedMemberAges = [primaryDOB];
        if (this.enrollment.dependents && this.enrollment.dependents.length > 0) {
          this.enrollment.dependents.forEach(dep => {
            if (dep.dateOfBirth) orderedMemberAges.push(dep.dateOfBirth);
          });
        }
        
        if (orderedMemberAges.length === 0) {
          // Fallback if no DOBs
          const fallback = plan.startingPrice ? parseFloat(plan.startingPrice) : 0;
          return `$${fallback.toFixed(2)}`;
        }
        
        // Get subscriber's age group (used for 1st and 2nd positions)
        const subscriberAgeGroup = this.calculateAgeGroup(primaryDOB);
        let subscriberAgeGroupKey = null;
        if (subscriberAgeGroup === 'Under 18') subscriberAgeGroupKey = 'Under 18 Yrs';
        else if (subscriberAgeGroup === '19-34') subscriberAgeGroupKey = '19 - 34 Yrs';
        else if (subscriberAgeGroup === '35-45') subscriberAgeGroupKey = '35 - 45 Yrs';
        else if (subscriberAgeGroup === '46-55') subscriberAgeGroupKey = '46 - 55 Yrs';
        else if (subscriberAgeGroup === '56+') subscriberAgeGroupKey = '56 +';
        
        // Calculate total price based on ordered members
        let totalPrice = 0;
        console.log(`💰 Frontend getPlanPriceForUser: Calculating price for ${orderedMemberAges.length} members`);
        console.log(`   Subscriber age group: ${subscriberAgeGroup} -> ${subscriberAgeGroupKey} (used for 1st and 2nd positions)`);
        orderedMemberAges.forEach((dateOfBirth, index) => {
          const position = index === 0 ? "1st" :
                         index === 1 ? "2nd" :
                         index === 2 ? "3rd" :
                         index === 3 ? "4th" :
                         index === 4 ? "5th" : "6th";
          
          // Calculate actual age for debugging
          const birthDate = new Date(dateOfBirth);
          const today = new Date();
          let age = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();
          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
          }
          
          // Determine which age group to use for pricing:
          // - 1st (subscriber): use subscriber's age group
          // - 2nd (first dependent): use subscriber's age group (fixed price, independent of dependent's age)
          // - 3rd+ (subsequent dependents): use each dependent's own age group
          let ageGroupKey;
          if (index === 0) {
            // Subscriber: use own age group
            ageGroupKey = subscriberAgeGroupKey;
          } else if (index === 1) {
            // First dependent: use subscriber's age group (fixed price)
            ageGroupKey = subscriberAgeGroupKey;
          } else {
            // Subsequent dependents: use their own age group
            const dependentAgeGroup = this.calculateAgeGroup(dateOfBirth);
            if (dependentAgeGroup === 'Under 18') ageGroupKey = 'Under 18 Yrs';
            else if (dependentAgeGroup === '19-34') ageGroupKey = '19 - 34 Yrs';
            else if (dependentAgeGroup === '35-45') ageGroupKey = '35 - 45 Yrs';
            else if (dependentAgeGroup === '46-55') ageGroupKey = '46 - 55 Yrs';
            else if (dependentAgeGroup === '56+') ageGroupKey = '56 +';
            else ageGroupKey = null;
          }
          
          const memberPrice = (ageGroupKey && pricing[ageGroupKey] && pricing[ageGroupKey][position] !== undefined)
            ? parseFloat(pricing[ageGroupKey][position]) || 0
            : 0;
          
          totalPrice += memberPrice;
          
          if (index === 0) {
            console.log(`   Member ${index + 1} (${position}) - Subscriber: DOB ${dateOfBirth}, Age ${age}, Group ${subscriberAgeGroup} -> ${ageGroupKey}, Price: $${memberPrice.toFixed(2)}`);
          } else if (index === 1) {
            console.log(`   Member ${index + 1} (${position}) - First Dependent: DOB ${dateOfBirth}, Age ${age}, Using subscriber's group ${subscriberAgeGroup} -> ${ageGroupKey} (fixed price), Price: $${memberPrice.toFixed(2)}`);
          } else {
            const dependentAgeGroup = this.calculateAgeGroup(dateOfBirth);
            console.log(`   Member ${index + 1} (${position}) - Subsequent Dependent: DOB ${dateOfBirth}, Age ${age}, Group ${dependentAgeGroup} -> ${ageGroupKey}, Price: $${memberPrice.toFixed(2)}`);
          }
          
          if (!ageGroupKey || !pricing[ageGroupKey] || pricing[ageGroupKey][position] === undefined) {
            console.warn(`   ⚠️ WARNING: No pricing found for ${ageGroupKey} / ${position}`);
            console.log(`   Available keys in pricing:`, pricing ? Object.keys(pricing) : 'N/A');
            if (pricing[ageGroupKey]) {
              console.log(`   Available positions for ${ageGroupKey}:`, Object.keys(pricing[ageGroupKey]));
            }
          }
        });
        
        if (totalPrice > 0) {
          console.log(`💰 ${plan.name} - Calculated total price: $${totalPrice.toFixed(2)} (${orderedMemberAges.length} member(s), ordered by age)`);
          return `$${totalPrice.toFixed(2)}`;
        }
        
        // Fallback: use startingPrice (calculated from 19-34 age group, 1st member)
        const fallback = plan.startingPrice ? parseFloat(plan.startingPrice) : 0;
        return `$${fallback.toFixed(2)}`;
      }
      
      // For PHC plans with tiered pricing (age + family size)
      if (plan.pricingModel === 'tiered-both' && plan.pricingTiers && this.enrollment.dateOfBirth) {
        // Calculate age group
        const ageGroup = this.calculateAgeGroup(this.enrollment.dateOfBirth);
        
        // Calculate number of members (enrollee + dependents)
        const numberOfMembers = 1 + (this.enrollment.dependents?.length || 0);
        
        // Determine family size category
        const familySizeCategory = numberOfMembers === 1 ? 'Individual' : 
                                   numberOfMembers === 2 ? 'Two Persons' : 
                                   numberOfMembers === 3 ? 'Three Persons' :
                                   numberOfMembers === 4 ? 'Four Persons' :
                                   numberOfMembers === 5 ? 'Five Persons' : 'Six Persons';
        
        // Find matching tier
        const matchingTier = plan.pricingTiers.find(tier => 
          tier.label.includes(familySizeCategory) && tier.label.includes(ageGroup)
        );
        
        if (matchingTier) {
          console.log(`💰 ${plan.name} price for ${ageGroup}, ${familySizeCategory}: $${matchingTier.monthlyFee}`);
        const total = parseFloat(matchingTier.monthlyFee) || 0;
        return `$${total.toFixed(2)}`;
        }
      }
      
      // Fallback to starting price
      const fallback = plan.startingPrice ? parseFloat(plan.startingPrice) : 0;
      return `$${fallback.toFixed(2)}`;
    },
    
    getPlanKeyBenefits(plan) {
      // Define key benefits that differentiate each plan
      const planBenefits = {
        'PHC Basic': ['Basic Coverage', 'Primary Care'],
        'PHC Plus': ['All Basic Benefits', 'Specialty Care', 'Pharmacy'],
        'PHC Premium': ['All Plus Benefits', 'Extended Specialty Access'],
        'DPO': ['Direct Primary Care', 'Unlimited Visits']
      };
      
      return planBenefits[plan.name] || plan.benefits?.slice(0, 2) || ['Basic Coverage'];
    },
    
    getAvailableAdditionalBenefits() {
      if (!Array.isArray(this.additionalBenefits)) return [];
      return this.additionalBenefits;
    },
    
    // selectCoverage method removed - familySize is now auto-calculated from dependents
    // selectPaymentMethod method removed - payment is always by Credit Card
    
    calculatePayment() {
      if (this.isBenefitsOnlyPlan) {
        this.enrollment.payment.monthlyFee = this.calculatedTotalPrice;
        this.enrollment.payment.applicationFee = 25;
        this.enrollment.payment.processingFee = 0;
        this.enrollment.payment.totalPayment = this.totalPayment;
        return;
      }
      const plan = this.availablePlans.find(p => p._id === this.enrollment.planId);
      if (!plan) return;
      
      let monthlyFee = plan.flatPrice || plan.startingPrice;
      
      // Adjust based on family size if tiered
      if (plan.pricingModel === 'tiered-family') {
        const tier = plan.pricingTiers.find(t => t.label === this.enrollment.familySize);
        if (tier) monthlyFee = tier.monthlyFee;
      }
      this.enrollment.payment.processingFee = 0;
      this.enrollment.payment.monthlyFee = parseFloat(monthlyFee || 0);
      this.enrollment.payment.totalPayment = this.totalPayment;
    },
    
    onDateSelected(date) {
      // Ensure the date is stored in YYYY-MM-DD format without timezone issues
      console.log('📅 Date selected:', date);
      this.enrollment.dateOfBirth = date;
    },
    
    calculateAgeGroup(dateOfBirth) {
      if (!dateOfBirth) return null;
      
      // Parse date as local date (YYYY-MM-DD) to avoid timezone issues
      const [year, month, day] = dateOfBirth.split('-').map(Number);
      const birthDate = new Date(year, month - 1, day); // month is 0-indexed
      
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      // Adjust age if birthday hasn't occurred yet this year
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      
      // Map age to age group
      if (age < 18) return 'Under 18';
      if (age >= 19 && age <= 34) return '19-34';
      if (age >= 35 && age <= 45) return '35-45';
      if (age >= 46 && age <= 55) return '46-55';
      if (age >= 56) return '56+';
      
      return null;
    },
    
    async submitEnrollment() {
      console.log('📝 submitEnrollment called');
      console.log('   canProceed:', this.canProceed);
      console.log('   currentQuestion:', this.currentQuestion);
      
      if (!this.canProceed) {
        console.warn('⚠️ Cannot proceed - validation failed');
        return;
      }
      
      this.submitting = true;
      try {
        console.log('💰 Step 1: Calculating bundle price...');
        await this.calculateBundlePrice();
        console.log('✅ Step 1 complete: Bundle price calculated');
        
        console.log('🖊️ Step 2: Getting signature from canvas...');
        if (!this.$refs.signatureCanvas) {
          console.error('❌ Signature canvas ref not found!');
          throw new Error('Signature canvas not found. Please sign the enrollment form.');
        }
        this.enrollment.signature = this.$refs.signatureCanvas.toDataURL();
        console.log('✅ Step 2 complete: Signature captured');
        
        // Set payment method to Credit Card (always)
        this.enrollment.payment.method = 'Credit Card';
        
        // For all plans: if 3+ members selected the same benefit, add it to all members
        // This applies to PHC, DPO, and Benefits Only plans
        if (Array.isArray(this.memberSelections)) {
          // Get all unique benefit IDs
          const allBenefitIds = new Set();
          this.memberSelections.forEach(memberSelection => {
            if (memberSelection && Array.isArray(memberSelection.selectedBenefits)) {
              memberSelection.selectedBenefits.forEach(benefitId => {
                allBenefitIds.add(benefitId);
              });
            }
          });
          
          // For each benefit, check if 3+ members selected it
          allBenefitIds.forEach(benefitId => {
            let selectedCount = 0;
            this.memberSelections.forEach(memberSelection => {
              if (memberSelection && 
                  Array.isArray(memberSelection.selectedBenefits) &&
                  memberSelection.selectedBenefits.includes(benefitId)) {
                selectedCount++;
              }
            });
            
            // If 3+ members selected, add to all members
            if (selectedCount >= 3) {
              console.log(`🔄 Benefit ${benefitId} selected by ${selectedCount} members - adding to all members`);
              this.memberSelections.forEach((memberSelection, index) => {
                if (memberSelection && Array.isArray(memberSelection.selectedBenefits)) {
                  if (!memberSelection.selectedBenefits.includes(benefitId)) {
                    memberSelection.selectedBenefits.push(benefitId);
                    console.log(`   ✅ Added to member ${index + 1}`);
                  }
                }
              });
            }
          });
          
          // Recalculate price after adding benefits to all members
          await this.calculateBundlePrice();
        }
        
        // Calculate age group automatically from date of birth
        const ageGroup = this.calculateAgeGroup(this.enrollment.dateOfBirth);
        
        // Prepare enrollment data with calculated age group
        const enrollmentData = { 
          ...this.enrollment,
          ageGroup: ageGroup // Set calculated age group
        };
        enrollmentData.payment = {
          ...enrollmentData.payment,
          totalPayment: this.totalPayment
        };
        enrollmentData.selectedAdditionalBenefits = [...this.selectedAdditionalBenefits];
        enrollmentData.memberSelections = JSON.parse(JSON.stringify(this.memberSelections));
        enrollmentData.memberCostBreakdown = JSON.parse(JSON.stringify(this.memberCostBreakdown));
        enrollmentData.pricingSummary = {
          basePlanMonthlyFee: this.basePlanMonthlyFee,
          basePlanProcessingFee: this.basePlanProcessingFee,
          totalBenefitsCost: this.totalBenefitsCost,
          totalSpecialtyCost: this.totalSpecialtyCost,
          calculatedMonthlyFee: this.calculatedTotalPrice
        };
        
        // Always save as Draft first (payment required)
        enrollmentData.status = 'Draft';
        
        // Remove empty optional fields
        if (!enrollmentData.employerName) delete enrollmentData.employerName;
        if (!enrollmentData.workPhone) delete enrollmentData.workPhone;
        if (!enrollmentData.homePhone) delete enrollmentData.homePhone;
        
        console.log('📊 Calculated age group:', ageGroup, 'for DOB:', this.enrollment.dateOfBirth);
        console.log('💳 Payment method set to: Credit Card');
        console.log('💰 Payment values before submission:');
        console.log('   monthlyFee:', this.enrollment.payment.monthlyFee);
        console.log('   applicationFee:', this.enrollment.payment.applicationFee);
        console.log('   monthsPaid:', this.enrollment.payment.monthsPaid);
        console.log('   calculatedTotalPrice:', this.calculatedTotalPrice);
        console.log('   totalPayment:', this.totalPayment);
        console.log('   Dependents count:', this.enrollment.dependents?.length || 0);
        console.log('   Dependents with DOB:', this.enrollment.dependents?.filter(d => d.dateOfBirth).length || 0);
        console.log('📤 Sending enrollment to backend...');
        
        const response = await axios.post('/api/fhc-enrollments', enrollmentData);
        
        console.log('📥 Backend response:', response.data);
        console.log('📥 Response status:', response.status);
        console.log('📥 Response data type:', typeof response.data);
        console.log('📥 Response data keys:', Object.keys(response.data || {}));
        
        if (!response.data || !response.data._id) {
          console.error('❌ Backend response missing _id field');
          console.error('   Full response:', JSON.stringify(response.data, null, 2));
          throw new Error('Backend did not return enrollment ID. Please try again.');
        }
        
        this.submittedEnrollmentId = response.data._id;
        this.submittedApplicationCode = response.data.applicationCode || null;
        
        console.log('✅ Enrollment saved successfully!');
        console.log('   ID:', this.submittedEnrollmentId);
        console.log('   Application Code:', this.submittedApplicationCode);
        
        // Always go to payment form (Q16)
        console.log('➡️ Moving to payment form (Q16)');
        this.currentQuestion = 16; // Payment form
        
      } catch (error) {
        console.error('❌ Error submitting enrollment:', error);
        console.error('   Error message:', error.message);
        console.error('   Error stack:', error.stack);
        console.error('   Error response:', error.response);
        console.error('   Error response data:', error.response?.data);
        console.error('   Error response status:', error.response?.status);
        
        const errorMsg = error.response?.data?.message || error.message || 'Error submitting enrollment. Please try again.';
        alert(errorMsg);
        
        // Reset submitting state and stay on current question
        this.submitting = false;
        // Don't change currentQuestion - let user see the error and try again
      } finally {
        this.submitting = false;
      }
    },
    
    async handlePaymentSuccess(paymentData) {
      console.log('✅ Payment successful:', paymentData);
      console.log('   Enrollment ID:', this.submittedEnrollmentId);
      console.log('   Backend already updated status to:', paymentData.enrollment?.status || 'Approved');
      
      // Update enrollment ID and application code from payment response if available
      if (paymentData.enrollment) {
        if (paymentData.enrollment._id) {
          this.submittedEnrollmentId = paymentData.enrollment._id;
        }
        if (paymentData.enrollment.applicationCode) {
          this.submittedApplicationCode = paymentData.enrollment.applicationCode;
        }
      }
      
      // Backend already updated status to 'Approved' after payment
      // No need to make another API call - just move to success screen
      console.log('🎉 Moving to success screen (Q17)');
      console.log('   Final Enrollment ID:', this.submittedEnrollmentId);
      console.log('   Final Application Code:', this.submittedApplicationCode);
      
      // Move to success screen
      this.currentQuestion = 17;
    },
    
    handlePaymentError(error) {
      console.error('❌ Payment error:', error);
      console.error('   Error details:', error.response?.data);
      
      // Show error message to user
      const errorMsg = error.response?.data?.message || error.message || 'Payment failed. Please try again.';
      this.$toast?.error(errorMsg) || alert(errorMsg);
      
      // Keep user on payment screen so they can retry
      // Don't change currentQuestion - let them retry
      console.log('💡 User can retry payment from current screen');
    },
    
    handlePaymentCancel() {
      // User cancelled payment - go back to review screen
      this.currentQuestion = 15;
    },
    
    async saveDraftAndExit() {
      try {
        await axios.post('/api/fhc-enrollments', {
          ...this.enrollment,
          status: 'Draft'
        });
        this.$toast?.success('Draft saved!') || console.log('Draft saved');
        this.goToLogin();
      } catch (error) {
        console.error('Error saving draft:', error);
      }
    },
    
    resetEnrollmentData() {
      this.hasDependents = null;
      this.selectedPlanKey = null;
      this.isBenefitsOnlyPlan = false;
      this.standaloneCostAllocation = null;
      this.selectedPlanDisclosure = null;
      this.enrollment = {
        firstName: '',
        lastName: '',
        dateOfBirth: null,
        email: '',
        cellPhone: '',
        streetAddress: '',
        city: '',
        state: 'FL',
        zipCode: '',
        maritalStatus: '',
        dependents: [],
        planId: null,
        planType: '',
        planName: '',
        familySize: 'Single',
        ageGroup: '',
        homePhone: '',
        employmentStatus: '',
        employerName: '',
        workPhone: '',
        payment: {
          method: '',
          monthsPaid: 1,
          monthlyFee: 0,
          applicationFee: 0,
          processingFee: 0,
          totalPayment: 0
        },
        signature: '',
        signatureDate: new Date().toISOString().substr(0, 10),
        status: 'Draft'
      };
      this.selectedAdditionalBenefits = [];
      this.calculatedTotalPrice = 0;
      this.basePlanMonthlyFee = 0;
      this.basePlanProcessingFee = 0;
      this.totalBenefitsCost = 0;
      this.totalSpecialtyCost = 0;
      this.clearSignature();
    },
    
    startNewEnrollment() {
      this.currentQuestion = 0;
      this.resetEnrollmentData();
    },
    
    async downloadMemberCard() {
      if (!this.submittedEnrollmentId) {
        console.error('No enrollment ID available');
        this.$toast?.error('Enrollment ID not available. Please wait for enrollment to complete.') || 
        alert('Enrollment ID not available. Please wait for enrollment to complete.');
        return;
      }
      
      try {
        this.downloadingCard = true;
        
        console.log('📥 Requesting member card PDF for enrollment:', this.submittedEnrollmentId);
        
        const response = await axios.get(`/api/fhc-enrollments/${this.submittedEnrollmentId}/member-card`, {
          responseType: 'blob',
          timeout: 30000 // 30 second timeout
        });
        
        console.log('✅ PDF received, size:', response.data.size, 'bytes');
        console.log('📄 Content type:', response.headers['content-type']);
        console.log('📄 Response type:', typeof response.data);
        
        // Check if response is actually a PDF
        const contentType = response.headers['content-type'] || '';
        if (contentType && !contentType.includes('application/pdf')) {
          console.error('❌ Unexpected content type:', contentType);
          // Try to parse as JSON error message
          const text = await response.data.text();
          try {
            const errorData = JSON.parse(text);
            throw new Error(errorData.error || errorData.message || 'Unknown error from server');
          } catch (parseErr) {
            throw new Error(`Server returned ${contentType} instead of PDF`);
          }
        }
        
        // Validate PDF data
        if (!response.data || response.data.size === 0) {
          throw new Error('Received empty PDF file');
        }
        
        // Create blob and download
        const blob = new Blob([response.data], { type: 'application/pdf' });
        console.log('📦 Blob created, size:', blob.size, 'bytes, type:', blob.type);
        
        if (blob.size === 0) {
          throw new Error('Received empty PDF file');
        }
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `member-cards-${this.submittedApplicationCode || this.submittedEnrollmentId}.pdf`;
        link.style.display = 'none';
        document.body.appendChild(link);
        
        console.log('📥 Triggering download...');
        link.click();
        
        // Clean up after a delay
        setTimeout(() => {
          if (document.body.contains(link)) {
            document.body.removeChild(link);
          }
          window.URL.revokeObjectURL(url);
          console.log('🧹 Cleaned up download link');
        }, 1000);
        
        console.log('✅ Member card downloaded successfully!');
        this.$toast?.success('Member card downloaded successfully!') || 
        console.log('Member card downloaded successfully!');
      } catch (error) {
        console.error('❌ Error downloading member card:', error);
        console.error('❌ Error details:', error.response?.data);
        console.error('❌ Error status:', error.response?.status);
        
        let errorMessage = 'Failed to download member card. ';
        if (error.response?.status === 404) {
          errorMessage += 'Enrollment not found.';
        } else if (error.response?.status === 400) {
          errorMessage += 'Invalid enrollment data.';
        } else if (error.response?.status === 500) {
          errorMessage += 'Server error. Please try again later.';
        } else if (error.message) {
          errorMessage += error.message;
        } else {
          errorMessage += 'Please try again.';
        }
        
        this.$toast?.error(errorMessage) || 
        alert(errorMessage);
      } finally {
        this.downloadingCard = false;
      }
    },
    
    goToLogin() {
      // Se está na primeira página, voltar normalmente
      if (this.currentQuestion === 0) {
        if (window.history.length > 1) {
          this.$router.go(-1);
        } else {
          this.$router.push('/');
        }
      } else {
        // Se já começou o preenchimento, mostrar modal de confirmação
        this.showCancelDialog = true;
      }
    },
    
    confirmCancel() {
      // Fechar modal e voltar para a primeira página
      this.showCancelDialog = false;
      this.currentQuestion = 0;
      
      // Limpar dados do enrollment
      this.resetEnrollmentData();
      
      // Mostrar mensagem de confirmação
      this.$toast?.info('Enrollment cancelled. You can start over anytime.') || 
      console.log('Enrollment cancelled. You can start over anytime.');
    },
    
    formatDate(date) {
      if (!date) return '';
      // Parse as local date to avoid timezone issues
      const [year, month, day] = date.split('-');
      return `${month}/${day}/${year}`;
    },
    
    async lookupZipCode() {
      // Clear previous timeout
      if (this.zipLookupTimeout) {
        clearTimeout(this.zipLookupTimeout);
      }
      
      // Reset auto-fill flag
      this.cityAutoFilled = false;
      
      // Only lookup if we have a complete zip code (5 digits)
      const zipCode = this.enrollment.zipCode.replace(/\D/g, '');
      if (zipCode.length !== 5) return;
      
      // Debounce the lookup
      this.zipLookupTimeout = setTimeout(async () => {
        this.lookingUpZip = true;
        
        try {
          // Using free ZipCodeAPI service
          const response = await axios.get(`https://api.zippopotam.us/us/${zipCode}`);
          
          if (response.data && response.data.places && response.data.places.length > 0) {
            const place = response.data.places[0];
            this.enrollment.city = place['place name'];
            this.enrollment.state = place['state abbreviation'];
            this.cityAutoFilled = true;
            
            this.$toast?.success('City and state auto-filled!') || console.log('Address found');
          }
        } catch (error) {
          console.log('Zip code not found or API unavailable');
          // Silently fail - user can enter manually
          this.cityAutoFilled = false;
        } finally {
          this.lookingUpZip = false;
        }
      }, 500); // Wait 500ms after user stops typing
    },
    
    // Signature methods
    initSignatureCanvas() {
      // Prevent multiple simultaneous initializations
      if (this.isInitializingSignature) {
        return;
      }
      this.isInitializingSignature = true;
      
      this.$nextTick(() => {
        const canvas = this.$refs.signatureCanvas;
        console.log('🖊️ Initializing signature canvas:', canvas);
        if (!canvas) {
          console.error('❌ Signature canvas not found!');
          this.isInitializingSignature = false;
          if (this.signatureInitAttempts < 5) {
            this.signatureInitAttempts += 1;
            setTimeout(() => {
              this.isInitializingSignature = false;
              this.initSignatureCanvas();
            }, 150);
          }
          return;
        }

        let imageData = null;
        if (this.signatureContext && canvas.width > 0 && canvas.height > 0) {
          try {
            imageData = this.signatureContext.getImageData(0, 0, canvas.width, canvas.height);
          } catch (e) {
            console.log('No previous signature to save');
          }
        }

        const container = canvas.parentElement;
        const questionContainer = this.$refs.questionContainer;
        let newWidth =
          canvas.offsetWidth ||
          (container ? container.clientWidth : 0) ||
          (questionContainer ? questionContainer.clientWidth - 32 : 0) ||
          (typeof window !== 'undefined' ? window.innerWidth - 48 : 0) ||
          600;

        newWidth = Math.max(Math.round(newWidth), 320);
        const newHeight = 150;

        canvas.style.width = `${newWidth}px`;
        canvas.style.height = `${newHeight}px`;

        if (canvas.width !== newWidth || canvas.height !== newHeight) {
          canvas.width = newWidth;
          canvas.height = newHeight;
          console.log('✅ Canvas resized to:', canvas.width, 'x', canvas.height);
        }
        if (newWidth <= 0 && this.signatureInitAttempts < 5) {
          this.signatureInitAttempts += 1;
          setTimeout(() => {
            this.isInitializingSignature = false;
            this.initSignatureCanvas();
          }, 150);
          return;
        }

        this.signatureContext = canvas.getContext('2d');
        if (!this.signatureContext) {
          console.error('❌ Failed to get 2D context for signature canvas');
          this.isInitializingSignature = false;
          if (this.signatureInitAttempts < 5) {
            this.signatureInitAttempts += 1;
            setTimeout(() => {
              this.isInitializingSignature = false;
              this.initSignatureCanvas();
            }, 150);
          }
          return;
        }
        this.signatureContext.strokeStyle = '#000000';
        this.signatureContext.lineWidth = 2;
        this.signatureContext.lineCap = 'round';

        if (imageData && imageData.width > 0 && imageData.height > 0) {
          try {
            this.signatureContext.putImageData(imageData, 0, 0);
            console.log('✅ Previous signature restored');
          } catch (e) {
            console.log('Could not restore previous signature');
          }
        }

        console.log('✅ Signature canvas initialized:', canvas.width, 'x', canvas.height);
        this.signatureInitAttempts = 0;
        this.isInitializingSignature = false;
      });
    },
    
    forceCalendarColors() {
      this.$nextTick(() => {
        const datePicker = document.querySelector('.custom-date-picker');
        if (datePicker) {
          // Aplicar cores nos elementos específicos do Vuetify
          
          // 1. Título do calendário (ano/data)
          const pickerTitle = datePicker.querySelector('.v-picker__title');
          if (pickerTitle) {
            pickerTitle.style.setProperty('background-color', 'transparent', 'important');
            pickerTitle.style.setProperty('color', '#1e293b', 'important');
          }
          
          // 2. Botões do título (ano e data)
          const titleBtns = datePicker.querySelectorAll('.v-picker__title__btn');
          titleBtns.forEach(btn => {
            btn.style.setProperty('color', '#1e293b', 'important');
            btn.style.setProperty('font-weight', '600', 'important');
          });
          
          // 3. Botão ativo do título
          const activeTitleBtn = datePicker.querySelector('.v-picker__title__btn--active');
          if (activeTitleBtn) {
            activeTitleBtn.style.setProperty('background-color', '#1976D2', 'important');
            activeTitleBtn.style.setProperty('color', 'white', 'important');
          }
          
          // 4. Cabeçalho do calendário (mês/ano)
          const datePickerHeader = datePicker.querySelector('.v-date-picker-header__value');
          if (datePickerHeader) {
            datePickerHeader.style.setProperty('color', '#1e293b', 'important');
            datePickerHeader.style.setProperty('font-weight', '600', 'important');
          }
          
          // 5. Botões de navegação do cabeçalho
          const headerBtns = datePicker.querySelectorAll('.v-date-picker-header .v-btn');
          headerBtns.forEach(btn => {
            btn.style.setProperty('color', '#1e293b', 'important');
          });
          
          // 6. Cabeçalhos dos dias da semana (S, M, T, W, T, F, S)
          const dayHeaders = datePicker.querySelectorAll('.v-date-picker-table th');
          dayHeaders.forEach(header => {
            header.style.setProperty('color', '#374151', 'important');
            header.style.setProperty('font-weight', '600', 'important');
          });
          
          // 7. Botões dos dias
          const dayBtns = datePicker.querySelectorAll('.v-date-picker-table .v-btn');
          dayBtns.forEach(btn => {
            btn.style.setProperty('color', '#1e293b', 'important');
          });
          
          // 8. Dia selecionado
          const activeDayBtn = datePicker.querySelector('.v-date-picker-table .v-btn--active');
          if (activeDayBtn) {
            activeDayBtn.style.setProperty('background-color', '#1976D2', 'important');
            activeDayBtn.style.setProperty('color', 'white', 'important');
          }
          
          // 9. Dias de outros meses (desabilitados)
          const disabledBtns = datePicker.querySelectorAll('.v-date-picker-table .v-btn[disabled]');
          disabledBtns.forEach(btn => {
            btn.style.setProperty('color', '#9ca3af', 'important');
          });
          
          console.log('✅ Cores aplicadas nos elementos específicos do Vuetify');
          
          // Observer para detectar mudanças no calendário
          const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              if (mutation.type === 'childList') {
                // Reaplicar cores quando novos elementos são adicionados
                setTimeout(() => {
                  this.forceCalendarColors();
                }, 100);
              }
            });
          });
          
          observer.observe(datePicker, {
            childList: true,
            subtree: true
          });
        }
      });
    },
    
    startDrawing(event) {
      console.log('🖱️ Start drawing', event.type);
      
      // Prevent default to avoid scrolling on touch devices
      if (event.type.startsWith('touch')) {
        event.preventDefault();
      }
      
      if (!this.signatureContext) {
        console.error('❌ Signature context not initialized! Initializing now...');
        this.initSignatureCanvas();
        // Wait for initialization and try again
        setTimeout(() => {
          if (this.signatureContext) {
            this.startDrawing(event);
          }
        }, 50);
        return;
      }
      
      this.isDrawing = true;
      const coords = this.getCoordinates(event);
      this.signatureContext.beginPath();
      this.signatureContext.moveTo(coords.x, coords.y);
    },
    
    draw(event) {
      if (!this.isDrawing) return;
      event.preventDefault();
      const coords = this.getCoordinates(event);
      this.signatureContext.lineTo(coords.x, coords.y);
      this.signatureContext.stroke();
    },
    
    stopDrawing() {
      if (this.isDrawing) {
        this.isDrawing = false;
        this.enrollment.signature = this.$refs.signatureCanvas.toDataURL();
      }
    },
    
    getCoordinates(event) {
      const canvas = this.$refs.signatureCanvas;
      const rect = canvas.getBoundingClientRect();
      
      let clientX, clientY;
      if (event.touches && event.touches.length > 0) {
        clientX = event.touches[0].clientX;
        clientY = event.touches[0].clientY;
      } else {
        clientX = event.clientX;
        clientY = event.clientY;
      }
      
      return {
        x: clientX - rect.left,
        y: clientY - rect.top
      };
    },
    
    clearSignature() {
      const canvas = this.$refs.signatureCanvas;
      if (canvas && this.signatureContext) {
        this.signatureContext.clearRect(0, 0, canvas.width, canvas.height);
        this.enrollment.signature = '';
      }
    },
    
    hideChatWidgets() {
      // Hide Chatra
      if (window.Chatra) {
        window.Chatra('hide');
        console.log('🚫 Chatra hidden');
      }
      
      // Hide Zendesk
      if (window.zE) {
        window.zE('messenger', 'hide');
        console.log('🚫 Zendesk hidden');
      }
      
      // Fallback: hide by CSS if API doesn't work
      const style = document.createElement('style');
      style.id = 'hide-chat-widgets';
      style.innerHTML = `
        #chatra, .chatra--pos-right, .chatra--pos-left,
        iframe[title*="Messaging"], iframe[title*="Button"],
        #launcher, .zEWidget-launcher {
          display: none !important;
          visibility: hidden !important;
        }
      `;
      document.head.appendChild(style);
    },
    
    showChatWidgets() {
      // Show Chatra
      if (window.Chatra) {
        window.Chatra('show');
        console.log('✅ Chatra shown');
      }
      
      // Show Zendesk
      if (window.zE) {
        window.zE('messenger', 'show');
        console.log('✅ Zendesk shown');
      }
      
      // Remove CSS override
      const style = document.getElementById('hide-chat-widgets');
      if (style) {
        style.remove();
      }
    }
  }
};
</script>

<style scoped>
.conversational-enrollment {
  background: linear-gradient(135deg, var(--theme-primary, #667eea) 0%, var(--theme-secondary, #764ba2) 100%);
  background-attachment: fixed;
  overflow-x: hidden !important;
  position: relative;
  width: 100vw;
  max-width: 100%;
}

.conversational-enrollment .v-application--wrap {
  overflow-x: hidden !important;
}

.conversational-enrollment .fill-height {
  overflow-x: hidden !important;
}

.plan-card {
  transition: all 0.3s ease;
  border: 2px solid #e5e7eb;
  background: white;
  position: relative;
}

.plan-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.15);
  border-color: #d1d5db;
}

.plan-card.selected {
  border-color: #1976d2;
  background-color: #f3f9ff;
  box-shadow: 0 8px 25px rgba(25, 118, 210, 0.2);
}

.plan-card.clickable-card {
  cursor: pointer;
}

.plan-card.non-clickable {
  cursor: default;
}

.plan-card.non-clickable:hover {
  transform: none;
  box-shadow: none;
  border-color: #e5e7eb;
}

.plan-card.suppressed-benefit {
  border-style: dashed;
  border-color: #d1d5db;
  background: #f9fafb;
  box-shadow: none;
}

.plan-card.suppressed-benefit .text-h6 {
  color: #6b7280 !important;
}

.plan-card.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.progress-bar-fixed {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 999;
}

.app-header {
  background: white;
  padding: 16px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  position: relative;
  z-index: 10;
}

.header-center {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 20px;
}

.client-logo {
  max-height: 60px;
  max-width: 250px;
  object-fit: contain;
}

.progress-text {
  font-size: 14px;
  color: #666;
  font-weight: 500;
  white-space: nowrap;
}

.question-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
}

.question-content {
  max-width: 700px;
  width: 100%;
  background: white;
  border-radius: 16px;
  padding: 48px;
  box-shadow: 0 10px 40px rgba(0,0,0,0.2);
  position: relative;
  z-index: 1;
}

.question-title {
  font-size: 32px;
  font-weight: 700;
  color: #333;
  margin-bottom: 12px;
  line-height: 1.2;
}

.question-subtitle {
  font-size: 18px;
  color: #666;
  margin-bottom: 32px;
}

.large-input {
  font-size: 20px !important;
}

.large-input >>> input {
  font-size: 20px !important;
}

.options-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 16px;
  margin-top: 24px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.options-grid-2 {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-top: 24px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.options-grid-3 {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 20px;
  margin-top: 24px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.options-grid-2x2 {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-top: 24px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.option-card {
  padding: 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid #e0e0e0;
}

.option-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0,0,0,0.15);
}

.option-card.selected {
  border-color: #1976D2;
  background: #E3F2FD;
}

.option-card-large {
  padding: 40px 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 3px solid #e0e0e0;
  border-radius: 12px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.option-card-large:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0,0,0,0.15);
}

.option-card-large.selected {
  border-color: #1976D2;
  background: linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%);
  box-shadow: 0 8px 20px rgba(25, 118, 210, 0.3);
}

.benefit-card {
  padding: 24px 16px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  position: relative;
  min-height: 160px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.benefit-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0,0,0,0.15);
}

.benefit-card.selected {
  border-color: var(--theme-accent, #1976D2);
  background: linear-gradient(135deg, var(--theme-light, #E3F2FD) 0%, var(--theme-lighter, #BBDEFB) 100%);
}

.check-icon {
  position: absolute;
  top: 8px;
  right: 8px;
}

.benefits-selection {
  margin-top: 24px;
}

.plan-card-conversational {
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid #e0e0e0;
}

.plan-card-conversational:hover {
  transform: translateX(8px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.plan-card-conversational.selected {
  border-color: var(--theme-accent, #1976D2);
  background: var(--theme-light, #E3F2FD);
}

.plans-scroll {
  max-height: 500px;
  overflow-y: auto;
  padding-right: 8px;
}

.plans-scroll::-webkit-scrollbar {
  width: 8px;
}

.plans-scroll::-webkit-scrollbar-thumb {
  background: var(--theme-accent, #1976D2);
  border-radius: 4px;
}

.nav-footer {
  padding: 24px;
  background: white;
  box-shadow: 0 -4px 12px rgba(0,0,0,0.1);
  max-width: 700px;
  margin: 0 auto;
  width: 100%;
}

.get-started-btn {
  padding: 32px 64px !important;
  font-size: 18px !important;
  border-radius: 50px !important;
  text-transform: none !important;
  letter-spacing: 0.5px !important;
}

.pricing-summary {
  border-radius: 12px;
}

.suppressed-note {
  color: #6b7280 !important;
  font-style: italic;
}

.signature-pad-conversational {
  border: 2px dashed #ccc;
  border-radius: 8px;
  background: #fafafa;
  padding: 10px;
  width: 100%;
  box-sizing: border-box;
}

.signature-canvas-conversational {
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: crosshair;
  background: white;
  width: 100%;
  max-width: 100%;
  height: 150px;
  touch-action: none;
  display: block;
}

/* Animations */
.slide-fade-enter-active {
  transition: opacity 0.2s ease-in;
}

.slide-fade-leave-active {
  transition: opacity 0.2s ease-out;
}

.slide-fade-enter {
  opacity: 0;
}

.slide-fade-leave-to {
  opacity: 0;
}

/* Responsive */
@media (max-width: 600px) {
  .conversational-enrollment .container {
    padding: 0 !important;
    margin: 0 !important;
    overflow: hidden;
  }
  
  .conversational-enrollment .row {
    margin: 0 !important;
  }
  
  .conversational-enrollment .col {
    padding: 0 !important;
  }
  
  .question-container {
    padding: 20px 16px;
    justify-content: center;
    align-items: center;
    margin: 0 auto;
    overflow-x: hidden;
    overflow-y: auto;
  }
  
  .question-content {
    padding: 32px 24px;
    margin: 0 auto;
    max-width: calc(100vw - 32px);
    width: calc(100% - 32px);
    box-sizing: border-box;
    overflow: hidden;
  }
  
  .question-content .v-row {
    margin: 0 !important;
  }
  
  .question-content .v-col {
    padding: 0 4px !important;
    margin-bottom: 16px;
  }
  
  /* Centralizar apenas elementos de formulário, não a tela de boas-vindas */
  .question-content > div:not(.text-center) .v-text-field {
    width: 100% !important;
    margin: 0 auto !important;
  }
  
  .question-content > div:not(.text-center) .v-date-picker {
    width: 100% !important;
    margin: 0 auto !important;
  }
  
  /* Personalizar cores do date picker com seletores mais específicos */
  .conversational-enrollment .v-date-picker,
  .conversational-enrollment .v-date-picker * {
    color: #1e293b !important;
  }
  
  .conversational-enrollment .v-date-picker .v-date-picker-table th,
  .conversational-enrollment .v-date-picker .v-date-picker-table .v-btn {
    color: #374151 !important;
    font-weight: 600 !important;
  }
  
  .conversational-enrollment .v-date-picker .v-date-picker-table td .v-btn {
    color: #1e293b !important;
  }
  
  .conversational-enrollment .v-date-picker .v-date-picker-table .v-btn--active {
    background-color: #1976D2 !important;
    color: white !important;
  }
  
  .conversational-enrollment .v-date-picker .v-date-picker-table .v-btn:hover {
    background-color: rgba(25, 118, 210, 0.1) !important;
    color: #1e293b !important;
  }
  
  /* Cabeçalho do date picker */
  .conversational-enrollment .v-date-picker .v-date-picker-header,
  .conversational-enrollment .v-date-picker .v-date-picker-header * {
    color: #1e293b !important;
  }
  
  .conversational-enrollment .v-date-picker .v-date-picker-header .v-btn {
    color: #1e293b !important;
  }
  
  .conversational-enrollment .v-date-picker .v-date-picker-header .v-btn:hover {
    background-color: rgba(25, 118, 210, 0.1) !important;
  }
  
  /* Título do mês/ano */
  .conversational-enrollment .v-date-picker .v-date-picker-header__value {
    color: #1e293b !important;
    font-weight: 600 !important;
  }
  
  .question-content > div:not(.text-center) .v-autocomplete {
    width: 100% !important;
    margin: 0 auto !important;
  }
  
  .question-content > div:not(.text-center) .v-select {
    width: 100% !important;
    margin: 0 auto !important;
  }
  
  .question-content > div:not(.text-center) .v-menu {
    width: 100% !important;
    margin: 0 auto !important;
  }
  
  .question-content > div:not(.text-center) .v-btn {
    margin: 0 auto !important;
    display: block !important;
  }
  
  .question-title {
    font-size: 24px;
    text-align: center;
  }
  
  .question-subtitle {
    text-align: center;
  }
  
  .question-content > div:not(.text-center) .v-messages {
    text-align: center !important;
  }
  
  .question-content > div:not(.text-center) .v-messages__message {
    text-align: center !important;
  }
  
  .question-content > div:not(.text-center) .v-input__control {
    margin: 0 auto !important;
  }
  
  .options-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .options-grid-2,
  .options-grid-3,
  .options-grid-2x2 {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .option-card-large {
    padding: 32px 20px;
  }
}

/* CSS Global para forçar cores do date picker */
.v-application .conversational-enrollment .v-date-picker {
  color: #1e293b !important;
}

.v-application .conversational-enrollment .v-date-picker * {
  color: #1e293b !important;
}

.v-application .conversational-enrollment .v-date-picker .v-date-picker-table th {
  color: #374151 !important;
  font-weight: 600 !important;
}

.v-application .conversational-enrollment .v-date-picker .v-date-picker-table td .v-btn {
  color: #1e293b !important;
}

.v-application .conversational-enrollment .v-date-picker .v-date-picker-table .v-btn--active {
  background-color: #1976D2 !important;
  color: white !important;
}

.v-application .conversational-enrollment .v-date-picker .v-date-picker-table .v-btn:hover {
  background-color: rgba(25, 118, 210, 0.1) !important;
  color: #1e293b !important;
}

.v-application .conversational-enrollment .v-date-picker .v-date-picker-header__value {
  color: #1e293b !important;
  font-weight: 600 !important;
}

.v-application .conversational-enrollment .v-date-picker .v-date-picker-header .v-btn {
  color: #1e293b !important;
}

/* CSS específico para o date picker customizado */
.custom-date-picker {
  color: #1e293b !important;
}

.custom-date-picker * {
  color: #1e293b !important;
}

.custom-date-picker .v-date-picker-table th {
  color: #374151 !important;
  font-weight: 600 !important;
}

.custom-date-picker .v-date-picker-table td .v-btn {
  color: #1e293b !important;
}

.custom-date-picker .v-date-picker-table .v-btn--active {
  background-color: #1976D2 !important;
  color: white !important;
}

.custom-date-picker .v-date-picker-table .v-btn:hover {
  background-color: rgba(25, 118, 210, 0.1) !important;
  color: #1e293b !important;
}

.custom-date-picker .v-date-picker-header__value {
  color: #1e293b !important;
  font-weight: 600 !important;
}

.custom-date-picker .v-date-picker-header .v-btn {
  color: #1e293b !important;
}
</style>

